import React, { useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import api from '../api';
import { Game, Sport, BaseballScore, SoccerScore, Team, Player, GameVideo, LineupEntry, BaseballAtBat } from '../types';
import { mediaUrl } from '../utils/mediaUrl';
import { parseGameTime, easternToLocalInput, localInputToEastern } from '../utils/gameTime';

type ModalType =
  | 'create' | 'edit_status' | 'score_baseball' | 'score_soccer' | 'stream_info' | 'game_film'
  | 'create_team' | 'edit_team' | 'create_player' | 'edit_player' | 'player_stats'
  | 'upload_recording' | 'upload_highlight' | 'import_ical' | 'roster_scan'
  | null;

interface ModalState { type: ModalType; game?: Game; team?: Team; player?: Player; sport?: string; }

const HLS_BASE = process.env.REACT_APP_HLS_URL || 'http://localhost:8000';
const API_URL  = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3001';

const formatDate = (s: string) => parseGameTime(s).toLocaleDateString('en-US', {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});

const StatusChip = ({ status }: { status: string }) => {
  const map: Record<string, { bg: string; color: string; border: string }> = {
    live:      { bg: 'rgba(244,67,54,0.12)',  color: '#F44336', border: 'rgba(244,67,54,0.25)' },
    scheduled: { bg: 'rgba(74,85,104,0.15)',  color: 'var(--ink-3)', border: 'var(--hairline)' },
    completed: { bg: 'rgba(34,197,94,0.1)',   color: '#22C55E', border: 'rgba(34,197,94,0.2)' },
  };
  const s = map[status] || map.scheduled;
  return (
    <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, padding: '3px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {status}
    </span>
  );
};

// ─── Create Game ──────────────────────────────────────────────────────────────
const CreateGameModal = ({ onClose, onCreated, teams }: { onClose: () => void; onCreated: (g: Game) => void; teams: Team[] }) => {
  const [form, setForm] = useState({ title: '', sport: 'baseball' as Sport, home_team: '', away_team: '', scheduled_at: '', location: '', description: '', team_id: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = (f: keyof typeof form) => (e: any) => setForm(p => ({ ...p, [f]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setLoading(true);
    try { const r = await api.post('/games', { ...form, scheduled_at: localInputToEastern(form.scheduled_at) }); onCreated(r.data); onClose(); }
    catch (err: any) { setError(err.response?.data?.error || 'Failed to create game'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>New Game</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <p style={{ color: 'var(--ink-3)', fontSize: '13px', marginBottom: '20px' }}>Create a new game and get a stream key for your camera.</p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Game Title</label>
            <input className="form-control" value={form.title} onChange={set('title')} placeholder="e.g. Carmel FC vs Eagles" required />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div className="form-group">
              <label>Sport</label>
              <select className="form-control" value={form.sport} onChange={set('sport') as any}>
                <option value="baseball">⚾ Baseball</option>
                <option value="softball">🥎 Softball</option>
                <option value="soccer">⚽ Soccer</option>
                <option value="basketball">🏀 Basketball</option>
              </select>
            </div>
            <div className="form-group">
              <label>Date & Time</label>
              <input className="form-control" type="datetime-local" value={form.scheduled_at} onChange={set('scheduled_at')} required />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div className="form-group"><label>Away Team</label><input className="form-control" value={form.away_team} onChange={set('away_team')} placeholder="Visiting team" required /></div>
            <div className="form-group"><label>Home Team</label><input className="form-control" value={form.home_team} onChange={set('home_team')} placeholder="Home team" required /></div>
          </div>
          <div className="form-group">
            <label>Link to Team (optional)</label>
            <select className="form-control" value={form.team_id} onChange={set('team_id') as any}>
              <option value="">— None —</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="form-group"><label>Location (optional)</label><input className="form-control" value={form.location} onChange={set('location')} placeholder="Field name or address" /></div>
          <div className="form-group"><label>Notes (optional)</label><textarea className="form-control" value={form.description} onChange={set('description') as any} placeholder="League, division..." rows={2} style={{ resize: 'vertical' }} /></div>
          {error && <div style={{ color: '#F87171', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Creating...' : 'Create Game'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Edit Game (full edit — all fields) ────────────────────────────────────────
const EditStatusModal = ({ game, onClose, onUpdated, teams }: { game: Game; onClose: () => void; onUpdated: (g: Game) => void; teams: Team[] }) => {
  const [form, setForm] = useState({
    title:       game.title,
    sport:       game.sport as Sport,
    home_team:   game.home_team,
    away_team:   game.away_team,
    scheduled_at: easternToLocalInput(game.scheduled_at ?? ''),
    location:    game.location ?? '',
    description: game.description ?? '',
    team_id:     game.team_id ?? '',
    status:      game.status,
    home_final_score: game.home_final_score?.toString() ?? '',
    away_final_score: game.away_final_score?.toString() ?? '',
  });
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [awayLogo,     setAwayLogo]     = useState<string | null>((game as any).away_logo_url ?? null);
  const [logoUploading, setLogoUploading] = useState(false);
  const set = (f: keyof typeof form) => (e: any) => setForm(p => ({ ...p, [f]: e.target.value }));

  const handleSave = async () => {
    setLoading(true); setError('');
    try {
      const body: any = { ...form, team_id: form.team_id || null, scheduled_at: localInputToEastern(form.scheduled_at), away_logo_url: awayLogo || null };
      if (form.home_final_score !== '') body.home_final_score = parseInt(form.home_final_score);
      else delete body.home_final_score;
      if (form.away_final_score !== '') body.away_final_score = parseInt(form.away_final_score);
      else delete body.away_final_score;
      const r = await api.put(`/games/${game.id}`, body);
      onUpdated(r.data); onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally { setLoading(false); }
  };

  const handleAwayLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const r = await api.post('/videos/upload-photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      // upload-photo returns { photo_url } — fall back to other shapes for safety
      setAwayLogo(r.data.photo_url ?? r.data.url ?? r.data.file_path ?? null);
    } catch { setError('Logo upload failed'); }
    finally { setLogoUploading(false); }
  };

  const statusOptions = [
    { value: 'scheduled', icon: '📅', label: 'Scheduled',  desc: 'Game has not started yet' },
    { value: 'live',      icon: '🔴', label: 'Live',       desc: 'Game is in progress — stream is active' },
    { value: 'completed', icon: '✅', label: 'Completed',  desc: 'Game is finished' },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>Edit Game</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        {error && <p style={{ color: '#EF4444', marginBottom: '12px', fontSize: '13px' }}>{error}</p>}

        <div className="form-group">
          <label>Game Title</label>
          <input className="form-control" value={form.title} onChange={set('title')} placeholder="e.g. Carmel FC vs Eagles" required />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <div className="form-group">
            <label>Sport</label>
            <select className="form-control" value={form.sport} onChange={set('sport') as any}>
              <option value="baseball">⚾ Baseball</option>
              <option value="softball">🥎 Softball</option>
              <option value="soccer">⚽ Soccer</option>
              <option value="basketball">🏀 Basketball</option>
            </select>
          </div>
          <div className="form-group">
            <label>Date & Time</label>
            <input className="form-control" type="datetime-local" value={form.scheduled_at} onChange={set('scheduled_at')} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <div className="form-group"><label>Away Team</label><input className="form-control" value={form.away_team} onChange={set('away_team')} /></div>
          <div className="form-group"><label>Home Team</label><input className="form-control" value={form.home_team} onChange={set('home_team')} /></div>
        </div>

        {/* Away team logo — shown in the live scoreboard overlay */}
        <div className="form-group">
          <label>Away Team Logo <span style={{ fontWeight: 400, color: 'var(--ink-3)', fontSize: '11px' }}>— shown in live score bug overlay</span></label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {awayLogo && (
              <div style={{ position: 'relative' }}>
                <img src={awayLogo} alt="Away logo" style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--hairline)', background: 'var(--surface-sunken)' }} />
                <button onClick={() => setAwayLogo(null)} style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', background: '#EF4444', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>✕</button>
              </div>
            )}
            <label style={{ cursor: 'pointer' }}>
              <div style={{ padding: '8px 14px', borderRadius: 8, border: '1px dashed var(--hairline)', background: 'var(--surface-sunken)', fontSize: 13, color: 'var(--ink-3)', fontWeight: 600 }}>
                {logoUploading ? 'Uploading…' : awayLogo ? '🔄 Change Logo' : '⬆️ Upload Logo'}
              </div>
              <input type="file" accept="image/*" onChange={handleAwayLogoUpload} disabled={logoUploading} style={{ display: 'none' }} />
            </label>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <div className="form-group">
            <label>Location</label>
            <input className="form-control" value={form.location} onChange={set('location')} placeholder="Field / venue" />
          </div>
          <div className="form-group">
            <label>Link to Team</label>
            <select className="form-control" value={form.team_id} onChange={set('team_id') as any}>
              <option value="">— None —</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Description (optional)</label>
          <textarea className="form-control" value={form.description} onChange={set('description')} rows={2} placeholder="Notes, details…" style={{ resize: 'vertical' }} />
        </div>

        <div className="section-label" style={{ marginTop: '8px' }}>Status</div>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', marginTop: '10px' }}>
          {statusOptions.map(o => (
            <button key={o.value} onClick={() => setForm(p => ({ ...p, status: o.value as any }))} style={{
              flex: 1, padding: '10px 8px', borderRadius: '10px', border: '2px solid',
              borderColor: form.status === o.value ? '#F97316' : 'var(--hairline)',
              background: form.status === o.value ? 'rgba(249,115,22,0.08)' : 'transparent',
              color: 'var(--ink-1)', cursor: 'pointer', textAlign: 'center',
            }}>
              <div style={{ fontSize: '18px' }}>{o.icon}</div>
              <div style={{ fontWeight: 700, fontSize: '12px', marginTop: '4px' }}>{o.label}</div>
            </button>
          ))}
        </div>

        <div className="section-label">Final Score (optional)</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '10px', alignItems: 'center', marginBottom: '20px', marginTop: '10px' }}>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--ink-3)', marginBottom: '5px', fontWeight: 600 }}>{form.away_team || 'Away'}</div>
            <input type="number" min={0} className="form-control" value={form.away_final_score} onChange={set('away_final_score')} style={{ textAlign: 'center', fontSize: '22px', fontWeight: 900, padding: '8px' }} placeholder="—" />
          </div>
          <div style={{ fontSize: '18px', color: 'var(--hairline)', fontWeight: 700 }}>–</div>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--ink-3)', marginBottom: '5px', fontWeight: 600 }}>{form.home_team || 'Home'}</div>
            <input type="number" min={0} className="form-control" value={form.home_final_score} onChange={set('home_final_score')} style={{ textAlign: 'center', fontSize: '22px', fontWeight: 900, padding: '8px' }} placeholder="—" />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>{loading ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
};

// ─── Baseball Score Modal ─────────────────────────────────────────────────────
// ── Outcome definitions ───────────────────────────────────────────────────────
const BB_OUTCOMES: { key: string; label: string; cat: 'hit'|'base'|'out'|'error'; isOut?: boolean; isHit?: boolean }[] = [
  { key: 'single',               label: '1B',     cat: 'hit',   isHit: true  },
  { key: 'double',               label: '2B',     cat: 'hit',   isHit: true  },
  { key: 'triple',               label: '3B',     cat: 'hit',   isHit: true  },
  { key: 'home_run',             label: 'HR',     cat: 'hit',   isHit: true  },
  { key: 'walk',                 label: 'BB',     cat: 'base'  },
  { key: 'hit_by_pitch',         label: 'HBP',    cat: 'base'  },
  { key: 'catcher_interference', label: 'CI',     cat: 'base'  },
  { key: 'strikeout_swinging',   label: 'K ⚡',   cat: 'out',   isOut: true  },
  { key: 'strikeout_looking',    label: 'Kl 👀',  cat: 'out',   isOut: true  },
  { key: 'groundout',            label: 'GO',     cat: 'out',   isOut: true  },
  { key: 'flyout',               label: 'FO',     cat: 'out',   isOut: true  },
  { key: 'lineout',              label: 'LO',     cat: 'out',   isOut: true  },
  { key: 'fielders_choice',      label: 'FC',     cat: 'out',   isOut: true  },
  { key: 'sac_fly',              label: 'SAC-F',  cat: 'out',   isOut: true  },
  { key: 'sac_bunt',             label: 'SAC-B',  cat: 'out',   isOut: true  },
  { key: 'double_play',          label: 'DP',     cat: 'out',   isOut: true  },
  { key: 'error',                label: 'E',      cat: 'error'             },
];
const CAT_COLORS: Record<string, string> = {
  hit: '#22C55E', base: '#3B82F6', out: '#EF4444', error: '#F59E0B',
};
const ORDINAL = ['1st','2nd','3rd','4th','5th','6th','7th','8th','9th','10th','11th','12th'];

const BaseballScoreModal = ({ game, onClose }: { game: Game; onClose: () => void }) => {
  type Tab = 'scoring' | 'lineup' | 'adjust';
  const [tab, setTab]       = useState<Tab>('scoring');
  const [score, setScore]   = useState<Partial<BaseballScore>>({
    current_inning: 1, is_top_inning: 1, balls: 0, strikes: 0, outs: 0,
    home_total_runs: 0, away_total_runs: 0, home_hits: 0, away_hits: 0,
    home_errors: 0, away_errors: 0, runner_first: 0, runner_second: 0, runner_third: 0,
    inning_scores: [], lineup_home: [], lineup_away: [], current_batter_home: 0, current_batter_away: 0,
  });
  const [atBats, setAtBats]     = useState<BaseballAtBat[]>([]);
  const [rosterPlayers, setRosterPlayers] = useState<Player[]>([]);
  // Pending at-bat (after outcome clicked, awaiting runs/RBI confirmation)
  const [pending, setPending]   = useState<{ outcome: string; label: string } | null>(null);
  const [pendingRuns, setPendingRuns] = useState(0);
  const [pendingRbi,  setPendingRbi]  = useState(0);
  const [busy,   setBusy]   = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [msg,    setMsg]    = useState('');

  const isSoftball = game.sport === 'softball';
  const sport      = game.sport;

  // Current batting team
  const battingTeam: 'home'|'away' = score.is_top_inning ? 'away' : 'home';
  const batterIdx = battingTeam === 'home' ? (score.current_batter_home ?? 0) : (score.current_batter_away ?? 0);
  const lineup    = battingTeam === 'home' ? (score.lineup_home ?? []) : (score.lineup_away ?? []);
  const currentBatter: LineupEntry | null = lineup[batterIdx] ?? null;

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  // Load score + roster
  useEffect(() => {
    api.get(`/scores/baseball/${game.id}`).then(r => setScore(r.data)).catch(() => {});
    api.get(`/scores/baseball/${game.id}/at-bats`).then(r => setAtBats(r.data)).catch(() => {});
    if (game.team_id) {
      api.get(`/teams/${game.team_id}`).then(r => setRosterPlayers(r.data.players || [])).catch(() => {});
    }
  }, [game.id, game.team_id]);

  // ── Lineup helpers ───────────────────────────────────────────────────────────
  const defaultLineup = (n: number): LineupEntry[] =>
    Array.from({ length: n }, (_, i) => ({ name: `Batter ${i + 1}`, number: `${i + 1}` }));

  const ensureLineups = (s: Partial<BaseballScore>) => {
    const lh = s.lineup_home && s.lineup_home.length > 0 ? s.lineup_home
      : rosterPlayers.slice(0, 9).map(p => ({ name: p.name, number: String(p.number ?? ''), player_id: p.id }));
    const la = s.lineup_away && s.lineup_away.length > 0 ? s.lineup_away : defaultLineup(9);
    return { ...s, lineup_home: lh, lineup_away: la };
  };

  const saveLineup = async (newScore: Partial<BaseballScore>) => {
    await api.put(`/scores/baseball/${game.id}/lineup`, {
      lineup_home: newScore.lineup_home,
      lineup_away: newScore.lineup_away,
      current_batter_home: newScore.current_batter_home,
      current_batter_away: newScore.current_batter_away,
    });
    setScore(newScore);
    flash('Lineup saved');
  };

  const addBatter = (team: 'home' | 'away') => {
    const key = team === 'home' ? 'lineup_home' : 'lineup_away';
    const cur = score[key] ?? [];
    if (cur.length >= 12) return;
    const next = [...cur, { name: `Batter ${cur.length + 1}`, number: String(cur.length + 1) }];
    setScore(s => ({ ...s, [key]: next }));
  };

  const removeBatter = (team: 'home' | 'away') => {
    const key = team === 'home' ? 'lineup_home' : 'lineup_away';
    const cur = score[key] ?? [];
    if (cur.length <= 1) return;
    setScore(s => ({ ...s, [key]: cur.slice(0, -1) }));
  };

  const updateBatter = (team: 'home' | 'away', idx: number, field: 'name'|'number'|'player_id', val: string) => {
    const key = team === 'home' ? 'lineup_home' : 'lineup_away';
    const cur = [...(score[key] ?? [])];
    cur[idx] = { ...cur[idx], [field]: val };
    // If picking a player from roster, auto-fill name + number
    if (field === 'player_id' && val) {
      const p = rosterPlayers.find(pl => pl.id === val);
      if (p) { cur[idx].name = p.name; cur[idx].number = String(p.number ?? ''); }
    }
    setScore(s => ({ ...s, [key]: cur }));
  };

  // ── Scoring helpers ──────────────────────────────────────────────────────────
  const adjustCount = async (field: 'balls'|'strikes', delta: number) => {
    const maxes = { balls: 3, strikes: 2 };
    const newVal = Math.max(0, Math.min(maxes[field], (score[field] ?? 0) + delta));
    const updated = { ...score, [field]: newVal };
    setScore(updated);
    await api.put(`/scores/baseball/${game.id}`, updated);
  };

  const adjustOuts = async (delta: number) => {
    const newVal = Math.max(0, Math.min(2, (score.outs ?? 0) + delta));
    const updated = { ...score, outs: newVal };
    setScore(updated);
    await api.put(`/scores/baseball/${game.id}`, updated);
  };

  const toggleRunner = async (f: 'runner_first'|'runner_second'|'runner_third') => {
    const updated = { ...score, [f]: score[f] ? 0 : 1 };
    setScore(updated);
    await api.put(`/scores/baseball/${game.id}`, updated);
  };

  const advanceInning = async () => {
    let s = { ...score };
    if (s.is_top_inning) {
      s.is_top_inning = 0;
    } else {
      s.is_top_inning = 1;
      s.current_inning = (s.current_inning ?? 1) + 1;
    }
    s.balls = 0; s.strikes = 0; s.outs = 0;
    s.runner_first = 0; s.runner_second = 0; s.runner_third = 0;
    setScore(s);
    await api.put(`/scores/baseball/${game.id}`, s);
    flash(`Now: ${s.is_top_inning ? '▲' : '▼'} Inning ${s.current_inning}`);
  };

  const selectOutcome = (key: string, label: string) => {
    const def = BB_OUTCOMES.find(o => o.key === key);
    if (def?.isHit || key === 'sac_fly' || key === 'error' || key === 'fielders_choice') {
      setPending({ outcome: key, label });
      setPendingRuns(key === 'home_run' ? 1 : 0);
      setPendingRbi(key === 'home_run' ? 1 : 0);
    } else {
      submitAtBat(key, label, 0, 0);
    }
  };

  const submitAtBat = async (outcome: string, label: string, runs: number, rbi: number) => {
    if (!currentBatter && lineup.length === 0) { flash('Set lineup first (Lineup tab)'); return; }
    setBusy(true);
    setPending(null);
    try {
      const r = await api.post(`/scores/baseball/${game.id}/at-bat`, {
        inning:        score.current_inning,
        is_top_inning: score.is_top_inning,
        team:          battingTeam,
        batter_name:   currentBatter?.name ?? '',
        batter_number: currentBatter?.number ?? '',
        batter_index:  batterIdx,
        outcome,
        runs_scored: runs,
        rbi,
      });
      setScore(r.data.score);
      const refreshed = await api.get(`/scores/baseball/${game.id}/at-bats`);
      setAtBats(refreshed.data);

      // Auto-increment outs for out outcomes, auto-clear count
      const def = BB_OUTCOMES.find(o => o.key === outcome);
      let updated = { ...r.data.score, balls: 0, strikes: 0 };
      if (def?.isOut) {
        const newOuts = (updated.outs ?? 0) + 1;
        updated.outs = Math.min(2, newOuts);
        if (newOuts >= 3) {
          // 3 outs — offer to advance inning
          updated.outs = 2;
          flash('3 outs! Use "End Half-Inning" button to advance.');
        }
      }
      const saved2 = await api.put(`/scores/baseball/${game.id}`, updated);
      setScore(saved2.data);

      flash(`${label} — ${currentBatter?.name || 'Batter'}`);
    } catch { flash('Error recording at-bat'); }
    finally { setBusy(false); }
  };

  // ── Undo ─────────────────────────────────────────────────────────────────────
  const undoLast = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`/scores/baseball/${game.id}/at-bat/last`);
      setScore(r.data.score);
      const refreshed = await api.get(`/scores/baseball/${game.id}/at-bats`);
      setAtBats(refreshed.data);
      flash('Last at-bat undone');
    } catch { flash('Nothing to undo'); }
    finally { setBusy(false); }
  };

  // ── Manual adjust (old form) ──────────────────────────────────────────────────
  const n = (f: keyof BaseballScore) => (e: any) => setScore(s => ({ ...s, [f]: Number(e.target.value) }));
  const saveManual = async () => {
    setSaving(true);
    try { await api.put(`/scores/baseball/${game.id}`, score); setSaved(true); setTimeout(() => setSaved(false), 2500); }
    finally { setSaving(false); }
  };

  // ── Tab styles ────────────────────────────────────────────────────────────────
  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 700,
    fontSize: '13px', transition: 'all 0.15s',
    background: active ? '#F97316' : 'transparent',
    color: active ? '#fff' : 'var(--ink-3)',
  });

  // ── Render ────────────────────────────────────────────────────────────────────
  const innings_n = (score.current_inning ?? 1);
  const halfLabel = score.is_top_inning ? `▲ Top ${innings_n}` : `▼ Bot ${innings_n}`;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '720px', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>{isSoftball ? '🥎' : '⚾'} {sport.charAt(0).toUpperCase() + sport.slice(1)} Live Scoring</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <p style={{ color: 'var(--ink-3)', fontSize: '13px', marginBottom: '16px' }}>
          {game.away_team} @ {game.home_team}
        </p>

        {/* Score bar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'var(--surface-sunken)', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px',
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '11px', color: 'var(--ink-3)', fontWeight: 600, marginBottom: '3px' }}>
              {score.is_top_inning ? '● BATTING' : ''}
            </div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink-1)' }}>{game.away_team}</div>
            <div style={{ fontSize: '32px', fontWeight: 900, color: 'var(--ink-1)', lineHeight: 1.1 }}>{score.away_total_runs ?? 0}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '15px', fontWeight: 800, color: '#F97316' }}>{halfLabel}</div>
            <div style={{ fontSize: '12px', color: 'var(--ink-3)', marginTop: '3px' }}>
              B{score.balls??0} · S{score.strikes??0} · O{score.outs??0}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '11px', color: 'var(--ink-3)', fontWeight: 600, marginBottom: '3px' }}>
              {!score.is_top_inning ? '● BATTING' : ''}
            </div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink-1)' }}>{game.home_team}</div>
            <div style={{ fontSize: '32px', fontWeight: 900, color: 'var(--ink-1)', lineHeight: 1.1 }}>{score.home_total_runs ?? 0}</div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: 'var(--surface-sunken)', borderRadius: '10px', padding: '4px' }}>
          {(['scoring','lineup','adjust'] as Tab[]).map(t => (
            <button key={t} style={tabStyle(tab === t)} onClick={() => setTab(t)}>
              {t === 'scoring' ? '⚾ Scoring' : t === 'lineup' ? '📋 Lineup' : '🔧 Adjust'}
            </button>
          ))}
        </div>

        {/* Flash message */}
        {msg && (
          <div style={{ background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.3)', borderRadius: '8px', padding: '8px 14px', marginBottom: '14px', fontSize: '13px', color: '#F97316', fontWeight: 600 }}>
            {msg}
          </div>
        )}

        {/* ── SCORING TAB ── */}
        {tab === 'scoring' && (
          <div>
            {/* Current batter */}
            <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px' }}>
              <div style={{ fontSize: '11px', color: 'var(--ink-3)', fontWeight: 600, letterSpacing: '1px', marginBottom: '6px' }}>
                AT BAT — {battingTeam === 'away' ? game.away_team.toUpperCase() : game.home_team.toUpperCase()}
              </div>
              {currentBatter ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#F97316', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '14px', color: '#fff', flexShrink: 0 }}>
                    #{currentBatter.number}
                  </div>
                  <div>
                    <div style={{ fontSize: '17px', fontWeight: 800, color: 'var(--ink-1)' }}>{currentBatter.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--ink-3)' }}>
                      {ORDINAL[batterIdx]} in order ({batterIdx + 1} of {lineup.length})
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: '13px', color: 'var(--ink-3)' }}>
                  No lineup set — go to <strong style={{ color: '#F97316' }}>Lineup</strong> tab to configure batting order
                </div>
              )}
            </div>

            {/* Outcome buttons */}
            {!pending && (
              <div style={{ marginBottom: '16px' }}>
                {(['hit','base','out','error'] as const).map(cat => {
                  const items = BB_OUTCOMES.filter(o => o.cat === cat);
                  const label = cat === 'hit' ? 'Hits' : cat === 'base' ? 'Reaches Base' : cat === 'out' ? 'Outs' : 'Error / Other';
                  return (
                    <div key={cat} style={{ marginBottom: '10px' }}>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--ink-4)', letterSpacing: '1px', marginBottom: '6px' }}>{label.toUpperCase()}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {items.map(o => (
                          <button key={o.key} disabled={busy} onClick={() => selectOutcome(o.key, o.label)} style={{
                            padding: '8px 14px', borderRadius: '8px', border: `2px solid ${CAT_COLORS[cat]}33`,
                            background: `${CAT_COLORS[cat]}15`, color: CAT_COLORS[cat],
                            fontWeight: 800, fontSize: '13px', cursor: busy ? 'not-allowed' : 'pointer',
                            opacity: busy ? 0.5 : 1,
                          }}>
                            {o.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pending at-bat confirmation */}
            {pending && (
              <div style={{ background: 'rgba(249,115,22,0.08)', border: '2px solid rgba(249,115,22,0.3)', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontSize: '14px', fontWeight: 800, color: '#F97316', marginBottom: '12px' }}>
                  {pending.label} — {currentBatter?.name || 'Batter'} — Add runs?
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  {[{ label: 'Runs Scored', val: pendingRuns, set: setPendingRuns }, { label: 'RBI', val: pendingRbi, set: setPendingRbi }].map(({ label, val, set }) => (
                    <div key={label}>
                      <div style={{ fontSize: '11px', color: 'var(--ink-3)', fontWeight: 600, marginBottom: '6px' }}>{label}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button onClick={() => set(Math.max(0, val - 1))} style={{ width: 30, height: 30, borderRadius: '6px', border: '1px solid var(--hairline)', background: 'var(--surface-sunken)', color: 'var(--ink-1)', cursor: 'pointer', fontSize: '16px' }}>−</button>
                        <span style={{ fontSize: '20px', fontWeight: 900, color: 'var(--ink-1)', minWidth: '28px', textAlign: 'center' }}>{val}</span>
                        <button onClick={() => set(Math.min(4, val + 1))} style={{ width: 30, height: 30, borderRadius: '6px', border: '1px solid var(--hairline)', background: 'var(--surface-sunken)', color: 'var(--ink-1)', cursor: 'pointer', fontSize: '16px' }}>+</button>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-secondary" onClick={() => setPending(null)}>Cancel</button>
                  <button className="btn btn-primary" disabled={busy} onClick={() => submitAtBat(pending.outcome, pending.label, pendingRuns, pendingRbi)}>
                    {busy ? 'Recording...' : '✓ Record At-Bat'}
                  </button>
                </div>
              </div>
            )}

            {/* Count + Runners + Inning controls */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
              {/* Count */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '10px', padding: '12px' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--ink-4)', letterSpacing: '1px', marginBottom: '8px' }}>COUNT</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {[{ label: 'BALL', field: 'balls' as const, max: 3, color: '#22C55E' }, { label: 'STRIKE', field: 'strikes' as const, max: 2, color: '#F97316' }].map(({ label, field, max, color }) => (
                    <div key={field} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink-3)', width: '50px' }}>{label}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <button onClick={() => adjustCount(field, -1)} style={{ width: 24, height: 24, borderRadius: '4px', border: '1px solid var(--hairline)', background: 'var(--surface-sunken)', color: 'var(--ink-1)', cursor: 'pointer', fontSize: '14px' }}>−</button>
                        <span style={{ fontSize: '16px', fontWeight: 900, color, minWidth: '20px', textAlign: 'center' }}>{score[field] ?? 0}</span>
                        <button onClick={() => adjustCount(field, 1)} style={{ width: 24, height: 24, borderRadius: '4px', border: '1px solid var(--hairline)', background: 'var(--surface-sunken)', color: 'var(--ink-1)', cursor: 'pointer', fontSize: '14px' }}>+</button>
                      </div>
                      <div style={{ display: 'flex', gap: '3px' }}>
                        {Array.from({ length: max }, (_, i) => (
                          <div key={i} style={{ width: 9, height: 9, borderRadius: '50%', background: i < (score[field] ?? 0) ? color : 'transparent', border: `2px solid ${i < (score[field] ?? 0) ? color : 'var(--hairline-strong)'}` }} />
                        ))}
                      </div>
                    </div>
                  ))}
                  {/* Outs */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink-3)', width: '50px' }}>OUTS</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <button onClick={() => adjustOuts(-1)} style={{ width: 24, height: 24, borderRadius: '4px', border: '1px solid var(--hairline)', background: 'var(--surface-sunken)', color: 'var(--ink-1)', cursor: 'pointer', fontSize: '14px' }}>−</button>
                      <span style={{ fontSize: '16px', fontWeight: 900, color: '#EF4444', minWidth: '20px', textAlign: 'center' }}>{score.outs ?? 0}</span>
                      <button onClick={() => adjustOuts(1)} style={{ width: 24, height: 24, borderRadius: '4px', border: '1px solid var(--hairline)', background: 'var(--surface-sunken)', color: 'var(--ink-1)', cursor: 'pointer', fontSize: '14px' }}>+</button>
                    </div>
                    <div style={{ display: 'flex', gap: '3px' }}>
                      {Array.from({ length: 3 }, (_, i) => (
                        <div key={i} style={{ width: 9, height: 9, borderRadius: '50%', background: i < (score.outs ?? 0) ? '#EF4444' : 'transparent', border: `2px solid ${i < (score.outs ?? 0) ? '#EF4444' : 'var(--hairline-strong)'}` }} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Runners */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '10px', padding: '12px' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--ink-4)', letterSpacing: '1px', marginBottom: '8px' }}>RUNNERS</div>
                {/* Diamond */}
                <div style={{ position: 'relative', width: 80, height: 80, margin: '0 auto 10px' }}>
                  {[
                    { key: 'runner_second' as const,  top: 2,    left: '50%', transform: 'translate(-50%,0) rotate(45deg)' },
                    { key: 'runner_third'  as const,  top: '50%', left: 2,    transform: 'translate(0,-50%) rotate(45deg)' },
                    { key: 'runner_first'  as const,  top: '50%', right: 2,   transform: 'translate(0,-50%) rotate(45deg)' },
                  ].map(b => {
                    const on = !!score[b.key];
                    return (
                      <button key={b.key} onClick={() => toggleRunner(b.key)} style={{
                        position: 'absolute', width: 22, height: 22, borderRadius: '3px',
                        ...(b.top !== undefined ? { top: typeof b.top === 'number' ? b.top : b.top } : {}),
                        ...(b.left !== undefined ? { left: typeof b.left === 'number' ? b.left : b.left } : {}),
                        ...(('right' in b) ? { right: (b as any).right } : {}),
                        transform: b.transform, border: `2px solid ${on ? '#F97316' : 'var(--hairline-strong)'}`,
                        background: on ? '#F97316' : 'transparent',
                        cursor: 'pointer',
                      }} />
                    );
                  })}
                  <div style={{ position: 'absolute', bottom: 2, left: '50%', transform: 'translate(-50%,0) rotate(45deg)', width: 18, height: 18, borderRadius: '3px', border: '2px solid var(--hairline-strong)', background: 'var(--surface-sunken)' }} />
                </div>
                <div style={{ fontSize: '11px', color: 'var(--ink-3)', textAlign: 'center' }}>Tap base to toggle runner</div>
              </div>
            </div>

            {/* Inning controls */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
              <button className="btn btn-secondary" style={{ flex: 1, fontSize: '13px' }} onClick={advanceInning}>
                {score.is_top_inning ? '▼ End Top / Start Bottom' : '▲ End Half-Inning / Next Inning'}
              </button>
              <button className="btn btn-secondary" style={{ fontSize: '13px' }} disabled={busy || atBats.length === 0} onClick={undoLast}>
                ↩ Undo Last
              </button>
            </div>

            {/* Recent at-bats */}
            {atBats.length > 0 && (
              <div>
                <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink-4)', letterSpacing: '1px', marginBottom: '8px' }}>RECENT AT-BATS</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '160px', overflowY: 'auto' }}>
                  {[...atBats].reverse().slice(0, 10).map((ab, i) => {
                    const def = BB_OUTCOMES.find(o => o.key === ab.outcome);
                    const color = def ? CAT_COLORS[def.cat] : 'var(--ink-3)';
                    return (
                      <div key={ab.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 10px', background: 'var(--surface-sunken)', borderRadius: '6px', fontSize: '12px' }}>
                        <span style={{ color: 'var(--ink-4)', minWidth: '20px' }}>{ab.inning}{ab.is_top_inning ? '▲' : '▼'}</span>
                        <span style={{ color: ab.team === 'home' ? '#3B82F6' : '#F97316', fontWeight: 700 }}>{ab.team === 'home' ? game.home_team.slice(0,3).toUpperCase() : game.away_team.slice(0,3).toUpperCase()}</span>
                        <span style={{ color: 'var(--ink-3)' }}>#{ab.batter_number} {ab.batter_name}</span>
                        <span style={{ marginLeft: 'auto', color, fontWeight: 800 }}>{def?.label ?? ab.outcome}</span>
                        {ab.runs_scored > 0 && <span style={{ color: '#22C55E', fontSize: '11px' }}>{ab.runs_scored}R {ab.rbi}RBI</span>}
                        {i === 0 && <span style={{ color: 'var(--ink-4)', fontSize: '10px' }}>← last</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── LINEUP TAB ── */}
        {tab === 'lineup' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              {(['away','home'] as const).map(team => {
                const key = team === 'home' ? 'lineup_home' : 'lineup_away';
                const lu = score[key] ?? [];
                const curIdx = team === 'home' ? (score.current_batter_home ?? 0) : (score.current_batter_away ?? 0);
                return (
                  <div key={team}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink-4)', letterSpacing: '1px', marginBottom: '8px' }}>
                      {team === 'away' ? `${game.away_team} (Away)` : `${game.home_team} (Home)`}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px' }}>
                      {lu.map((entry, idx) => (
                        <div key={idx} style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          background: idx === curIdx ? 'rgba(249,115,22,0.08)' : 'var(--surface-sunken)',
                          border: `1px solid ${idx === curIdx ? 'rgba(249,115,22,0.3)' : 'var(--hairline)'}`,
                          borderRadius: '8px', padding: '6px 8px',
                        }}>
                          <span style={{ fontSize: '11px', fontWeight: 700, color: idx === curIdx ? '#F97316' : 'var(--ink-4)', minWidth: '18px' }}>{idx + 1}.</span>
                          <input
                            style={{ width: '40px', padding: '3px 5px', background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '4px', color: 'var(--ink-1)', fontSize: '12px', fontWeight: 700, textAlign: 'center' }}
                            placeholder="#"
                            value={entry.number}
                            onChange={e => updateBatter(team, idx, 'number', e.target.value)}
                          />
                          {team === 'home' && rosterPlayers.length > 0 ? (
                            <select
                              style={{ flex: 1, padding: '3px 5px', background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '4px', color: 'var(--ink-1)', fontSize: '12px' }}
                              value={entry.player_id || ''}
                              onChange={e => updateBatter('home', idx, 'player_id', e.target.value)}
                            >
                              <option value="">— type name —</option>
                              {rosterPlayers.map(p => (
                                <option key={p.id} value={p.id}>#{p.number} {p.name}</option>
                              ))}
                            </select>
                          ) : null}
                          {(team === 'away' || !rosterPlayers.length || !entry.player_id) && (
                            <input
                              style={{ flex: 1, padding: '3px 5px', background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '4px', color: 'var(--ink-1)', fontSize: '12px' }}
                              placeholder="Name"
                              value={entry.name}
                              onChange={e => updateBatter(team, idx, 'name', e.target.value)}
                            />
                          )}
                          {idx === curIdx && <span style={{ color: '#F97316', fontSize: '14px' }}>●</span>}
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button className="btn btn-secondary" style={{ fontSize: '12px', padding: '5px 10px' }} onClick={() => addBatter(team)} disabled={lu.length >= 12}>+ Add Batter</button>
                      <button className="btn btn-secondary" style={{ fontSize: '12px', padding: '5px 10px' }} onClick={() => removeBatter(team)} disabled={lu.length <= 1}>− Remove</button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-secondary" onClick={() => {
                const s = ensureLineups(score);
                setScore(s);
                flash('Lineups initialized from roster');
              }}>↺ Auto-fill from Roster</button>
              <button className="btn btn-primary" onClick={() => saveLineup(score)}>💾 Save Lineups</button>
            </div>
          </div>
        )}

        {/* ── ADJUST TAB ── */}
        {tab === 'adjust' && (
          <div>
            <p style={{ color: 'var(--ink-3)', fontSize: '13px', marginBottom: '16px' }}>Manually correct any values — use for fixes, not normal scoring.</p>

            <div className="section-label">Inning</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '20px' }}>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--ink-3)', display: 'block', marginBottom: '5px', fontWeight: 600 }}>Inning #</label>
                <input type="number" min={1} max={15} className="form-control" style={{ textAlign: 'center', fontSize: '16px', fontWeight: 800 }} value={score.current_inning ?? 1} onChange={n('current_inning')} />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--ink-3)', display: 'block', marginBottom: '5px', fontWeight: 600 }}>Half</label>
                <select className="form-control" value={score.is_top_inning ?? 1} onChange={n('is_top_inning')}>
                  <option value={1}>▲ Top — Away batting</option>
                  <option value={0}>▼ Bottom — Home batting</option>
                </select>
              </div>
            </div>

            <div className="section-label">Count</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px', marginBottom: '20px' }}>
              {[{l:'Balls', f:'balls' as const, max:3},{l:'Strikes', f:'strikes' as const, max:2},{l:'Outs', f:'outs' as const, max:2}].map(({l,f,max}) => (
                <div key={f}>
                  <label style={{ fontSize: '11px', color: 'var(--ink-3)', display: 'block', marginBottom: '5px', fontWeight: 600 }}>{l}</label>
                  <input type="number" min={0} max={max} className="form-control" style={{ textAlign: 'center', fontSize: '16px', fontWeight: 800 }} value={score[f] ?? 0} onChange={n(f)} />
                </div>
              ))}
            </div>

            <div className="section-label">Runners on Base</div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
              {([['runner_first','1st'],['runner_second','2nd'],['runner_third','3rd']] as const).map(([f, lbl]) => {
                const on = !!score[f];
                return (
                  <button key={f} onClick={() => setScore(s => ({ ...s, [f]: on ? 0 : 1 }))} style={{
                    padding: '8px 16px', borderRadius: '8px', border: `2px solid ${on ? '#F97316' : 'var(--hairline)'}`,
                    background: on ? 'rgba(249,115,22,0.12)' : 'transparent', color: on ? '#F97316' : 'var(--ink-3)', cursor: 'pointer', fontWeight: 700,
                  }}>
                    {lbl} {on ? '●' : '○'}
                  </button>
                );
              })}
            </div>

            <div className="section-label">Totals (R / H / E)</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr 1fr', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
              <div />
              {['R','H','E'].map(h => <div key={h} style={{ fontSize: '11px', fontWeight: 800, color: h==='E'?'#EF4444':'var(--ink-1)', textAlign: 'center' }}>{h}</div>)}
              {[
                { name: game.away_team, r:'away_total_runs' as const, h:'away_hits' as const, e:'away_errors' as const },
                { name: game.home_team, r:'home_total_runs' as const, h:'home_hits' as const, e:'home_errors' as const },
              ].map(row => (
                <React.Fragment key={row.name}>
                  <div style={{ fontSize: '13px', fontWeight: 700 }}>{row.name}</div>
                  {[row.r, row.h, row.e].map(field => (
                    <input key={field} type="number" min={0} className="form-control" style={{ padding: '9px', textAlign: 'center', fontWeight: 800 }} value={score[field] ?? 0} onChange={n(field)} />
                  ))}
                </React.Fragment>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
              <button className="btn btn-primary" onClick={saveManual} disabled={saving}>
                {saved ? '✓ Pushed!' : saving ? 'Pushing...' : '📡 Push Update'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Soccer Score Modal ───────────────────────────────────────────────────────
const SoccerScoreModal = ({ game, onClose }: { game: Game; onClose: () => void }) => {
  const [score, setScore] = useState<Partial<SoccerScore>>({ home_score: 0, away_score: 0, period: 1, game_minutes: 0, status: 'not_started', events: [] });
  const [newEvent, setNewEvent] = useState({ event_type: 'goal', team: 'home', player_name: '', minute: 0 });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const EVENT_ICONS: Record<string, string> = { goal: '⚽', yellow_card: '🟨', red_card: '🟥', substitution: '🔄' };

  useEffect(() => { api.get(`/scores/soccer/${game.id}`).then(r => setScore(r.data)).catch(() => {}); }, [game.id]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/scores/soccer/${game.id}`, {
        home_score: score.home_score, away_score: score.away_score,
        period: score.period, game_minutes: score.game_minutes, status: score.status,
      });
      setScore(p => ({ ...p, ...r.data }));
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } finally { setSaving(false); }
  };

  const addEvent = async () => {
    await api.post(`/scores/soccer/${game.id}/event`, newEvent);
    const r = await api.get(`/scores/soccer/${game.id}`);
    setScore(r.data);
    setNewEvent(e => ({ ...e, player_name: '' }));
  };

  const delEvent = async (id: string) => {
    await api.delete(`/scores/soccer/${game.id}/event/${id}`);
    const r = await api.get(`/scores/soccer/${game.id}`);
    setScore(r.data);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '560px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>⚽ Soccer Scoreboard</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <p style={{ color: 'var(--ink-3)', fontSize: '13px', marginBottom: '22px' }}>{game.away_team} @ {game.home_team}</p>

        <div className="section-label">Match Status</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '22px' }}>
          <div>
            <label style={{ fontSize: '11px', color: 'var(--ink-3)', display: 'block', marginBottom: '5px', fontWeight: 600 }}>Period</label>
            <select className="form-control" value={score.status} onChange={e => setScore(s => ({ ...s, status: e.target.value as any }))}>
              <option value="not_started">Not Started</option>
              <option value="first_half">1st Half</option>
              <option value="half_time">Half Time</option>
              <option value="second_half">2nd Half</option>
              <option value="full_time">Full Time</option>
              <option value="extra_time">Extra Time</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '11px', color: 'var(--ink-3)', display: 'block', marginBottom: '5px', fontWeight: 600 }}>Minute</label>
            <input type="number" min={0} max={120} className="form-control" style={{ fontWeight: 800 }}
              value={score.game_minutes ?? 0} onChange={e => setScore(s => ({ ...s, game_minutes: Number(e.target.value) }))} />
          </div>
        </div>

        <div className="section-label">Score</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '16px', alignItems: 'center', marginBottom: '22px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: 'var(--ink-3)', marginBottom: '6px', fontWeight: 600 }}>{game.away_team} (Away)</div>
            <input type="number" min={0} className="form-control" style={{ padding: '10px', textAlign: 'center', fontSize: '28px', fontWeight: 900 }}
              value={score.away_score ?? 0} onChange={e => setScore(s => ({ ...s, away_score: Number(e.target.value) }))} />
          </div>
          <div style={{ fontSize: '22px', color: 'var(--hairline)', fontWeight: 700 }}>—</div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: 'var(--ink-3)', marginBottom: '6px', fontWeight: 600 }}>{game.home_team} (Home)</div>
            <input type="number" min={0} className="form-control" style={{ padding: '10px', textAlign: 'center', fontSize: '28px', fontWeight: 900 }}
              value={score.home_score ?? 0} onChange={e => setScore(s => ({ ...s, home_score: Number(e.target.value) }))} />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saved ? '✓ Pushed!' : saving ? 'Pushing...' : '📡 Push Score'}
          </button>
        </div>

        <div className="section-label">Add Event</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 60px', gap: '8px', marginBottom: '10px' }}>
          <select className="form-control" value={newEvent.event_type} onChange={e => setNewEvent(n => ({ ...n, event_type: e.target.value }))}>
            <option value="goal">⚽ Goal</option>
            <option value="yellow_card">🟨 Yellow Card</option>
            <option value="red_card">🟥 Red Card</option>
            <option value="substitution">🔄 Sub</option>
          </select>
          <select className="form-control" value={newEvent.team} onChange={e => setNewEvent(n => ({ ...n, team: e.target.value }))}>
            <option value="away">{game.away_team}</option>
            <option value="home">{game.home_team}</option>
          </select>
          <input className="form-control" placeholder="Player name" value={newEvent.player_name}
            onChange={e => setNewEvent(n => ({ ...n, player_name: e.target.value }))} />
          <input type="number" min={0} max={120} className="form-control" placeholder="Min"
            value={newEvent.minute} onChange={e => setNewEvent(n => ({ ...n, minute: Number(e.target.value) }))} />
        </div>
        <button className="btn btn-secondary" style={{ marginBottom: '16px' }} onClick={addEvent}>+ Add Event</button>

        {(score.events || []).length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {(score.events || []).map((ev: any, i: number) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', background: 'var(--surface-muted)', borderRadius: '8px', border: '1px solid var(--hairline)' }}>
                <span>{EVENT_ICONS[ev.event_type]}</span>
                <span style={{ fontWeight: 700, fontSize: '13px', color: '#F97316' }}>{ev.minute}'</span>
                <span style={{ fontSize: '12px', color: 'var(--ink-3)' }}>{ev.team === 'home' ? game.home_team : game.away_team}</span>
                <span style={{ fontSize: '13px', flex: 1 }}>{ev.player_name}</span>
                <button onClick={() => delEvent(ev.id)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: '14px', padding: '2px 6px' }}>✕</button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ─── Game Film Modal (with Highlight Clipper) ─────────────────────────────────
const GameFilmModal = ({ game, onClose }: { game: Game; onClose: () => void }) => {
  const videoRef  = useRef<HTMLVideoElement>(null);
  const [recording, setRecording]   = useState<GameVideo | null | undefined>(undefined);
  const [clips,     setClips]       = useState<GameVideo[]>([]);
  const [players,   setPlayers]     = useState<Player[]>([]);
  const [showClipper, setShowClipper] = useState(false);
  // Clipper state
  const [clipIn,    setClipIn]      = useState<number | null>(null);
  const [clipOut,   setClipOut]     = useState<number | null>(null);
  const [vidDur,    setVidDur]      = useState(0);
  const [clipTitle, setClipTitle]   = useState('');
  const [taggedIds, setTaggedIds]   = useState<string[]>([]);
  const [clipping,  setClipping]    = useState(false);
  const [clipErr,   setClipErr]     = useState('');
  const [clipDone,  setClipDone]    = useState(false);
  // Highlight upload state
  const [showUpload, setShowUpload]  = useState(false);
  const [hlFile,    setHlFile]      = useState<File | null>(null);
  const [hlTitle,   setHlTitle]     = useState('');
  const [hlProg,    setHlProg]      = useState(0);
  const [hlLoading, setHlLoading]   = useState(false);
  const [hlErr,     setHlErr]       = useState('');

  const loadData = useCallback(() => {
    api.get(`/videos?game_id=${game.id}`).then(r => {
      const vids = r.data as GameVideo[];
      setRecording(vids.find(v => v.video_type === 'recording') ?? null);
      setClips(vids.filter(v => v.video_type === 'highlight').sort((a, b) =>
        new Date(b.uploaded_at || 0).getTime() - new Date(a.uploaded_at || 0).getTime()
      ));
    }).catch(() => setRecording(null));
    if (game.team_id) api.get(`/players?team_id=${game.team_id}`).then(r => setPlayers(r.data)).catch(() => {});
  }, [game.id, game.team_id]);

  useEffect(() => { loadData(); }, [loadData]);

  const recUrl = recording
    ? mediaUrl(recording.file_path.startsWith('http') || recording.file_path.startsWith('/api/')
        ? recording.file_path
        : recording.file_path.startsWith('/uploads/')
          ? `${API_URL}${recording.file_path}`
          : `${HLS_BASE}${recording.file_path}`)
    : null;

  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

  const markIn  = () => { if (videoRef.current) setClipIn(videoRef.current.currentTime); };
  const markOut = () => { if (videoRef.current) setClipOut(videoRef.current.currentTime); };
  const togglePlayer = (id: string) => setTaggedIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const saveClip = async () => {
    if (clipIn === null || clipOut === null) { setClipErr('Set both start and end points first'); return; }
    if (clipOut <= clipIn) { setClipErr('End point must come after start point'); return; }
    if (!recording) return;
    setClipping(true); setClipErr('');
    try {
      const r = await api.post(`/videos/${recording.id}/clip`, {
        start_seconds: clipIn, end_seconds: clipOut,
        title: clipTitle, player_ids: taggedIds,
      });
      setClips(prev => [...(r.data.clips as GameVideo[]), ...prev]);
      setClipIn(null); setClipOut(null); setClipTitle(''); setTaggedIds([]);
      setShowClipper(false); setClipDone(true);
      setTimeout(() => setClipDone(false), 3000);
    } catch (e: any) { setClipErr(e.response?.data?.error || 'Clip creation failed'); }
    finally { setClipping(false); }
  };

  const uploadHighlight = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hlFile) { setHlErr('Select a video file'); return; }
    setHlLoading(true); setHlErr('');
    try {
      const fd = new FormData();
      fd.append('video', hlFile);
      fd.append('game_id', game.id);
      if (hlTitle.trim()) fd.append('title', hlTitle.trim());
      await api.post('/videos/upload-game-highlight', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (ev: any) => setHlProg(Math.round((ev.loaded / (ev.total || 1)) * 100)),
      });
      loadData(); setShowUpload(false); setHlFile(null); setHlTitle('');
    } catch (e: any) { setHlErr(e.response?.data?.error || 'Upload failed'); }
    finally { setHlLoading(false); }
  };

  const inPct  = vidDur > 0 && clipIn  !== null ? (clipIn  / vidDur) * 100 : null;
  const outPct = vidDur > 0 && clipOut !== null ? (clipOut / vidDur) * 100 : null;

  const btnBase: React.CSSProperties = {
    padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700,
    border: '1px solid var(--hairline)', background: 'var(--surface-muted)', color: 'var(--ink-3)',
    touchAction: 'manipulation',
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div style={{ width: '100%', maxWidth: 960, padding: '0 16px', maxHeight: '95vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 17, color: 'var(--ink-1)' }}>{game.title}</div>
            <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 2 }}>
              {game.away_team} @ {game.home_team}
              {game.away_final_score != null && game.home_final_score != null && (
                <span style={{ color: 'var(--ink-1)', fontWeight: 700, marginLeft: 8 }}>
                  {game.away_final_score}–{game.home_final_score}
                </span>
              )}
            </div>
          </div>
          <button className="modal-close" onClick={onClose} style={{ color: 'var(--ink-1)' }}>✕</button>
        </div>

        {/* Recording player */}
        {recording === undefined ? (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 12, padding: 60, textAlign: 'center', color: 'var(--ink-3)' }}>Loading…</div>
        ) : recUrl ? (
          <div style={{ background: '#000', borderRadius: 12, marginBottom: 12 }}>
            <video
              ref={videoRef}
              src={recUrl}
              controls
              playsInline
              style={{ width: '100%', borderRadius: 12, maxHeight: '55vh', display: 'block' }}
              onDurationChange={e => setVidDur((e.target as HTMLVideoElement).duration)}
            />
          </div>
        ) : (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 12, padding: '40px', textAlign: 'center', color: 'var(--ink-4)', marginBottom: 12 }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🎬</div>
            <div>No game recording found.</div>
            <div style={{ fontSize: 12, marginTop: 6 }}>Upload a recording from the Videos tab.</div>
          </div>
        )}

        {/* Action bar */}
        {recUrl && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <button style={{ ...btnBase, ...(showClipper ? { borderColor: '#F97316', color: '#F97316', background: 'rgba(249,115,22,0.08)' } : {}) }}
              onClick={() => { setShowClipper(v => !v); setShowUpload(false); }}>
              ✂️ {showClipper ? 'Hide Clip Creator' : 'Create Highlight Clip'}
            </button>
            <button style={{ ...btnBase, ...(showUpload ? { borderColor: '#22C55E', color: '#22C55E', background: 'rgba(34,197,94,0.08)' } : {}) }}
              onClick={() => { setShowUpload(v => !v); setShowClipper(false); }}>
              📤 {showUpload ? 'Hide Upload' : 'Upload Highlight Reel'}
            </button>
            {clipDone && <span style={{ fontSize: 13, color: '#22C55E', alignSelf: 'center' }}>✅ Clip saved!</span>}
          </div>
        )}

        {/* ── Clip Creator ── */}
        {showClipper && recUrl && (
          <div style={{ background: 'var(--surface)', border: '1px solid rgba(249,115,22,0.3)', borderRadius: 12, padding: 20, marginBottom: 12 }}>
            <div style={{ fontWeight: 800, fontSize: 13, color: '#F97316', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.6px' }}>✂️ Clip Creator</div>

            {/* Mark buttons */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              <button onClick={markIn} style={{ padding: '10px 18px', borderRadius: 8, border: `2px solid ${clipIn !== null ? '#F97316' : 'var(--hairline)'}`, background: clipIn !== null ? 'rgba(249,115,22,0.1)' : 'var(--surface-sunken)', color: clipIn !== null ? '#F97316' : 'var(--ink-3)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                ⏮ Set Start {clipIn !== null ? `(${fmt(clipIn)})` : ''}
              </button>
              <button onClick={markOut} style={{ padding: '10px 18px', borderRadius: 8, border: `2px solid ${clipOut !== null ? '#22C55E' : 'var(--hairline)'}`, background: clipOut !== null ? 'rgba(34,197,94,0.1)' : 'var(--surface-sunken)', color: clipOut !== null ? '#22C55E' : 'var(--ink-3)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                Set End ⏭ {clipOut !== null ? `(${fmt(clipOut)})` : ''}
              </button>
              {clipIn !== null && clipOut !== null && clipOut > clipIn && (
                <span style={{ alignSelf: 'center', fontSize: 13, color: 'var(--ink-3)' }}>
                  Duration: <strong style={{ color: 'var(--ink-1)' }}>{fmt(clipOut - clipIn)}</strong>
                </span>
              )}
            </div>

            {/* Timeline bar */}
            {vidDur > 0 && (
              <div style={{ position: 'relative', height: 24, background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', borderRadius: 6, marginBottom: 14, overflow: 'hidden' }}>
                {inPct !== null && outPct !== null && (
                  <div style={{ position: 'absolute', left: `${inPct}%`, width: `${outPct - inPct}%`, height: '100%', background: 'rgba(249,115,22,0.4)', borderLeft: '2px solid #F97316', borderRight: '2px solid #22C55E' }} />
                )}
                {inPct !== null && (
                  <div style={{ position: 'absolute', left: `${inPct}%`, top: 0, bottom: 0, width: 2, background: '#F97316' }}>
                    <span style={{ position: 'absolute', top: 2, left: 4, fontSize: 9, color: '#F97316', fontWeight: 700, whiteSpace: 'nowrap' }}>IN</span>
                  </div>
                )}
                {outPct !== null && (
                  <div style={{ position: 'absolute', left: `${outPct}%`, top: 0, bottom: 0, width: 2, background: '#22C55E' }}>
                    <span style={{ position: 'absolute', top: 2, left: 4, fontSize: 9, color: '#22C55E', fontWeight: 700, whiteSpace: 'nowrap' }}>OUT</span>
                  </div>
                )}
                <div style={{ position: 'absolute', right: 4, bottom: 2, fontSize: 10, color: 'var(--ink-4)' }}>{fmt(vidDur)}</div>
              </div>
            )}

            {/* Title */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 6 }}>Clip Title</label>
              <input className="form-control" value={clipTitle} onChange={e => setClipTitle(e.target.value)} placeholder="e.g. Great save, Corner kick goal…" style={{ fontSize: 13 }} />
            </div>

            {/* Player tagging */}
            {players.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 8 }}>Tag Players (saves to their profile)</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 6, maxHeight: 180, overflowY: 'auto' }}>
                  {players.map(p => {
                    const sel = taggedIds.includes(p.id);
                    return (
                      <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, cursor: 'pointer', background: sel ? 'rgba(249,115,22,0.1)' : 'var(--surface-sunken)', border: `1px solid ${sel ? '#F97316' : 'var(--hairline)'}`, fontSize: 12 }}>
                        <input type="checkbox" checked={sel} onChange={() => togglePlayer(p.id)} style={{ accentColor: '#F97316' }} />
                        <span>#{p.number ?? '?'} {p.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {clipErr && <div style={{ color: '#F87171', fontSize: 13, marginBottom: 10 }}>{clipErr}</div>}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button style={{ ...btnBase }} onClick={() => { setShowClipper(false); setClipIn(null); setClipOut(null); setClipErr(''); }}>Cancel</button>
              <button
                onClick={saveClip}
                disabled={clipping || clipIn === null || clipOut === null}
                style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: clipping ? 'var(--hairline)' : '#F97316', color: clipping ? 'var(--ink-4)' : '#fff', fontWeight: 800, fontSize: 13, cursor: clipping ? 'default' : 'pointer' }}>
                {clipping ? '⏳ Creating clip…' : '💾 Save Clip'}
              </button>
            </div>
            {clipping && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8, textAlign: 'center' }}>This may take 30–60 seconds for longer clips…</div>}
          </div>
        )}

        {/* ── Upload Highlight Reel ── */}
        {showUpload && (
          <div style={{ background: 'var(--surface)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 12, padding: 20, marginBottom: 12 }}>
            <div style={{ fontWeight: 800, fontSize: 13, color: '#22C55E', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.6px' }}>📤 Upload Highlight Reel</div>
            <form onSubmit={uploadHighlight}>
              <div className="form-group">
                <label>Title (optional)</label>
                <input className="form-control" value={hlTitle} onChange={e => setHlTitle(e.target.value)} placeholder="e.g. Game Highlights" style={{ fontSize: 13 }} />
              </div>
              <div className="form-group">
                <label>Video File</label>
                <input type="file" accept="video/*" className="form-control" style={{ padding: 8, fontSize: 13 }} onChange={e => setHlFile(e.target.files?.[0] || null)} />
                {hlFile && <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>{hlFile.name} · {(hlFile.size / 1024 / 1024).toFixed(1)} MB</div>}
              </div>
              {hlLoading && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--ink-3)', marginBottom: 4 }}><span>Uploading…</span><span>{hlProg}%</span></div>
                  <div style={{ height: 6, background: 'var(--hairline)', borderRadius: 4 }}>
                    <div style={{ height: '100%', width: `${hlProg}%`, background: '#22C55E', borderRadius: 4, transition: 'width 0.2s' }} />
                  </div>
                </div>
              )}
              {hlErr && <div style={{ color: '#F87171', fontSize: 13, marginBottom: 10 }}>{hlErr}</div>}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" style={{ ...btnBase }} onClick={() => setShowUpload(false)} disabled={hlLoading}>Cancel</button>
                <button type="submit" disabled={hlLoading || !hlFile} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: hlLoading || !hlFile ? 'var(--hairline)' : '#22C55E', color: hlLoading || !hlFile ? 'var(--ink-4)' : '#fff', fontWeight: 800, fontSize: 13, cursor: hlLoading || !hlFile ? 'default' : 'pointer' }}>
                  {hlLoading ? 'Uploading…' : '⬆️ Upload'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ── Highlights / Clips ── */}
        {clips.length > 0 && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 12, padding: 16, marginBottom: 12 }}>
            <div style={{ fontWeight: 800, fontSize: 12, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10 }}>
              🎬 Highlights & Clips ({clips.length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {clips.map(clip => {
                const player = clip.player_id ? players.find(p => p.id === clip.player_id) : null;
                const clipUrl = mediaUrl(clip.file_path.startsWith('http') || clip.file_path.startsWith('/api/') ? clip.file_path : clip.file_path.startsWith('/uploads/') ? `${API_URL}${clip.file_path}` : `${HLS_BASE}${clip.file_path}`);
                return (
                  <div key={clip.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: 'var(--surface-sunken)', borderRadius: 8, border: '1px solid var(--hairline)' }}>
                    <div style={{ fontSize: 24 }}>🎞️</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--ink-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{clip.title || 'Highlight'}</div>
                      {player && <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>#{player.number} {player.name}</div>}
                      {clip.duration_seconds && <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>{fmt(clip.duration_seconds)}</div>}
                    </div>
                    <a href={clipUrl} target="_blank" rel="noreferrer" style={{ padding: '5px 12px', borderRadius: 6, background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.3)', color: '#F97316', fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>▶ Play</a>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* AI Summary */}
        {game.ai_summary && (
          <div style={{ background: 'var(--surface)', border: '1px solid rgba(139,92,246,0.25)', borderRadius: 12, padding: '20px 24px', marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#A78BFA', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>✨ Game Recap</div>
            <p style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--ink-2)', margin: 0, whiteSpace: 'pre-wrap' }}>{game.ai_summary}</p>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ─── Stream Info ──────────────────────────────────────────────────────────────
const StreamInfoModal = ({ game, onClose }: { game: Game; onClose: () => void }) => {
  const [copied, setCopied] = useState<string | null>(null);
  // Always use the real server hostname — window.location.hostname returns
  // "localhost" inside the Capacitor iOS webview, not the production domain.
  const rtmpUrl = `rtmp://www.slowrib.com:1935/live`;
  const hlsPath = `${HLS_BASE}/live/${game.stream_key}/index.m3u8`;
  const hlsUrl = hlsPath.startsWith('/') ? `https://www.slowrib.com${hlsPath}` : hlsPath;
  const copy = (val: string, label: string) => { navigator.clipboard.writeText(val).then(() => { setCopied(label); setTimeout(() => setCopied(null), 2000); }); };
  const Row = ({ label, value }: { label: string; value: string }) => (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ fontSize: '11px', color: 'var(--ink-3)', marginBottom: '7px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
        <code style={{ flex: 1, background: 'var(--surface-sunken)', padding: '10px 14px', borderRadius: '8px', fontSize: '12px', color: '#22C55E', border: '1px solid var(--hairline)', wordBreak: 'break-all', lineHeight: 1.5 }}>{value}</code>
        <button className="btn btn-secondary btn-sm" style={{ flexShrink: 0 }} onClick={() => copy(value, label)}>{copied === label ? '✓' : '📋'}</button>
      </div>
    </div>
  );
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>📡 Camera Setup</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <p style={{ color: 'var(--ink-3)', fontSize: '13px', marginBottom: '20px' }}>{game.title}</p>
        <div style={{ background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: '10px', padding: '14px 16px', marginBottom: '20px' }}>
          <p style={{ fontSize: '13px', fontWeight: 700, color: '#F97316', marginBottom: '4px' }}>X Bot Go Setup</p>
          <p style={{ fontSize: '12px', color: 'var(--ink-3)', lineHeight: 1.6 }}>Enter the RTMP URL + Stream Key below into your X Bot Go camera app.</p>
        </div>
        <Row label="RTMP Server URL" value={rtmpUrl} />
        <Row label="Stream Key" value={game.stream_key} />
        <Row label="Viewer Playback URL (HLS)" value={hlsUrl} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ─── Create / Edit Team ───────────────────────────────────────────────────────
const TeamModal = ({ team, onClose, onSaved }: { team?: Team; onClose: () => void; onSaved: () => void }) => {
  const [form, setForm] = useState({
    name: team?.name || '',
    sport: team?.sport || 'soccer',
    season_year: team?.season_year?.toString() || new Date().getFullYear().toString(),
    primary_color: team?.primary_color || '#22C55E',
    secondary_color: team?.secondary_color || '#ffffff',
    background_theme: team?.background_theme || 'dark',
    description: team?.description || '',
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>(team?.logo_url || '');
  const [logoUploading, setLogoUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = (f: keyof typeof form) => (e: any) => setForm(p => ({ ...p, [f]: e.target.value }));

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      if (team) { await api.put(`/teams/${team.id}`, form); }
      else { await api.post('/teams', form); }

      // Upload logo if selected
      if (logoFile && team) {
        setLogoUploading(true);
        const fd = new FormData();
        fd.append('logo', logoFile);
        fd.append('team_id', team.id);
        await api.post('/videos/upload-team-logo', fd);
        setLogoUploading(false);
      }

      onSaved(); onClose();
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to save team'); }
    finally { setLoading(false); setLogoUploading(false); }
  };

  const primaryHex = form.primary_color;
  const secondaryHex = form.secondary_color;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>{team ? 'Edit Team' : 'New Team'}</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <form onSubmit={handleSubmit}>
          <div className="form-group"><label>Team Name</label><input className="form-control" value={form.name} onChange={set('name')} placeholder="e.g. Carmel FC 2013G Gold NL" required /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div className="form-group">
              <label>Sport</label>
              <select className="form-control" value={form.sport} onChange={set('sport') as any}>
                <option value="soccer">⚽ Soccer</option>
                <option value="baseball">⚾ Baseball</option>
                <option value="softball">🥎 Softball</option>
                <option value="basketball">🏀 Basketball</option>
              </select>
            </div>
            <div className="form-group"><label>Season Year</label><input type="number" className="form-control" value={form.season_year} onChange={set('season_year')} /></div>
          </div>

          {/* Colors */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div className="form-group">
              <label>Primary Color</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="color" value={form.primary_color} onChange={set('primary_color')} style={{ width: 40, height: 36, padding: '2px', background: 'none', border: '1px solid var(--hairline)', borderRadius: '6px', cursor: 'pointer' }} />
                <input className="form-control" value={form.primary_color} onChange={set('primary_color')} style={{ flex: 1 }} />
              </div>
            </div>
            <div className="form-group">
              <label>Secondary Color</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="color" value={form.secondary_color} onChange={set('secondary_color')} style={{ width: 40, height: 36, padding: '2px', background: 'none', border: '1px solid var(--hairline)', borderRadius: '6px', cursor: 'pointer' }} />
                <input className="form-control" value={form.secondary_color} onChange={set('secondary_color')} style={{ flex: 1 }} />
              </div>
            </div>
          </div>

          {/* Background theme + preview */}
          <div className="form-group">
            <label>Background Theme</label>
            <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
              {(['dark', 'light'] as const).map(t => (
                <button key={t} type="button" onClick={() => setForm(p => ({ ...p, background_theme: t }))}
                  style={{ flex: 1, padding: '10px', borderRadius: '8px', border: `2px solid ${form.background_theme === t ? primaryHex : 'var(--hairline)'}`, background: t === 'dark' ? '#0A0D12' : '#F5F7FA', color: t === 'dark' ? 'var(--ink-1)' : 'var(--surface)', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>
                  {t === 'dark' ? '🌙 Dark' : '☀️ Light'}
                </button>
              ))}
            </div>
            {/* Color preview strip */}
            <div style={{ marginTop: '10px', borderRadius: '8px', overflow: 'hidden', height: '32px', display: 'flex' }}>
              <div style={{ flex: 1, background: primaryHex }} />
              <div style={{ flex: 1, background: secondaryHex }} />
              <div style={{ flex: 2, background: form.background_theme === 'dark' ? '#0A0D12' : '#F5F7FA' }} />
            </div>
          </div>

          {/* Logo upload — only when editing existing team */}
          {team && (
            <div className="form-group">
              <label>Team Logo</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginTop: '6px', flexWrap: 'wrap' }}>
                {logoPreview && (
                  <img src={logoPreview} alt="Logo preview" style={{ width: 56, height: 56, objectFit: 'contain', borderRadius: '8px', border: '1px solid var(--hairline)', background: 'var(--surface-sunken)' }} />
                )}
                <label style={{ cursor: 'pointer', padding: '8px 14px', borderRadius: '7px', border: '1px dashed var(--ink-4)', background: 'var(--surface)', color: 'var(--ink-1)', fontSize: '13px' }}>
                  {logoUploading ? 'Uploading…' : logoFile ? logoFile.name : 'Choose image'}
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoChange} />
                </label>
                <span style={{ fontSize: '11px', color: 'var(--ink-4)' }}>JPG, PNG, SVG · max 10 MB</span>
              </div>
            </div>
          )}

          <div className="form-group"><label>Description (optional)</label><textarea className="form-control" value={form.description} onChange={set('description') as any} placeholder="Team description, division, coach..." rows={2} style={{ resize: 'vertical' }} /></div>
          {error && <div style={{ color: '#F87171', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || logoUploading}>{loading ? 'Saving...' : team ? 'Update Team' : 'Create Team'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Create / Edit Player ─────────────────────────────────────────────────────
const PlayerModal = ({ player, teamId, onClose, onSaved }: { player?: Player; teamId: string; onClose: () => void; onSaved: () => void }) => {
  const [form, setForm] = useState({
    name: player?.name || '',
    number: player?.number?.toString() || '',
    position: player?.position || '',
    grade: player?.grade || '',
    age: player?.age?.toString() || '',
  });
  const [photo, setPhoto] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = (f: keyof typeof form) => (e: any) => setForm(p => ({ ...p, [f]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const body = { ...form, number: form.number ? parseInt(form.number) : null, age: form.age ? parseInt(form.age) : null, team_id: teamId };
      let savedId: string;
      if (player) { await api.put(`/players/${player.id}`, body); savedId = player.id; }
      else { const r = await api.post('/players', body); savedId = r.data.id; }

      if (photo) {
        const fd = new FormData(); fd.append('photo', photo); fd.append('player_id', savedId);
        await api.post('/videos/upload-photo', fd);
      }

      onSaved();
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to save player'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>{player ? 'Edit Player' : 'Add Player'}</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <form onSubmit={handleSubmit}>
          <div className="form-group"><label>Player Name</label><input className="form-control" value={form.name} onChange={set('name')} required /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
            <div className="form-group"><label>Jersey #</label><input type="number" className="form-control" value={form.number} onChange={set('number')} placeholder="#" /></div>
            <div className="form-group"><label>Grade</label><input className="form-control" value={form.grade} onChange={set('grade')} placeholder="e.g. 7th" /></div>
            <div className="form-group"><label>Age</label><input type="number" className="form-control" value={form.age} onChange={set('age')} /></div>
          </div>
          <div className="form-group"><label>Position</label><input className="form-control" value={form.position} onChange={set('position')} placeholder="e.g. Forward, Midfielder, Goalkeeper" /></div>
          <div className="form-group">
            <label>Player Photo (optional)</label>
            <input type="file" accept="image/*" className="form-control" style={{ padding: '8px' }} onChange={e => setPhoto(e.target.files?.[0] || null)} />
          </div>
          {error && <div style={{ color: '#F87171', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : player ? 'Update' : 'Add Player'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Roster Scan Modal ────────────────────────────────────────────────────────
interface ScannedPlayer { name: string; number: string; position: string; grade: string; include: boolean; }

const RosterScanModal = ({ teamId, onClose, onImported }: { teamId: string; onClose: () => void; onImported: () => void }) => {
  const [image, setImage]       = useState<File | null>(null);
  const [preview, setPreview]   = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [importing, setImporting] = useState(false);
  const [players, setPlayers]   = useState<ScannedPlayer[] | null>(null);
  const [error, setError]       = useState('');
  const [imported, setImported] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const pickImage = (file: File) => {
    setImage(file);
    setPlayers(null);
    setError('');
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const scan = async () => {
    if (!image) return;
    setScanning(true); setError('');
    try {
      const fd = new FormData();
      fd.append('image', image);
      const r = await api.post('/players/roster-scan', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPlayers((r.data.players as any[]).map(p => ({ ...p, number: p.number ?? '', position: p.position ?? '', grade: p.grade ?? '', include: true })));
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to scan image');
    } finally { setScanning(false); }
  };

  const updatePlayer = (i: number, field: keyof ScannedPlayer, val: string | boolean) =>
    setPlayers(ps => ps ? ps.map((p, j) => j === i ? { ...p, [field]: val } : p) : ps);

  const doImport = async () => {
    if (!players) return;
    const toImport = players.filter(p => p.include && p.name.trim());
    if (!toImport.length) { setError('No players selected'); return; }
    setImporting(true); setError('');
    try {
      await api.post('/players/roster-import', { team_id: teamId, players: toImport });
      setImported(true);
      onImported();
      setTimeout(onClose, 1500);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Import failed');
    } finally { setImporting(false); }
  };

  const inputStyle: React.CSSProperties = { background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', borderRadius: 6, color: 'var(--ink-1)', padding: '4px 8px', fontSize: 12, width: '100%' };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 620, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2>📷 Scan Roster from Photo</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '0 0 4px' }}>
          {/* Upload area */}
          {!preview ? (
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) pickImage(f); }}
              style={{ border: '2px dashed var(--hairline)', borderRadius: 12, padding: '48px 24px', textAlign: 'center', cursor: 'pointer', margin: '0 0 16px', transition: 'border-color 0.15s' }}
            >
              <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Drop a roster photo here</div>
              <div style={{ fontSize: 13, color: 'var(--ink-4)', marginBottom: 16 }}>or click to browse — printed sheets, whiteboards, apps</div>
              <button className="btn btn-secondary btn-sm" type="button">Choose Photo</button>
              <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) pickImage(f); }} />
            </div>
          ) : (
            <div style={{ marginBottom: 16 }}>
              <img src={preview} alt="Roster" style={{ width: '100%', maxHeight: 240, objectFit: 'contain', borderRadius: 10, border: '1px solid var(--hairline)', background: 'var(--surface-sunken)' }} />
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => { setPreview(null); setImage(null); setPlayers(null); }}>✕ Remove photo</button>
            </div>
          )}

          {/* Scan button */}
          {preview && !players && (
            <button className="btn btn-primary" style={{ width: '100%', marginBottom: 16 }} onClick={scan} disabled={scanning}>
              {scanning ? '🔍 Scanning…' : '🔍 Scan Roster with AI'}
            </button>
          )}

          {error && <div style={{ color: '#F87171', fontSize: 13, marginBottom: 12, padding: '8px 12px', background: 'rgba(239,68,68,0.08)', borderRadius: 8 }}>{error}</div>}

          {/* Parsed player table */}
          {players && !imported && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-3)' }}>
                  Found {players.length} player{players.length !== 1 ? 's' : ''} — edit before importing
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => setPlayers(null)}>← Re-scan</button>
              </div>

              {/* Header */}
              <div style={{ display: 'grid', gridTemplateColumns: '24px 1fr 60px 100px 70px', gap: 6, padding: '6px 4px', fontSize: 10, fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.6px', borderBottom: '1px solid var(--hairline)', marginBottom: 4 }}>
                <div></div><div>Name</div><div>#</div><div>Position</div><div>Grade</div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
                {players.map((p, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '24px 1fr 60px 100px 70px', gap: 6, alignItems: 'center', padding: '4px', borderRadius: 6, background: p.include ? 'transparent' : 'rgba(0,0,0,0.2)', opacity: p.include ? 1 : 0.4 }}>
                    <input type="checkbox" checked={p.include} onChange={e => updatePlayer(i, 'include', e.target.checked)} style={{ cursor: 'pointer', accentColor: '#F97316' }} />
                    <input value={p.name} onChange={e => updatePlayer(i, 'name', e.target.value)} style={inputStyle} placeholder="Name" />
                    <input value={p.number} onChange={e => updatePlayer(i, 'number', e.target.value)} style={{ ...inputStyle }} placeholder="#" />
                    <input value={p.position} onChange={e => updatePlayer(i, 'position', e.target.value)} style={inputStyle} placeholder="Position" />
                    <input value={p.grade} onChange={e => updatePlayer(i, 'grade', e.target.value)} style={inputStyle} placeholder="Grade" />
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>{players.filter(p => p.include).length} of {players.length} selected</div>
                <button className="btn btn-primary" onClick={doImport} disabled={importing}>
                  {importing ? 'Importing…' : `✅ Import ${players.filter(p => p.include).length} Player${players.filter(p => p.include).length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </>
          )}

          {imported && (
            <div style={{ textAlign: 'center', padding: '32px 16px' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>Roster imported!</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Player Stats Modal ───────────────────────────────────────────────────────
const PlayerStatsModal = ({ player, games, sport, onClose }: { player: Player; games: Game[]; sport?: string; onClose: () => void }) => {
  const isBaseball = sport === 'baseball' || sport === 'softball';

  // statsMap: gameId → stat row (empty object for games with no entry yet)
  const [statsMap, setStatsMap] = useState<Record<string, any>>({});
  const [saving, setSaving]     = useState<string | null>(null);
  const [saved,  setSaved]      = useState<string | null>(null);

  const teamGames = games
    .filter(g => g.team_id === player.team_id)
    .sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime());

  // Load all player stats once on open
  useEffect(() => {
    api.get(`/players/${player.id}/stats`).then(r => {
      const m: Record<string, any> = {};
      r.data.forEach((s: any) => { m[s.game_id] = s; });
      setStatsMap(m);
    }).catch(() => {});
  }, [player.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (gameId: string, field: string, value: number) =>
    setStatsMap(prev => ({ ...prev, [gameId]: { ...(prev[gameId] || {}), [field]: value } }));

  const saveRow = async (gameId: string) => {
    setSaving(gameId);
    try {
      await api.put(`/players/${player.id}/stats/${gameId}`, statsMap[gameId] || {});
      setSaved(gameId);
      setTimeout(() => setSaved(g => g === gameId ? null : g), 2500);
    } finally { setSaving(null); }
  };

  const soccerFields: [string, string][] = [
    ['G', 'goals'], ['A', 'assists'], ['YC', 'yellow_cards'], ['RC', 'red_cards'],
    ['Min', 'minutes_played'], ['Shots', 'shots'], ['SOT', 'shots_on_target'],
  ];
  const battingFields: [string, string][] = [
    ['AB', 'at_bats'], ['H', 'hits'], ['R', 'runs'], ['RBI', 'rbis'],
    ['2B', 'doubles'], ['3B', 'triples'], ['HR', 'home_runs'],
    ['BB', 'walks'], ['K', 'strikeouts'], ['SB', 'stolen_bases'],
  ];
  const pitchingFields: [string, string][] = [
    ['IP', 'innings_pitched'], ['ER', 'earned_runs'],
    ['Kp', 'pitching_strikeouts'], ['BBp', 'pitching_walks'], ['E', 'errors'],
  ];
  const fields = isBaseball ? [...battingFields, ...pitchingFields] : soccerFields;

  const cellInput = (gameId: string, field: string, isIp = false) => (
    <input
      type="number" min={0} step={isIp ? 0.1 : 1}
      value={statsMap[gameId]?.[field] ?? 0}
      onChange={e => update(gameId, field, Number(e.target.value))}
      style={{
        width: '42px', textAlign: 'center', background: 'var(--surface-sunken)',
        border: '1px solid var(--hairline)', borderRadius: '4px', color: 'var(--ink-1)',
        padding: '4px 2px', fontSize: '12px', fontWeight: 700,
      }}
    />
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '960px', width: '95vw' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Stats — {player.name}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {!teamGames.length ? (
          <div style={{ color: 'var(--ink-3)', padding: '24px', textAlign: 'center' }}>No games found for this team.</div>
        ) : (
          <div style={{ overflowX: 'auto', maxHeight: '68vh', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead style={{ position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1 }}>
                <tr style={{ borderBottom: '1px solid var(--hairline)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--ink-3)', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', whiteSpace: 'nowrap', minWidth: '140px' }}>Game</th>
                  {fields.map(([label]) => (
                    <th key={label} style={{ textAlign: 'center', padding: '8px 4px', color: 'var(--ink-3)', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{label}</th>
                  ))}
                  <th style={{ padding: '8px 8px', minWidth: '60px' }}></th>
                </tr>
              </thead>
              <tbody>
                {teamGames.map((game, i) => {
                  const isSavingThis = saving === game.id;
                  const isSavedThis  = saved  === game.id;
                  return (
                    <tr key={game.id} style={{ borderBottom: '1px solid var(--hairline)', background: i % 2 === 0 ? 'transparent' : 'var(--surface-muted)' }}>
                      <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                        <div style={{ fontSize: '11px', color: 'var(--ink-3)' }}>{parseGameTime(game.scheduled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ink-1)', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{game.title}</div>
                      </td>
                      {fields.map(([, f]) => (
                        <td key={f} style={{ padding: '4px 3px', textAlign: 'center' }}>
                          {cellInput(game.id, f, f === 'innings_pitched')}
                        </td>
                      ))}
                      <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                        <button
                          onClick={() => saveRow(game.id)}
                          disabled={isSavingThis}
                          style={{
                            fontSize: '11px', padding: '4px 10px', borderRadius: '5px', border: 'none',
                            cursor: 'pointer', fontWeight: 700,
                            background: isSavedThis ? '#22C55E' : '#F97316', color: '#fff',
                          }}
                        >
                          {isSavedThis ? '✓' : isSavingThis ? '…' : 'Save'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ─── Import iCal Modal ────────────────────────────────────────────────────────
const ImportIcalModal = ({ teams, onClose, onImported }: {
  teams: Team[];
  onClose: () => void;
  onImported: (count: number) => void;
}) => {
  const [teamId,  setTeamId]  = useState('');
  const [url,     setUrl]     = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [result,  setResult]  = useState<{ imported: number; updated: number; deleted: number; skipped: number; total: number } | null>(null);

  const handleImport = async () => {
    if (!teamId) { setError('Please select a team'); return; }
    if (!url.trim()) { setError('Please enter a calendar URL'); return; }
    setLoading(true); setError(''); setResult(null);
    try {
      const r = await api.post('/games/import-ical', { url: url.trim(), teamId });
      setResult(r.data);
      onImported(r.data.imported);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Import failed. Check the URL and try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={result ? onClose : undefined}>
      <div className="modal" style={{ maxWidth: '480px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📅 Import from Calendar</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <p style={{ color: 'var(--ink-3)', fontSize: '13px', marginBottom: '20px' }}>
          Paste an iCalendar (.ics) URL from TeamSnap, GameChanger, Stack TeamApp, or any calendar app to import games. The URL is saved and the schedule syncs automatically every hour.
        </p>

        <div className="form-group">
          <label>Team to import into</label>
          <select className="form-control" value={teamId} onChange={e => setTeamId(e.target.value)} disabled={!!result}>
            <option value="">— Select a team —</option>
            {teams.map(t => (
              <option key={t.id} value={t.id}>{t.name} · {t.sport} {t.season_year}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>iCalendar URL</label>
          <input
            className="form-control"
            type="text"
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="webcal:// or https:// calendar URL"
            disabled={!!result}
            onKeyDown={e => { if (e.key === 'Enter' && !result) handleImport(); }}
          />
          <div style={{ fontSize: '11px', color: 'var(--ink-4)', marginTop: '6px' }}>
            Stack TeamApp: Fixtures → Share → iCal/Subscribe link. webcal:// and https:// URLs both work.
          </div>
        </div>

        {error && (
          <div style={{ color: '#F87171', fontSize: '13px', marginBottom: '14px', background: 'rgba(248,113,113,0.08)', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(248,113,113,0.2)' }}>
            {error}
          </div>
        )}

        {result && (
          <div style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: '8px', padding: '14px 16px', marginBottom: '16px' }}>
            <div style={{ fontSize: '15px', fontWeight: 800, color: '#22C55E', marginBottom: '6px' }}>✓ Sync complete</div>
            <div style={{ fontSize: '12px', color: 'var(--ink-3)', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {result.imported > 0 && <span style={{ color: '#22C55E' }}>+{result.imported} added</span>}
              {result.updated  > 0 && <span style={{ color: '#F97316' }}>{result.updated} updated</span>}
              {result.deleted  > 0 && <span style={{ color: '#EF4444' }}>{result.deleted} removed</span>}
              <span>{result.skipped} unchanged</span>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>{result ? 'Done' : 'Cancel'}</button>
          {!result && (
            <button className="btn btn-primary" onClick={handleImport} disabled={loading}>
              {loading ? 'Importing…' : 'Import Games'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Upload Video Modal ───────────────────────────────────────────────────────
const UploadVideoModal = ({ videoType, games, players, teams, onClose, onUploaded }: {
  videoType: 'recording' | 'highlight';
  games: Game[];
  players: Player[];
  teams: Team[];
  onClose: () => void;
  onUploaded: () => void;
}) => {
  const [gameId,        setGameId]        = useState('');
  const [teamId,        setTeamId]        = useState('');
  const [playerIds,     setPlayerIds]     = useState<string[]>([]);
  const [title,         setTitle]         = useState('');
  const [ourTeamColor,  setOurTeamColor]  = useState('');
  const [opponentColor, setOpponentColor] = useState('');
  const [matchFocus,    setMatchFocus]    = useState('');
  const [file,          setFile]          = useState<File | null>(null);
  const [progress,      setProgress]      = useState(0);
  const [uploading,     setUploading]     = useState(false);
  const [error,         setError]         = useState('');

  // Filter players by selected team
  const filteredPlayers = teamId
    ? players.filter(p => p.team_id === teamId)
    : players;

  const togglePlayer = (id: string) =>
    setPlayerIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleUpload = async () => {
    if (!file) { setError('Please select a video file'); return; }
    if (videoType === 'recording' && !gameId) { setError('Please select a game'); return; }
    if (videoType === 'highlight' && playerIds.length === 0) { setError('Please select at least one player'); return; }

    setUploading(true); setError('');
    const fd = new FormData();
    fd.append('video', file);
    fd.append('video_type', videoType);
    if (gameId) fd.append('game_id', gameId);
    playerIds.forEach(id => fd.append('player_ids', id));
    if (title)         fd.append('title',          title);
    if (ourTeamColor)  fd.append('our_team_color', ourTeamColor);
    if (opponentColor) fd.append('opponent_color', opponentColor);
    if (matchFocus)    fd.append('match_focus',    matchFocus);

    try {
      await api.post('/videos/upload', fd, {
        onUploadProgress: (e: any) => setProgress(Math.round((e.loaded / e.total) * 100)),
      });
      onUploaded();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally { setUploading(false); setProgress(0); }
  };

  return (
    <div className="modal-overlay" onClick={uploading ? undefined : onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{videoType === 'recording' ? '🎬 Upload Game Recording' : '⭐ Upload Highlight Clip'}</h2>
          <button className="modal-close" onClick={onClose} disabled={uploading}>✕</button>
        </div>

        <div className="form-group">
          <label>Title (optional)</label>
          <input className="form-control" value={title} onChange={e => setTitle(e.target.value)}
            placeholder={videoType === 'recording' ? 'Full Game Recording' : 'e.g. Hat trick vs Eagles'} />
        </div>

        {videoType === 'recording' && (
          <>
            <div className="form-group">
              <label>Game</label>
              <select className="form-control" value={gameId} onChange={e => setGameId(e.target.value)}>
                <option value="">— Select game —</option>
                {games.map(g => <option key={g.id} value={g.id}>{parseGameTime(g.scheduled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {g.title}</option>)}
              </select>
            </div>

            <div style={{ fontSize: '11px', color: 'var(--ink-3)', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '8px', marginTop: '4px' }}>
              AI Analysis Context (optional)
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div className="form-group" style={{ flex: 1, marginBottom: '12px' }}>
                <label style={{ fontSize: '12px' }}>Our Team Jersey Color</label>
                <input className="form-control" value={ourTeamColor} onChange={e => setOurTeamColor(e.target.value)}
                  placeholder="e.g. Blue" />
              </div>
              <div className="form-group" style={{ flex: 1, marginBottom: '12px' }}>
                <label style={{ fontSize: '12px' }}>Opponent Jersey Color</label>
                <input className="form-control" value={opponentColor} onChange={e => setOpponentColor(e.target.value)}
                  placeholder="e.g. White" />
              </div>
            </div>
            <div className="form-group">
              <label style={{ fontSize: '12px' }}>Coach's Match Focus</label>
              <textarea className="form-control" value={matchFocus} onChange={e => setMatchFocus(e.target.value)}
                rows={2} placeholder="e.g. Breaking lines through midfield and pressing high in the opponent's half" />
            </div>
          </>
        )}

        {videoType === 'highlight' && (
          <>
            {/* Step 1: Team filter */}
            <div className="form-group">
              <label>Team</label>
              <select className="form-control" value={teamId}
                onChange={e => { setTeamId(e.target.value); setPlayerIds([]); }}>
                <option value="">— Select a team to filter players —</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>

            {/* Step 2: Multi-player checkboxes */}
            <div className="form-group">
              <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Players {playerIds.length > 0 && <span style={{ color: '#F97316', fontWeight: 700 }}>({playerIds.length} selected)</span>}</span>
                {playerIds.length > 0 && (
                  <button type="button" onClick={() => setPlayerIds([])}
                    style={{ background: 'none', border: 'none', color: 'var(--ink-3)', fontSize: '12px', cursor: 'pointer', padding: 0 }}>
                    Clear all
                  </button>
                )}
              </label>
              {filteredPlayers.length === 0 ? (
                <div style={{ padding: '12px', background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', borderRadius: '8px', color: 'var(--ink-4)', fontSize: '13px', textAlign: 'center' }}>
                  {teamId ? 'No players on this team' : 'Select a team above to see players'}
                </div>
              ) : (
                <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid var(--hairline)', borderRadius: '8px', background: 'var(--surface-sunken)' }}>
                  {filteredPlayers.map((p, i) => (
                    <label key={p.id} style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '9px 12px', cursor: 'pointer',
                      borderBottom: i < filteredPlayers.length - 1 ? '1px solid var(--hairline)' : 'none',
                      background: playerIds.includes(p.id) ? 'rgba(249,115,22,0.08)' : 'transparent',
                      transition: 'background 0.1s',
                    }}>
                      <input
                        type="checkbox"
                        checked={playerIds.includes(p.id)}
                        onChange={() => togglePlayer(p.id)}
                        style={{ accentColor: '#F97316', width: '15px', height: '15px', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: '14px', fontWeight: playerIds.includes(p.id) ? 700 : 400, color: playerIds.includes(p.id) ? 'var(--ink-1)' : 'var(--ink-3)' }}>
                        {p.name}{p.number != null ? <span style={{ color: '#F97316', marginLeft: '6px', fontSize: '12px' }}>#{p.number}</span> : ''}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Step 3: Optional game link */}
            <div className="form-group">
              <label>Game (optional — links the clip to a game)</label>
              <select className="form-control" value={gameId} onChange={e => setGameId(e.target.value)}>
                <option value="">— Link to game —</option>
                {games.map(g => <option key={g.id} value={g.id}>{parseGameTime(g.scheduled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {g.title}</option>)}
              </select>
            </div>
          </>
        )}

        <div className="form-group">
          <label>Video File (MP4, MOV, etc.)</label>
          <input type="file" accept="video/*" className="form-control" style={{ padding: '8px' }}
            onChange={e => setFile(e.target.files?.[0] || null)} />
          {file && <div style={{ fontSize: '12px', color: 'var(--ink-3)', marginTop: '6px' }}>{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</div>}
        </div>

        {uploading && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--ink-3)', marginBottom: '6px' }}>
              <span>Uploading...</span><span>{progress}%</span>
            </div>
            <div style={{ height: '6px', background: 'var(--hairline)', borderRadius: '3px' }}>
              <div style={{ height: '100%', width: `${progress}%`, background: '#F97316', borderRadius: '3px', transition: 'width 0.3s' }} />
            </div>
          </div>
        )}
        {error && <div style={{ color: '#F87171', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={uploading}>Cancel</button>
          <button className="btn btn-primary" onClick={handleUpload} disabled={uploading || !file}>
            {uploading ? `Uploading ${progress}%...` : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Coaches Modal ────────────────────────────────────────────────────────────
const CoachesModal = ({ team, onClose }: { team: Team; onClose: () => void }) => {
  const [coaches, setCoaches] = useState<any[]>([]);
  const [allCoachUsers, setAllCoachUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [addUserId, setAddUserId] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const fetchCoaches = useCallback(async () => {
    const [coachesRes, usersRes] = await Promise.all([
      api.get(`/teams/${team.id}/coaches`),
      api.get('/auth/users'),
    ]);
    const assigned = new Set(coachesRes.data.map((c: any) => c.id));
    setCoaches(coachesRes.data);
    setAllCoachUsers(usersRes.data.filter((u: any) => u.role === 'coach' && !assigned.has(u.id)));
    setLoading(false);
  }, [team.id]);

  useEffect(() => { fetchCoaches(); }, [fetchCoaches]);

  const removeCoach = async (userId: string) => {
    await api.delete(`/teams/${team.id}/coaches/${userId}`);
    fetchCoaches();
  };

  const addCoach = async () => {
    if (!addUserId) return;
    setAdding(true); setError('');
    try {
      await api.post(`/teams/${team.id}/coaches`, { userId: addUserId });
      setAddUserId('');
      fetchCoaches();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add coach');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
        <div className="modal-header">
          <h2>👥 Coaches — {team.name}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '24px', color: 'var(--ink-4)' }}>Loading…</div>
        ) : (
          <>
            <div className="section-label" style={{ marginBottom: '10px' }}>Assigned Coaches</div>
            {coaches.length === 0 ? (
              <div style={{ color: 'var(--ink-4)', fontSize: '13px', marginBottom: '20px' }}>No coaches assigned yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
                {coaches.map(c => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: 'var(--surface-sunken)', borderRadius: '8px', border: '1px solid var(--hairline)' }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #22D3EE, #0891B2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '13px', color: '#fff', flexShrink: 0 }}>
                      {c.username.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: '14px' }}>{c.username}</div>
                      <div style={{ fontSize: '12px', color: 'var(--ink-3)' }}>{c.email}</div>
                    </div>
                    <button className="btn btn-danger btn-sm" onClick={() => removeCoach(c.id)}>Remove</button>
                  </div>
                ))}
              </div>
            )}

            <div className="section-label" style={{ marginBottom: '10px' }}>Add Coach</div>
            {allCoachUsers.length === 0 ? (
              <div style={{ fontSize: '13px', color: 'var(--ink-4)', padding: '12px', background: 'var(--surface-sunken)', borderRadius: '8px', border: '1px solid var(--hairline)' }}>
                No available coaches. Promote a user to the <strong style={{ color: '#22D3EE' }}>coach</strong> role in the Users tab first.
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '8px' }}>
                <select className="form-control" value={addUserId} onChange={e => setAddUserId(e.target.value)} style={{ flex: 1 }}>
                  <option value="">— Select coach —</option>
                  {allCoachUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.username} ({u.email})</option>
                  ))}
                </select>
                <button className="btn btn-primary" onClick={addCoach} disabled={!addUserId || adding} style={{ whiteSpace: 'nowrap' }}>
                  {adding ? 'Adding…' : 'Add'}
                </button>
              </div>
            )}
            {error && <div style={{ color: '#F87171', fontSize: '13px', marginTop: '10px' }}>{error}</div>}
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ─── iCal URL Modal ───────────────────────────────────────────────────────────
const IcalModal = ({ team, onClose, onSaved }: { team: Team; onClose: () => void; onSaved: () => void }) => {
  const [url, setUrl] = useState(team.ical_url || '');
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');

  const saveUrl = async () => {
    setSaving(true); setError(''); setResult('');
    try {
      await api.put(`/teams/${team.id}/ical`, { ical_url: url.trim() });
      onSaved();
      if (!url.trim()) { onClose(); return; }
      setResult('✓ URL saved — games sync automatically every hour');
      setTimeout(() => setResult(''), 5000);
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to save URL'); }
    finally { setSaving(false); }
  };

  const syncNow = async () => {
    setSyncing(true); setResult(''); setError('');
    try {
      const r = await api.post(`/teams/${team.id}/sync-ical`);
      const { imported, updated, deleted, skipped } = r.data;
      const parts = [];
      if (imported > 0) parts.push(`+${imported} added`);
      if (updated  > 0) parts.push(`${updated} updated`);
      if (deleted  > 0) parts.push(`${deleted} removed`);
      if (parts.length === 0) parts.push(`up to date (${skipped} unchanged)`);
      setResult(`✓ ${parts.join(', ')}`);
      onSaved();
      setTimeout(() => setResult(''), 5000);
    } catch (err: any) { setError(err.response?.data?.error || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📅 iCal Schedule — {team.name}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {team.ical_url && (
          <div style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px' }}>
            <div style={{ color: '#F97316', fontWeight: 700, marginBottom: '4px' }}>📅 Auto-sync active</div>
            <div style={{ color: 'var(--ink-3)', wordBreak: 'break-all' }}>{team.ical_url}</div>
            {team.ical_last_synced_at && (
              <div style={{ color: 'var(--ink-4)', fontSize: '12px', marginTop: '4px' }}>
                Last synced: {new Date(team.ical_last_synced_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </div>
            )}
          </div>
        )}

        <div className="form-group">
          <label>iCalendar URL</label>
          <input
            className="form-control"
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://… or webcal://…"
          />
          <div style={{ fontSize: '12px', color: 'var(--ink-4)', marginTop: '6px' }}>
            Paste the .ics subscription link from TeamSnap, GameChanger, Stack TeamApp, or any calendar app. Games will sync automatically every hour. Clear the field and save to remove auto-sync.
          </div>
        </div>

        {(result || error) && (
          <div style={{ fontSize: '13px', marginBottom: '12px', color: result ? '#22C55E' : '#F87171' }}>
            {result || error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          {team.ical_url && (
            <button type="button" className="btn btn-secondary" onClick={syncNow} disabled={syncing}>
              {syncing ? '⟳ Syncing…' : '🔄 Sync Now'}
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={saveUrl} disabled={saving}>
            {saving ? 'Saving…' : url.trim() ? 'Save URL' : 'Remove iCal'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Season Rollover Modal ────────────────────────────────────────────────────
// Starts a new season for a team: creates a NEW team record and copies the
// roster + staff forward. The current season becomes a read-only archive —
// its games, stats, videos and coach feedback are never touched.
const RolloverModal = ({ team, onClose }: { team: Team; onClose: () => void }) => {
  const [roster, setRoster]   = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [seasonYear,  setSeasonYear]  = useState(String((team.season_year || new Date().getFullYear()) + 1));
  const [seasonLabel, setSeasonLabel] = useState('');
  const [teamName,    setTeamName]    = useState(team.name);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [numbers,  setNumbers]  = useState<Record<string, string>>({});
  const [carryStaff,      setCarryStaff]      = useState(true);
  const [relinkParents,   setRelinkParents]   = useState(true);
  const [carrySchedule,   setCarrySchedule]   = useState(false);
  const [archivePrevious, setArchivePrevious] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]     = useState('');
  const [result, setResult]   = useState<any>(null);

  useEffect(() => {
    api.get(`/players?team_id=${team.id}`)
      .then(r => {
        const players: Player[] = r.data || [];
        setRoster(players);
        setSelected(Object.fromEntries(players.map(p => [p.id, true])));
        setNumbers(Object.fromEntries(players.map(p => [p.id, p.number != null ? String(p.number) : ''])));
      })
      .catch(() => setError('Could not load the roster.'))
      .finally(() => setLoading(false));
  }, [team.id]);

  const selectedIds = roster.filter(p => selected[p.id]).map(p => p.id);

  const submit = async () => {
    setSubmitting(true); setError('');
    try {
      const overrides: Record<string, any> = {};
      roster.filter(p => selected[p.id]).forEach(p => {
        const typed = numbers[p.id];
        const original = p.number != null ? String(p.number) : '';
        if (typed !== original) overrides[p.id] = { number: typed === '' ? null : parseInt(typed, 10) };
      });
      const r = await api.post(`/teams/${team.id}/rollover`, {
        season_year:  parseInt(seasonYear, 10),
        season_label: seasonLabel.trim() || null,
        name: teamName.trim() !== team.name ? teamName.trim() : undefined,
        player_ids: selectedIds,
        player_overrides: overrides,
        carry_staff:           carryStaff,
        relink_parents:        relinkParents,
        carry_schedule_config: carrySchedule,
        archive_previous:      archivePrevious,
      });
      setResult(r.data);
    } catch (err: any) {
      const data = err.response?.data;
      setError(data?.existing_team
        ? `${data.error} (${data.existing_team.season_label || data.existing_team.season_year})`
        : (data?.error || 'Season rollover failed.'));
    } finally {
      setSubmitting(false);
    }
  };

  const checkbox = (checked: boolean, onChange: (v: boolean) => void, label: string, hint: string) => (
    <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer', padding: '8px 0' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={{ marginTop: '3px' }} />
      <span>
        <span style={{ fontWeight: 700, fontSize: '13px' }}>{label}</span>
        <span style={{ display: 'block', fontSize: '12px', color: 'var(--ink-3)' }}>{hint}</span>
      </span>
    </label>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '560px' }}>
        <div className="modal-header">
          <h2>🔁 New Season — {team.name}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {result ? (
          <>
            <div style={{ padding: '16px', background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', borderRadius: '10px', fontSize: '13px', lineHeight: 1.7 }}>
              <div style={{ fontWeight: 800, fontSize: '15px', marginBottom: '8px' }}>
                {result.team.name} — {result.team.season_label || result.team.season_year} is ready.
              </div>
              <div>{result.players_copied} player{result.players_copied === 1 ? '' : 's'} carried over</div>
              <div>{result.staff_copied} coach/manager/access assignment{result.staff_copied === 1 ? '' : 's'} copied</div>
              <div>{result.parents_relinked} parent account{result.parents_relinked === 1 ? '' : 's'} now point at the new season</div>
              <div style={{ marginTop: '8px', color: 'var(--ink-3)' }}>
                {result.previous_archived
                  ? `${team.season_label || team.season_year} is archived — its games, stats, highlights and coach feedback are unchanged.`
                  : `${team.season_label || team.season_year} is still listed as an active season.`}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
              <a className="btn btn-primary" href={`/teams/${result.team.id}`} style={{ textDecoration: 'none' }}>Open new season</a>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: '13px', color: 'var(--ink-3)', marginBottom: '16px', lineHeight: 1.6 }}>
              This creates a <strong>new team record</strong> for the new season with a fresh 0-0-0 record and no games.
              The {team.season_label || team.season_year} season keeps every game, stat, highlight and coach post exactly as it is.
            </div>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
              <div style={{ flex: '0 0 110px' }}>
                <label className="form-label">Season year</label>
                <input className="form-control" value={seasonYear} onChange={e => setSeasonYear(e.target.value)} inputMode="numeric" />
              </div>
              <div style={{ flex: 1 }}>
                <label className="form-label">Season label (optional)</label>
                <input className="form-control" value={seasonLabel} onChange={e => setSeasonLabel(e.target.value)} placeholder="Fall 2026 / 2026-27" />
              </div>
            </div>

            <div style={{ marginBottom: '18px' }}>
              <label className="form-label">Team name</label>
              <input className="form-control" value={teamName} onChange={e => setTeamName(e.target.value)} />
            </div>

            <div className="section-label" style={{ marginBottom: '8px' }}>
              Roster — who's coming back? ({selectedIds.length} of {roster.length})
            </div>
            {loading ? (
              <div style={{ color: 'var(--ink-4)', fontSize: '13px' }}>Loading roster…</div>
            ) : roster.length === 0 ? (
              <div style={{ color: 'var(--ink-4)', fontSize: '13px' }}>No players on this roster.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '260px', overflowY: 'auto', marginBottom: '6px' }}>
                {roster.map(p => (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', background: 'var(--surface-sunken)', borderRadius: '8px', opacity: selected[p.id] ? 1 : 0.45 }}>
                    <input
                      type="checkbox"
                      checked={!!selected[p.id]}
                      onChange={e => setSelected(s => ({ ...s, [p.id]: e.target.checked }))}
                    />
                    <div style={{ flex: 1, fontSize: '13px', fontWeight: 700 }}>{p.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--ink-3)' }}>{p.position || '—'}</div>
                    <input
                      className="form-control"
                      value={numbers[p.id] ?? ''}
                      onChange={e => setNumbers(n => ({ ...n, [p.id]: e.target.value }))}
                      disabled={!selected[p.id]}
                      inputMode="numeric"
                      title="Jersey number for the new season"
                      style={{ width: '58px', padding: '4px 8px', fontSize: '12px', textAlign: 'center' }}
                    />
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setSelected(Object.fromEntries(roster.map(p => [p.id, true])))}>Select all</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setSelected({})}>Clear all</button>
            </div>

            <div className="section-label" style={{ marginBottom: '4px' }}>Carry over</div>
            {checkbox(carryStaff, setCarryStaff, 'Coaches, managers & scoped users',
              'Copies coach, team-manager and team-access assignments to the new season.')}
            {checkbox(relinkParents, setRelinkParents, 'Parent/player logins follow their kid',
              'Linked accounts point at the new season. Last season’s coach feedback stays on the archived roster.')}
            {checkbox(carrySchedule, setCarrySchedule, 'Schedule & league settings',
              'iCal feed URL, league link and league assignment. Usually change each season — off by default.')}
            {checkbox(archivePrevious, setArchivePrevious, `Archive the ${team.season_label || team.season_year} season`,
              'Hides it from team lists; still reachable from the season switcher on the team page.')}

            <div style={{ fontSize: '12px', color: 'var(--ink-3)', marginTop: '14px', padding: '10px 12px', background: 'var(--surface-sunken)', borderRadius: '8px', lineHeight: 1.6 }}>
              <strong>Not carried over:</strong> games, player stats, videos and highlights, coach feedback,
              tournaments, sponsors, and any manual win/loss record override. They stay with the {team.season_label || team.season_year} season.
            </div>

            {error && <div style={{ color: '#F87171', fontSize: '13px', marginTop: '12px' }}>{error}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={submit} disabled={submitting || loading || !seasonYear.trim()}>
                {submitting ? 'Rolling over…' : `Start ${seasonLabel.trim() || seasonYear} season`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Teams & Players Panel ────────────────────────────────────────────────────
const TeamsPanel = ({ games, onModalOpen }: { games: Game[]; onModalOpen: (type: ModalType, data?: any) => void }) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [players, setPlayers] = useState<Record<string, Player[]>>({});
  const [expanded, setExpanded] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<Record<string, boolean>>({});
  const [syncResult, setSyncResult] = useState<Record<string, string>>({});
  const [coachesTeam, setCoachesTeam] = useState<Team | null>(null);
  const [icalTeam, setIcalTeam] = useState<Team | null>(null);
  const [rolloverTeam, setRolloverTeam] = useState<Team | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  // Admin is the archive-management surface, so it asks for past seasons too;
  // they're hidden behind the toggle below until wanted.
  const fetchTeams = useCallback(async () => {
    const r = await api.get('/teams?includeArchived=1');
    setTeams(r.data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchTeams(); }, [fetchTeams]);

  const fetchPlayers = async (teamId: string) => {
    const r = await api.get(`/players?team_id=${teamId}`);
    setPlayers(p => ({ ...p, [teamId]: r.data }));
  };

  const toggleTeam = (teamId: string) => {
    if (expanded === teamId) { setExpanded(null); return; }
    setExpanded(teamId);
    fetchPlayers(teamId);
  };

  const deleteTeam = async (team: Team) => {
    if (!window.confirm(`Delete team "${team.name}"? All players will also be removed.`)) return;
    await api.delete(`/teams/${team.id}`);
    setTeams(ts => ts.filter(t => t.id !== team.id));
  };

  const deletePlayer = async (playerId: string, teamId: string) => {
    if (!window.confirm('Remove this player?')) return;
    await api.delete(`/players/${playerId}`);
    setPlayers(p => ({ ...p, [teamId]: p[teamId].filter(pl => pl.id !== playerId) }));
  };

  const refreshTeamPlayers = (teamId: string) => fetchPlayers(teamId);

  const syncNow = async (team: Team) => {
    setSyncing(s => ({ ...s, [team.id]: true }));
    setSyncResult(r => ({ ...r, [team.id]: '' }));
    try {
      const r = await api.post(`/teams/${team.id}/sync-ical`);
      const { imported, updated, deleted, skipped } = r.data;
      const parts: string[] = [];
      if (imported > 0) parts.push(`+${imported} added`);
      if (updated  > 0) parts.push(`${updated} updated`);
      if (deleted  > 0) parts.push(`${deleted} removed`);
      if (parts.length === 0) parts.push(`up to date`);
      setSyncResult(prev => ({ ...prev, [team.id]: `✓ ${parts.join(', ')}` }));
      if (imported > 0) fetchTeams();
      setTimeout(() => setSyncResult(prev => ({ ...prev, [team.id]: '' })), 4000);
    } catch (err: any) {
      setSyncResult(prev => ({ ...prev, [team.id]: `✗ ${err.response?.data?.error || 'Sync failed'}` }));
      setTimeout(() => setSyncResult(prev => ({ ...prev, [team.id]: '' })), 5000);
    } finally {
      setSyncing(s => ({ ...s, [team.id]: false }));
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '40px', color: 'var(--ink-4)' }}>Loading teams...</div>;

  const handleModalOpen = (type: ModalType, data?: any) => {
    onModalOpen(type, { ...data, _onSave: () => { fetchTeams(); if (data?.team) refreshTeamPlayers(data.team.id); } });
  };

  const archivedCount = teams.filter(t => t.archived).length;
  const visibleTeams  = showArchived ? teams : teams.filter(t => !t.archived);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        {archivedCount > 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--ink-3)', cursor: 'pointer' }}>
            <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
            Show past seasons ({archivedCount})
          </label>
        )}
        <button className="btn btn-primary" onClick={() => onModalOpen('create_team')}>+ New Team</button>
      </div>
      {visibleTeams.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '16px' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏆</div>
          <h3 style={{ fontSize: '18px', fontWeight: 800, marginBottom: '8px' }}>No teams yet</h3>
          <button className="btn btn-primary" style={{ marginTop: '8px' }} onClick={() => onModalOpen('create_team')}>Create Team</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {visibleTeams.map(team => (
            <div key={team.id} style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '12px', overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: team.primary_color, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800, fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {team.name}
                    {!!team.archived && (
                      <span style={{ fontSize: '10px', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--ink-3)', background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', borderRadius: '999px', padding: '2px 8px' }}>
                        Past season
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--ink-3)' }}>
                    {team.sport} · {team.season_label || team.season_year}
                    {team.ical_url && (
                      <span style={{ marginLeft: '8px', color: '#F97316' }}>
                        📅 auto-sync
                        {team.ical_last_synced_at && (
                          <span style={{ color: 'var(--ink-4)', marginLeft: '4px' }}>
                            · last synced {new Date(team.ical_last_synced_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                  {syncResult[team.id] && (
                    <div style={{ fontSize: '11px', marginTop: '3px', color: syncResult[team.id].startsWith('✓') ? '#22C55E' : '#F87171' }}>
                      {syncResult[team.id]}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => toggleTeam(team.id)}>
                    {expanded === team.id ? '▲ Roster' : '▼ Roster'}
                  </button>
                  {team.ical_url && (
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => syncNow(team)}
                      disabled={syncing[team.id]}
                      title="Fetch latest games from the saved iCal feed"
                    >
                      {syncing[team.id] ? '⟳ Syncing…' : '🔄 Sync'}
                    </button>
                  )}
                  <button
                    className="btn btn-sm"
                    style={{ background: 'rgba(249,115,22,0.1)', color: '#F97316', border: '1px solid rgba(249,115,22,0.3)' }}
                    onClick={() => setIcalTeam(team)}
                    title="Set or update iCalendar schedule URL"
                  >
                    📅 iCal
                  </button>
                  <button
                    className="btn btn-sm"
                    style={{ background: 'rgba(6,182,212,0.1)', color: '#22D3EE', border: '1px solid rgba(6,182,212,0.25)' }}
                    onClick={() => setCoachesTeam(team)}
                  >
                    👥 Coaches
                  </button>
                  {!team.archived && (
                    <button
                      className="btn btn-sm"
                      style={{ background: 'rgba(13,79,76,0.1)', color: 'var(--sr-teal, #0D4F4C)', border: '1px solid rgba(13,79,76,0.3)' }}
                      onClick={() => setRolloverTeam(team)}
                      title="Start a new season — new record and stats, this season archived"
                    >
                      🔁 New Season
                    </button>
                  )}
                  <button className="btn btn-secondary btn-sm" onClick={() => onModalOpen('edit_team', { team })}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => deleteTeam(team)}>Delete</button>
                </div>
              </div>

              {expanded === team.id && (
                <div style={{ borderTop: '1px solid var(--hairline)', padding: '12px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div className="section-label">Roster</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => onModalOpen('roster_scan', { team })}>📷 Scan</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => onModalOpen('create_player', { team })}>+ Add Player</button>
                    </div>
                  </div>
                  {!players[team.id] ? (
                    <div style={{ color: 'var(--ink-4)', fontSize: '13px' }}>Loading...</div>
                  ) : players[team.id].length === 0 ? (
                    <div style={{ color: 'var(--ink-4)', fontSize: '13px' }}>No players yet.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {players[team.id].map(player => (
                        <div key={player.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: 'var(--surface-sunken)', borderRadius: '8px' }}>
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: team.primary_color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 900, color: '#fff', flexShrink: 0, overflow: 'hidden' }}>
                            {player.photo_url
                              ? <img src={mediaUrl(player.photo_url)} alt={player.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              : `#${player.number ?? '?'}`}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, fontSize: '13px' }}>{player.name}</div>
                            <div style={{ fontSize: '11px', color: 'var(--ink-3)' }}>{player.position || '—'}{player.grade ? ` · ${player.grade}` : ''}</div>
                          </div>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => onModalOpen('player_stats', { player, sport: team.sport })}>Stats</button>
                            <button className="btn btn-secondary btn-sm" onClick={() => onModalOpen('edit_player', { team, player })}>Edit</button>
                            <button className="btn btn-danger btn-sm" onClick={() => deletePlayer(player.id, team.id)}>✕</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {rolloverTeam && (
        <RolloverModal
          team={rolloverTeam}
          onClose={() => { setRolloverTeam(null); fetchTeams(); }}
        />
      )}
      {coachesTeam && <CoachesModal team={coachesTeam} onClose={() => setCoachesTeam(null)} />}
      {icalTeam && (
        <IcalModal
          team={icalTeam}
          onClose={() => setIcalTeam(null)}
          onSaved={() => fetchTeams()}
        />
      )}
    </div>
  );
};

// ─── Trim Video Modal ─────────────────────────────────────────────────────────
const TrimVideoModal = ({
  video,
  videoUrl,
  onClose,
  onSaved,
}: {
  video: GameVideo;
  videoUrl: string;
  onClose: () => void;
  onSaved: (updated: GameVideo) => void;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [startTime, setStartTime] = useState('00:00:00');
  const [endTime,   setEndTime]   = useState('');
  const [trimming,  setTrimming]  = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error,     setError]     = useState('');

  // Format seconds → HH:MM:SS
  const fmtTime = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = Math.floor(s % 60);
    return [h, m, sec].map(v => String(v).padStart(2, '0')).join(':');
  };

  // Parse HH:MM:SS or MM:SS → seconds
  const parseTime = (str: string): number | null => {
    const parts = str.trim().split(':').map(Number);
    if (parts.some(isNaN)) return null;
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    if (parts.length === 1) return parts[0];
    return null;
  };

  const onVideoLoaded = () => {
    if (videoRef.current) setEndTime(fmtTime(videoRef.current.duration));
  };

  const captureStart = () => {
    if (videoRef.current) setStartTime(fmtTime(videoRef.current.currentTime));
  };

  const captureEnd = () => {
    if (videoRef.current) setEndTime(fmtTime(videoRef.current.currentTime));
  };

  const seekTo = (timeStr: string) => {
    const s = parseTime(timeStr);
    if (s !== null && videoRef.current) videoRef.current.currentTime = s;
  };

  const applyTrim = async () => {
    const start = parseTime(startTime);
    const end   = parseTime(endTime);
    if (start === null || end === null) { setError('Invalid time format. Use HH:MM:SS.'); return; }
    if (end <= start) { setError('End time must be after start time.'); return; }
    setError(''); setTrimming(true);
    try {
      const res = await api.post(`/videos/${video.id}/trim`, { start_seconds: start, end_seconds: end });
      onSaved(res.data.video);
      onClose();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Trim failed. Please try again.');
    } finally {
      setTrimming(false);
    }
  };

  const restoreOriginal = async () => {
    if (!window.confirm('Restore the original uncut video? The current trim will be discarded.')) return;
    setError(''); setRestoring(true);
    try {
      const res = await api.post(`/videos/${video.id}/trim/restore`);
      onSaved(res.data.video);
      onClose();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Restore failed.');
    } finally {
      setRestoring(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', borderRadius: '6px',
    color: 'var(--ink-1)', padding: '6px 10px', fontSize: '14px', width: '110px',
    fontFamily: 'monospace',
  };
  const btnStyle = (color: string): React.CSSProperties => ({
    background: color, border: 'none', borderRadius: '6px', color: '#fff',
    padding: '5px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: 'var(--surface)', borderRadius: '16px', border: '1px solid var(--hairline)', width: '100%', maxWidth: '820px', maxHeight: '90vh', overflowY: 'auto', padding: '28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800 }}>✂️ Trim Video</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--ink-3)', fontSize: '24px', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <p style={{ color: 'var(--ink-3)', fontSize: '13px', marginBottom: '16px' }}>
          Play the video and use the buttons below to set trim start and end points. The pregame footage will be removed.
          {video.original_file_path && <span style={{ color: '#F97316', marginLeft: '8px' }}>⚠️ This video has already been trimmed.</span>}
        </p>

        {/* Video player */}
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          playsInline
          onLoadedMetadata={onVideoLoaded}
          style={{ width: '100%', borderRadius: '8px', background: '#000', marginBottom: '20px', maxHeight: '380px' }}
        />

        {/* Trim controls */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
          {/* Start */}
          <div style={{ background: 'var(--surface)', borderRadius: '10px', padding: '14px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--ink-3)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Start Time</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input value={startTime} onChange={e => setStartTime(e.target.value)} style={inputStyle} placeholder="00:00:00" />
              <button onClick={captureStart} style={btnStyle('#2D3748')} title="Set to current playback position">📍 Mark</button>
              <button onClick={() => seekTo(startTime)} style={btnStyle('#2D3748')} title="Jump video to this time">▶ Preview</button>
            </div>
          </div>
          {/* End */}
          <div style={{ background: 'var(--surface)', borderRadius: '10px', padding: '14px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--ink-3)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>End Time</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input value={endTime} onChange={e => setEndTime(e.target.value)} style={inputStyle} placeholder="01:17:00" />
              <button onClick={captureEnd} style={btnStyle('#2D3748')} title="Set to current playback position">📍 Mark</button>
              <button onClick={() => seekTo(endTime)} style={btnStyle('#2D3748')} title="Jump video to this time">▶ Preview</button>
            </div>
          </div>
        </div>

        {error && <div style={{ color: '#FC8181', fontSize: '13px', marginBottom: '14px', background: 'rgba(252,129,129,0.1)', padding: '10px 14px', borderRadius: '8px' }}>{error}</div>}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {video.original_file_path && (
            <button onClick={restoreOriginal} disabled={restoring} style={{ ...btnStyle('var(--ink-4)'), opacity: restoring ? 0.6 : 1 }}>
              {restoring ? 'Restoring…' : '↩ Restore Original'}
            </button>
          )}
          <button onClick={onClose} style={btnStyle('#2D3748')}>Cancel</button>
          <button onClick={applyTrim} disabled={trimming} style={{ ...btnStyle('#F97316'), opacity: trimming ? 0.6 : 1, padding: '8px 20px', fontSize: '14px' }}>
            {trimming ? '✂️ Trimming…' : '✂️ Apply Trim'}
          </button>
        </div>
        <p style={{ color: 'var(--ink-4)', fontSize: '11px', marginTop: '12px' }}>
          Trimming uses fast copy (no re-encoding) and takes only a few seconds. The AI analysis will need to be re-run after trimming.
        </p>
      </div>
    </div>
  );
};

// ─── Videos Panel ─────────────────────────────────────────────────────────────
// ─── Analyze Video Modal ──────────────────────────────────────────────────────
const AnalyzeModal = ({ video, onClose, onStarted }: {
  video: GameVideo;
  onClose: () => void;
  onStarted: (updated: GameVideo) => void;
}) => {
  const [ourTeamColor,  setOurTeamColor]  = useState(video.our_team_color  || '');
  const [opponentColor, setOpponentColor] = useState(video.opponent_color || '');
  const [matchFocus,    setMatchFocus]    = useState(video.match_focus    || '');
  const [submitting,    setSubmitting]    = useState(false);
  const [error,         setError]         = useState('');

  const handleStart = async () => {
    setSubmitting(true); setError('');
    try {
      const res = await api.post(`/videos/${video.id}/analyze`, {
        our_team_color: ourTeamColor || undefined,
        opponent_color: opponentColor || undefined,
        match_focus:    matchFocus    || undefined,
      });
      onStarted(res.data);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start analysis');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="modal-overlay" onClick={submitting ? undefined : onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🤖 AI Analysis</h2>
          <button className="modal-close" onClick={onClose} disabled={submitting}>✕</button>
        </div>

        <p style={{ fontSize: '13px', color: 'var(--ink-3)', marginBottom: '16px', lineHeight: '1.5' }}>
          Gemini will analyse this video using the Welsh Way coaching framework. Provide jersey colors so it can correctly identify your team.
        </p>

        <div style={{ display: 'flex', gap: '10px' }}>
          <div className="form-group" style={{ flex: 1, marginBottom: '12px' }}>
            <label style={{ fontSize: '12px' }}>Our Team Jersey Color</label>
            <input className="form-control" value={ourTeamColor} onChange={e => setOurTeamColor(e.target.value)}
              placeholder="e.g. Blue" />
          </div>
          <div className="form-group" style={{ flex: 1, marginBottom: '12px' }}>
            <label style={{ fontSize: '12px' }}>Opponent Jersey Color</label>
            <input className="form-control" value={opponentColor} onChange={e => setOpponentColor(e.target.value)}
              placeholder="e.g. White" />
          </div>
        </div>

        <div className="form-group">
          <label style={{ fontSize: '12px' }}>Coach's Match Focus (optional)</label>
          <textarea className="form-control" value={matchFocus} onChange={e => setMatchFocus(e.target.value)}
            rows={2} placeholder="e.g. Breaking lines through midfield and pressing high in the opponent's half" />
        </div>

        {error && <div style={{ color: '#F87171', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="btn btn-primary" onClick={handleStart} disabled={submitting}>
            {submitting ? 'Starting...' : '🤖 Start Analysis'}
          </button>
        </div>
      </div>
    </div>
  );
};

const VideosPanel = ({ games, players, onModalOpen }: { games: Game[]; players: Player[]; onModalOpen: (type: ModalType, data?: any) => void }) => {
  const [videos,       setVideos]       = useState<GameVideo[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [trimVideo,    setTrimVideo]    = useState<GameVideo | null>(null);
  const [analyzeVideo, setAnalyzeVideo] = useState<GameVideo | null>(null);

  useEffect(() => {
    api.get('/videos').then(r => setVideos(r.data)).finally(() => setLoading(false));
  }, []);

  const deleteVideo = async (v: GameVideo) => {
    if (!window.confirm(`Delete "${v.title || 'this video'}"?`)) return;
    await api.delete(`/videos/${v.id}`);
    setVideos(vs => vs.filter(vid => vid.id !== v.id));
  };

  const getVideoUrl = (v: GameVideo) =>
    mediaUrl(
      v.file_path.startsWith('http') || v.file_path.startsWith('/api/') ? v.file_path
      : v.file_path.startsWith('/uploads/') ? `${API_URL}${v.file_path}`
      : `${HLS_BASE}${v.file_path}`
    );

  const getGameTitle  = (gameId?: string)   => games.find(g => g.id === gameId)?.title || 'Unknown game';
  const getPlayerName = (playerId?: string) => players.find(p => p.id === playerId)?.name || '';

  const onTrimSaved = (updated: GameVideo) =>
    setVideos(vs => vs.map(v => v.id === updated.id ? updated : v));

  const onAnalysisStarted = (updated: GameVideo) =>
    setVideos(vs => vs.map(v => v.id === updated.id ? updated : v));

  // Only local RTMP recordings can be trimmed (not Wasabi proxied files)
  const canTrim = (v: GameVideo) =>
    v.video_type === 'recording' && !v.file_path.startsWith('/api/videos/proxy/');

  if (loading) return <div style={{ textAlign: 'center', padding: '40px', color: 'var(--ink-4)' }}>Loading videos...</div>;

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <button className="btn btn-secondary" onClick={() => onModalOpen('upload_recording')}>🎬 Upload Recording</button>
        <button className="btn btn-primary"   onClick={() => onModalOpen('upload_highlight')}>⭐ Upload Highlight</button>
      </div>
      {videos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '16px' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎬</div>
          <h3 style={{ fontSize: '18px', fontWeight: 800, marginBottom: '8px' }}>No videos yet</h3>
          <p style={{ color: 'var(--ink-3)', fontSize: '14px' }}>Upload game recordings or highlight clips. Game recordings will also auto-appear here after a live stream ends.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {videos.map(v => (
            <div key={v.id} style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '10px', padding: '14px 18px' }}>
              {/* Top row: icon + info */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '10px' }}>
                <div style={{ fontSize: '22px', lineHeight: 1, marginTop: '2px' }}>{v.video_type === 'recording' ? '🎬' : '⭐'}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {v.title || 'Untitled'}
                    {v.original_file_path && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#F97316', fontWeight: 600 }}>✂️ Trimmed</span>}
                    {v.analysis_status === 'processing'  && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#FBBF24', fontWeight: 600 }}>🤖 Analysing…</span>}
                    {v.analysis_status === 'completed'   && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#22C55E', fontWeight: 600 }}>✅ Analysis ready</span>}
                    {v.analysis_status === 'failed'      && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#F87171', fontWeight: 600 }}>⚠️ Analysis failed</span>}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--ink-3)' }}>
                    {v.video_type === 'recording' ? 'Game Recording' : 'Highlight'}
                    {v.game_id   ? ` · ${getGameTitle(v.game_id)}` : ''}
                    {v.player_id ? ` · ${getPlayerName(v.player_id)}` : ''}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--ink-4)', marginTop: '2px' }}>
                    {new Date(v.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    {v.file_size_bytes ? ` · ${(v.file_size_bytes / 1024 / 1024).toFixed(0)} MB` : ''}
                  </div>
                </div>
              </div>
              {/* Bottom row: buttons always on their own line, no wrapping fights */}
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <a href={getVideoUrl(v)} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">▶ Play</a>
                {canTrim(v) && (
                  <button className="btn btn-secondary btn-sm" onClick={() => setTrimVideo(v)}>✂️ Trim</button>
                )}
                {v.video_type === 'recording' && v.analysis_status !== 'processing' && (
                  <button className="btn btn-secondary btn-sm" onClick={() => setAnalyzeVideo(v)}>🤖 Analyse</button>
                )}
                <button className="btn btn-danger btn-sm" onClick={() => deleteVideo(v)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {trimVideo && (
        <TrimVideoModal
          video={trimVideo}
          videoUrl={getVideoUrl(trimVideo)}
          onClose={() => setTrimVideo(null)}
          onSaved={onTrimSaved}
        />
      )}

      {analyzeVideo && (
        <AnalyzeModal
          video={analyzeVideo}
          onClose={() => setAnalyzeVideo(null)}
          onStarted={onAnalysisStarted}
        />
      )}
    </div>
  );
};

// ─── Assign Teams Modal ───────────────────────────────────────────────────────
const AssignTeamsModal = ({
  user, allTeams, onClose, onChanged,
}: {
  user: any; allTeams: Team[]; onClose: () => void; onChanged: () => void;
}) => {
  const [assignedIds, setAssignedIds] = useState<string[]>(
    (user.coach_teams || []).map((t: any) => t.id)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggle = (teamId: string) =>
    setAssignedIds(prev => prev.includes(teamId) ? prev.filter(id => id !== teamId) : [...prev, teamId]);

  const save = async () => {
    setSaving(true); setError('');
    const currentIds = new Set(assignedIds);
    const originalIds = new Set<string>((user.coach_teams || []).map((t: any) => t.id));
    try {
      for (const id of Array.from(currentIds)) {
        if (!originalIds.has(id)) await api.post(`/teams/${id}/coaches`, { userId: user.id });
      }
      for (const id of Array.from(originalIds)) {
        if (!currentIds.has(id)) await api.delete(`/teams/${id}/coaches/${user.id}`);
      }
      onChanged();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update assignments');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '460px' }}>
        <div className="modal-header">
          <h2>🏆 Team Assignments — {user.username}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <p style={{ color: 'var(--ink-3)', fontSize: '13px', marginBottom: '16px' }}>
          Check the teams this coach can post feedback to. Coaches can only see and post to players on their assigned teams.
        </p>

        {allTeams.length === 0 ? (
          <div style={{ color: 'var(--ink-4)', fontSize: '13px', padding: '12px', background: 'var(--surface-sunken)', borderRadius: '8px', border: '1px solid var(--hairline)' }}>
            No teams exist yet. Create a team first in the Teams &amp; Players tab.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
            {allTeams.map(team => {
              const assigned = assignedIds.includes(team.id);
              return (
                <div
                  key={team.id}
                  onClick={() => toggle(team.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 14px', borderRadius: '8px', cursor: 'pointer',
                    border: assigned ? `2px solid ${team.primary_color}` : '2px solid var(--hairline)',
                    background: assigned ? `${team.primary_color}12` : 'var(--surface-sunken)',
                    transition: 'all 0.15s', userSelect: 'none',
                  }}
                >
                  <div style={{ width: 12, height: 12, borderRadius: '50%', background: team.primary_color, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: assigned ? 'var(--ink-1)' : 'var(--ink-3)' }}>{team.name}</div>
                    <div style={{ fontSize: '11px', color: 'var(--ink-4)' }}>{team.sport} · {team.season_year}</div>
                  </div>
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                    background: assigned ? team.primary_color : 'var(--hairline)',
                    border: `1px solid ${assigned ? team.primary_color : 'var(--hairline)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '11px', fontWeight: 900, color: '#fff',
                  }}>
                    {assigned ? '✓' : ''}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {error && <div style={{ color: '#F87171', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving || allTeams.length === 0}>
            {saving ? 'Saving…' : 'Save Assignments'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Manage User Teams Modal ──────────────────────────────────────────────────
// Replaces all team access entries for a user via PUT /auth/users/:id/teams
const ManageUserTeamsModal = ({
  user, allTeams, onClose, onChanged,
}: {
  user: any; allTeams: Team[]; onClose: () => void; onChanged: () => void;
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(user.team_ids || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggle = (teamId: string) =>
    setSelectedIds(prev => prev.includes(teamId) ? prev.filter(id => id !== teamId) : [...prev, teamId]);

  const save = async () => {
    setSaving(true); setError('');
    try {
      await api.put(`/auth/users/${user.id}/teams`, { team_ids: selectedIds });
      onChanged();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update team access');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '460px' }}>
        <div className="modal-header">
          <h2>🔑 Team Access — {user.username}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <p style={{ color: 'var(--ink-3)', fontSize: '13px', marginBottom: '16px' }}>
          Select which teams this user can access. Leave all unchecked to grant full access to every team.
        </p>

        {allTeams.length === 0 ? (
          <div style={{ color: 'var(--ink-4)', fontSize: '13px', padding: '12px', background: 'var(--surface-sunken)', borderRadius: '8px', border: '1px solid var(--hairline)' }}>
            No teams exist yet. Create a team first in the Teams &amp; Players tab.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
              {allTeams.map(team => {
                const assigned = selectedIds.includes(team.id);
                return (
                  <div
                    key={team.id}
                    onClick={() => toggle(team.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '12px 14px', borderRadius: '8px', cursor: 'pointer',
                      border: assigned ? '2px solid #F97316' : '2px solid var(--hairline)',
                      background: assigned ? 'rgba(249,115,22,0.08)' : 'var(--surface-sunken)',
                      transition: 'all 0.15s', userSelect: 'none',
                    }}
                  >
                    <div style={{ width: 12, height: 12, borderRadius: '50%', background: team.primary_color, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: '14px', color: assigned ? 'var(--ink-1)' : 'var(--ink-3)' }}>{team.name}</div>
                      <div style={{ fontSize: '11px', color: 'var(--ink-4)' }}>{team.sport} · {team.season_year}</div>
                    </div>
                    <div style={{
                      width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                      background: assigned ? '#F97316' : 'var(--hairline)',
                      border: `1px solid ${assigned ? '#F97316' : 'var(--hairline)'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '11px', fontWeight: 900, color: '#fff',
                    }}>
                      {assigned ? '✓' : ''}
                    </div>
                  </div>
                );
              })}
            </div>
            {selectedIds.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedIds([])}
                style={{ background: 'none', border: 'none', color: 'var(--ink-4)', cursor: 'pointer', fontSize: '12px', marginBottom: '12px' }}
              >
                ✕ Clear all (grant full access)
              </button>
            )}
          </>
        )}

        {error && <div style={{ color: '#F87171', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save Access'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Pending User Card ────────────────────────────────────────────────────────
const PendingUserCard = ({
  user, allPlayers, onApprove, onReject,
}: {
  user: any; allPlayers: Player[];
  onApprove: (userId: string, linkedPlayerId?: string) => void;
  onReject: (userId: string) => void;
}) => {
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const isPlayerParent = user.requested_role === 'player_parent';
  const canApprove = !isPlayerParent || selectedPlayerId !== '';
  const activePlayers = allPlayers.filter((p: any) => p.is_active !== false);

  return (
    <div style={{
      background: 'rgba(249,115,22,0.05)',
      border: '1px solid rgba(249,115,22,0.25)',
      borderRadius: '12px', padding: '16px 20px',
    }}>
      {/* Row 1: Avatar + Info */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <div style={{
          width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
          background: 'linear-gradient(135deg, #F97316, #EA580C)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '16px', fontWeight: 800, color: '#fff',
        }}>
          {user.username.charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 800, fontSize: '15px' }}>{user.username}</span>
            <span style={{
              fontSize: '10px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase',
              padding: '2px 8px', borderRadius: '20px',
              background: isPlayerParent ? 'rgba(139,92,246,0.12)' : 'rgba(6,182,212,0.12)',
              color: isPlayerParent ? '#A78BFA' : '#22D3EE',
              border: `1px solid ${isPlayerParent ? 'rgba(139,92,246,0.3)' : 'rgba(6,182,212,0.3)'}`,
            }}>
              {isPlayerParent ? 'Player/Parent' : 'Coach'}
            </span>
          </div>
          <div style={{ fontSize: '13px', color: 'var(--ink-3)', marginTop: '2px' }}>{user.email}</div>
          <div style={{ fontSize: '11px', color: 'var(--ink-4)', marginTop: '3px' }}>
            Registered {new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>

          {/* Player dropdown lives inside info column */}
          {isPlayerParent && (
            <div style={{ marginTop: '10px' }}>
              <label style={{
                fontSize: '11px', color: 'var(--ink-3)', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '5px',
              }}>
                Link to Player *
              </label>
              <select
                className="form-control"
                value={selectedPlayerId}
                onChange={e => setSelectedPlayerId(e.target.value)}
                style={{ fontSize: '13px', padding: '7px 10px', maxWidth: '320px', width: '100%' }}
              >
                <option value="">— Select player —</option>
                {activePlayers.map((p: any) => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.number ? ` #${p.number}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Row 2: Approve / Reject — always on their own row */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px', justifyContent: 'flex-end' }}>
        <button
          className="btn btn-sm"
          style={{
            background: canApprove ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.05)',
            color: canApprove ? '#22C55E' : 'var(--ink-4)',
            border: `1px solid ${canApprove ? 'rgba(34,197,94,0.3)' : 'var(--hairline)'}`,
            cursor: canApprove ? 'pointer' : 'not-allowed',
          }}
          disabled={!canApprove}
          onClick={() => onApprove(user.id, isPlayerParent ? selectedPlayerId : undefined)}
        >
          ✓ Approve
        </button>
        <button className="btn btn-danger btn-sm" onClick={() => onReject(user.id)}>
          ✕ Reject
        </button>
      </div>
    </div>
  );
};

// ─── Pending Manager Card ─────────────────────────────────────────────────────
const SPORTS = [
  { value: 'soccer',      label: '⚽ Soccer' },
  { value: 'baseball',    label: '⚾ Baseball' },
  { value: 'softball',    label: '🥎 Softball' },
  { value: 'basketball',  label: '🏀 Basketball' },
];

const PendingManagerCard = ({
  user, allTeams, onApprove, onReject, onCreateAndApprove,
}: {
  user: any; allTeams: Team[];
  onApprove: (userId: string, teamIds: string[]) => void;
  onReject: (userId: string) => void;
  onCreateAndApprove: (userId: string, teamName: string, sport: string) => Promise<void>;
}) => {
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [newTeamSport, setNewTeamSport] = useState<string>('soccer');
  const [creatingTeam, setCreatingTeam] = useState(false);

  const hasPendingTeam = !!user.pending_team_name;
  const canApproveExisting = selectedTeamIds.length > 0;

  const toggleTeam = (teamId: string) => {
    setSelectedTeamIds(prev =>
      prev.includes(teamId) ? prev.filter(id => id !== teamId) : [...prev, teamId]
    );
  };

  const handleCreateAndApprove = async () => {
    setCreatingTeam(true);
    try {
      await onCreateAndApprove(user.id, user.pending_team_name, newTeamSport);
    } finally {
      setCreatingTeam(false);
    }
  };

  return (
    <div style={{
      background: 'rgba(34,197,94,0.05)',
      border: '1px solid rgba(34,197,94,0.25)',
      borderRadius: '12px', padding: '16px 20px',
    }}>
      {/* Row 1: Avatar + Info (no buttons here) */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        {/* Avatar */}
        <div style={{
          width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
          background: 'linear-gradient(135deg, #22C55E, #16A34A)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '16px', fontWeight: 800, color: '#fff',
        }}>
          {user.username.charAt(0).toUpperCase()}
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: '160px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 800, fontSize: '15px' }}>{user.username}</span>
            <span style={{
              fontSize: '10px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase',
              padding: '2px 8px', borderRadius: '20px',
              background: 'rgba(34,197,94,0.12)', color: '#22C55E',
              border: '1px solid rgba(34,197,94,0.3)',
            }}>
              🗂️ Team Manager Request
            </span>
          </div>

          {/* Full name (shown when provided) */}
          {user.full_name && (
            <div style={{ fontSize: '13px', color: 'var(--ink-1)', marginTop: '2px', fontWeight: 600 }}>
              {user.full_name}
            </div>
          )}

          <div style={{ fontSize: '13px', color: 'var(--ink-3)', marginTop: user.full_name ? '1px' : '2px' }}>{user.email}</div>
          <div style={{ fontSize: '11px', color: 'var(--ink-4)', marginTop: '3px' }}>
            Registered {new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>

          {/* New team request banner */}
          {hasPendingTeam && (
            <div style={{
              marginTop: '12px',
              padding: '10px 14px',
              background: 'rgba(34,197,94,0.08)',
              border: '1px solid rgba(34,197,94,0.2)',
              borderRadius: '8px',
            }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#22C55E', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                🏗️ Wants to create a new team
              </div>
              <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--ink-1)' }}>
                {user.pending_team_name}
              </div>

              {/* Sport selector for new team */}
              <div style={{ marginTop: '10px' }}>
                <label style={{
                  fontSize: '11px', color: 'var(--ink-3)', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px',
                }}>
                  Sport *
                </label>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {SPORTS.map(s => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => setNewTeamSport(s.value)}
                      style={{
                        fontSize: '12px', fontWeight: 600, padding: '5px 12px', borderRadius: '20px', cursor: 'pointer',
                        border: `1px solid ${newTeamSport === s.value ? '#22C55E' : 'var(--hairline)'}`,
                        background: newTeamSport === s.value ? 'rgba(34,197,94,0.15)' : 'var(--surface-sunken)',
                        color: newTeamSport === s.value ? '#22C55E' : 'var(--ink-3)',
                        transition: 'all 0.12s',
                      }}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Create & Approve button */}
              <button
                className="btn btn-sm"
                style={{
                  marginTop: '10px',
                  background: creatingTeam ? 'rgba(34,197,94,0.05)' : 'rgba(34,197,94,0.15)',
                  color: creatingTeam ? 'var(--ink-4)' : '#22C55E',
                  border: `1px solid ${creatingTeam ? 'var(--hairline)' : 'rgba(34,197,94,0.4)'}`,
                  cursor: creatingTeam ? 'not-allowed' : 'pointer',
                  fontWeight: 700,
                }}
                disabled={creatingTeam}
                onClick={handleCreateAndApprove}
              >
                {creatingTeam ? '⏳ Creating…' : '🏗️ Create Team & Approve as Manager'}
              </button>
            </div>
          )}

          {/* Existing team multi-select */}
          <div style={{ marginTop: '12px' }}>
            <label style={{
              fontSize: '11px', color: 'var(--ink-3)', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px',
            }}>
              {hasPendingTeam ? 'Or assign to an existing team' : 'Assign to Teams *'}
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {allTeams.map((t: Team) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggleTeam(t.id)}
                  style={{
                    fontSize: '12px', fontWeight: 600, padding: '4px 10px', borderRadius: '20px', cursor: 'pointer',
                    border: `1px solid ${selectedTeamIds.includes(t.id) ? '#22C55E' : 'var(--hairline)'}`,
                    background: selectedTeamIds.includes(t.id) ? 'rgba(34,197,94,0.15)' : 'var(--surface-sunken)',
                    color: selectedTeamIds.includes(t.id) ? '#22C55E' : 'var(--ink-3)',
                    transition: 'all 0.12s',
                  }}
                >
                  {selectedTeamIds.includes(t.id) && '✓ '}{t.name}
                </button>
              ))}
              {allTeams.length === 0 && !hasPendingTeam && (
                <span style={{ fontSize: '12px', color: 'var(--ink-4)', fontStyle: 'italic' }}>No teams yet — create one first</span>
              )}
              {allTeams.length === 0 && hasPendingTeam && (
                <span style={{ fontSize: '12px', color: 'var(--ink-4)', fontStyle: 'italic' }}>No existing teams</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Approve / Reject — always on their own row */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px', justifyContent: 'flex-end' }}>
        {!hasPendingTeam && (
          <button
            className="btn btn-sm"
            style={{
              background: canApproveExisting ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.05)',
              color: canApproveExisting ? '#22C55E' : 'var(--ink-4)',
              border: `1px solid ${canApproveExisting ? 'rgba(34,197,94,0.3)' : 'var(--hairline)'}`,
              cursor: canApproveExisting ? 'pointer' : 'not-allowed',
            }}
            disabled={!canApproveExisting}
            onClick={() => onApprove(user.id, selectedTeamIds)}
          >
            ✓ Approve
          </button>
        )}
        {hasPendingTeam && canApproveExisting && (
          <button
            className="btn btn-sm"
            style={{
              background: 'rgba(34,197,94,0.12)',
              color: '#22C55E',
              border: '1px solid rgba(34,197,94,0.3)',
            }}
            onClick={() => onApprove(user.id, selectedTeamIds)}
          >
            ✓ Assign to Selected
          </button>
        )}
        <button className="btn btn-danger btn-sm" onClick={() => onReject(user.id)}>
          ✕ Reject
        </button>
      </div>
    </div>
  );
};

// ─── Leagues Panel ────────────────────────────────────────────────────────────

interface LeagueRow {
  id: string; name: string; sport: string; logo_url?: string;
  primary_color: string; secondary_color: string; description?: string;
  season_year: number; max_teams: number; status: string;
  website_url?: string; location?: string;
  contact_name?: string; contact_email?: string; notes?: string;
  team_count?: number; manager_count?: number; created_at: string;
}

const STATUS_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  active:    { bg: 'rgba(34,197,94,0.12)',   color: '#22C55E', border: 'rgba(34,197,94,0.3)' },
  trial:     { bg: 'rgba(249,115,22,0.12)',  color: '#F97316', border: 'rgba(249,115,22,0.3)' },
  suspended: { bg: 'rgba(239,68,68,0.10)',   color: '#F87171', border: 'rgba(239,68,68,0.25)' },
};

// ── LeagueModal ───────────────────────────────────────────────────────────────
const LeagueModal = ({ league, onClose, onSaved }: {
  league?: LeagueRow; onClose: () => void; onSaved: () => void;
}) => {
  const [form, setForm] = useState({
    name:            league?.name            || '',
    sport:           league?.sport           || 'soccer',
    description:     league?.description     || '',
    season_year:     String(league?.season_year  || new Date().getFullYear()),
    max_teams:       String(league?.max_teams    || 16),
    status:          league?.status          || 'active',
    primary_color:   league?.primary_color   || '#F97316',
    secondary_color: league?.secondary_color || '#ffffff',
    logo_url:        league?.logo_url        || '',
    website_url:     league?.website_url     || '',
    location:        league?.location        || '',
    contact_name:    league?.contact_name    || '',
    contact_email:   league?.contact_email   || '',
    notes:           league?.notes           || '',
  });
  const [saving, setSaving]           = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [err, setErr]                 = useState<string | null>(null);
  const logoRef = useRef<HTMLInputElement>(null);

  const up = (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [k]: e.target.value }));

  const uploadLogo = async (file: File) => {
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const { data } = await api.post('/videos/upload-photo', fd);
      setForm(f => ({ ...f, logo_url: data.photo_url }));
    } catch { setErr('Logo upload failed.'); }
    finally { setLogoUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { setErr('League name is required.'); return; }
    setSaving(true); setErr(null);
    const body = { ...form, season_year: parseInt(form.season_year), max_teams: parseInt(form.max_teams) };
    try {
      if (league?.id) { await api.put(`/leagues/${league.id}`, body); }
      else             { await api.post('/leagues', body); }
      onSaved();
    } catch (e: any) { setErr(e.response?.data?.error || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: '8px', fontSize: '14px', background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', color: 'var(--ink-1)', boxSizing: 'border-box' };
  const lbl: React.CSSProperties = { fontSize: '11px', fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '5px' };
  const fg: React.CSSProperties = { marginBottom: '14px' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 800, margin: 0 }}>{league ? 'Edit League' : '+ New League'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'var(--ink-3)', lineHeight: 1 }}>×</button>
        </div>

        <div style={fg}><div style={lbl}>League Name *</div>
          <input style={inp} value={form.name} onChange={up('name')} placeholder="e.g. Carmel Youth Soccer League" /></div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '14px' }}>
          <div><div style={lbl}>Sport</div>
            <select style={inp} value={form.sport} onChange={up('sport')}>
              <option value="soccer">⚽ Soccer</option>
              <option value="baseball">⚾ Baseball</option>
              <option value="softball">🥎 Softball</option>
              <option value="basketball">🏀 Basketball</option>
            </select>
          </div>
          <div><div style={lbl}>Season Year</div>
            <input style={inp} type="number" value={form.season_year} onChange={up('season_year')} min="2020" max="2035" /></div>
          <div><div style={lbl}>Max Teams (soft cap)</div>
            <input style={inp} type="number" value={form.max_teams} onChange={up('max_teams')} min="2" max="256" /></div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
          <div><div style={lbl}>Primary Color</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input type="color" value={form.primary_color} onChange={up('primary_color')} style={{ width: 36, height: 36, border: 'none', borderRadius: '6px', cursor: 'pointer', padding: 2 }} />
              <input style={{ ...inp, flex: 1 }} value={form.primary_color} onChange={up('primary_color')} />
            </div>
          </div>
          <div><div style={lbl}>Status</div>
            <select style={inp} value={form.status} onChange={up('status')}>
              <option value="active">✅ Active</option>
              <option value="trial">🔶 Trial</option>
              <option value="suspended">🚫 Suspended</option>
            </select>
          </div>
        </div>

        <div style={fg}><div style={lbl}>Description</div>
          <textarea style={{ ...inp, minHeight: '64px', resize: 'vertical' } as React.CSSProperties}
            value={form.description} onChange={up('description')} placeholder="Optional description shown on the public league page…" /></div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
          <div><div style={lbl}>Location</div><input style={inp} value={form.location} onChange={up('location')} placeholder="City / Region" /></div>
          <div><div style={lbl}>Website</div><input style={inp} value={form.website_url} onChange={up('website_url')} placeholder="https://…" /></div>
        </div>

        <div style={{ borderTop: '1px solid var(--hairline)', margin: '6px 0 14px', paddingTop: '14px' }}>
          <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '12px' }}>Logo & Contact</div>
          <div style={fg}>
            <div style={lbl}>Logo</div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              {form.logo_url && (
                <div style={{ width: 56, height: 56, borderRadius: '10px', overflow: 'hidden', background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img src={mediaUrl(form.logo_url)} alt="logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                </div>
              )}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <input style={inp} value={form.logo_url} onChange={up('logo_url')} placeholder="Paste URL or upload →" />
                <input ref={logoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { if (e.target.files?.[0]) uploadLogo(e.target.files[0]); }} />
                <button type="button" onClick={() => logoRef.current?.click()} style={{ padding: '6px 12px', fontSize: '12px', fontWeight: 700, borderRadius: '7px', border: '1px solid var(--hairline)', background: 'var(--surface-muted)', color: 'var(--ink-3)', cursor: 'pointer' }}>
                  {logoUploading ? '⏳ Uploading…' : '⬆ Upload Logo'}
                </button>
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
            <div><div style={lbl}>Contact Name</div><input style={inp} value={form.contact_name} onChange={up('contact_name')} placeholder="Jane Smith" /></div>
            <div><div style={lbl}>Contact Email</div><input style={inp} value={form.contact_email} onChange={up('contact_email')} placeholder="jane@example.com" /></div>
          </div>
          <div><div style={lbl}>Internal Notes</div>
            <textarea style={{ ...inp, minHeight: '56px', resize: 'vertical' } as React.CSSProperties}
              value={form.notes} onChange={up('notes')} placeholder="Billing notes, contract details, renewal dates…" /></div>
        </div>

        {err && <div style={{ marginBottom: '12px', padding: '10px 14px', borderRadius: '8px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)', fontSize: '13px', color: '#F87171' }}>{err}</div>}

        <div style={{ display: 'flex', gap: '10px', paddingTop: '4px' }}>
          <button className="btn btn-primary" onClick={save} disabled={saving || logoUploading}>{saving ? 'Saving…' : league ? 'Save Changes' : 'Create League'}</button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ── LeagueManagersModal ───────────────────────────────────────────────────────
const LeagueManagersModal = ({ league, allUsers, onClose }: {
  league: LeagueRow; allUsers: any[]; onClose: () => void;
}) => {
  const [managers, setManagers] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading]  = useState(true);
  const [saving, setSaving]    = useState(false);
  const [err, setErr]          = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/leagues/${league.id}/managers`); setManagers(r.data); }
    catch {} finally { setLoading(false); }
  }, [league.id]);
  useEffect(() => { load(); }, [load]);

  const addManager = async () => {
    if (!selectedUserId) return;
    setSaving(true); setErr(null);
    try { await api.post(`/leagues/${league.id}/managers`, { user_id: selectedUserId }); await load(); setSelectedUserId(''); }
    catch (e: any) { setErr(e.response?.data?.error || 'Failed to add manager.'); }
    finally { setSaving(false); }
  };

  const removeManager = async (userId: string) => {
    if (!window.confirm('Remove this manager from the league?')) return;
    await api.delete(`/leagues/${league.id}/managers/${userId}`);
    await load();
  };

  const availableUsers = allUsers.filter(u => !managers.find(m => m.user_id === u.id));

  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: '8px', fontSize: '14px', background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', color: 'var(--ink-1)', boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 800, margin: 0 }}>👥 League Managers — {league.name}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'var(--ink-3)', lineHeight: 1 }}>×</button>
        </div>

        {loading ? <div style={{ textAlign: 'center', padding: '24px', color: 'var(--ink-4)' }}>Loading…</div> : (
          <>
            {managers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: 'var(--ink-4)', fontSize: '13px', marginBottom: '16px' }}>No managers assigned yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                {managers.map(m => (
                  <div key={m.user_id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', borderRadius: '8px', background: 'var(--surface-sunken)', border: '1px solid var(--hairline)' }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #06B6D4, #0891B2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 800, color: '#fff', flexShrink: 0 }}>
                      {m.username.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--ink-1)' }}>{m.username}</div>
                      <div style={{ fontSize: '12px', color: 'var(--ink-3)' }}>{m.email}</div>
                    </div>
                    <button style={{ padding: '4px 10px', fontSize: '12px', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#F87171' }}
                      onClick={() => removeManager(m.user_id)}>Remove</button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ borderTop: '1px solid var(--hairline)', paddingTop: '14px' }}>
              <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '10px' }}>Add Manager</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <select style={{ ...inp, flex: 1 }} value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}>
                  <option value="">— Select a user —</option>
                  {availableUsers.map(u => <option key={u.id} value={u.id}>{u.username} ({u.email})</option>)}
                </select>
                <button className="btn btn-primary" onClick={addManager} disabled={!selectedUserId || saving}>{saving ? '…' : 'Add'}</button>
              </div>
              {err && <div style={{ marginTop: '8px', fontSize: '13px', color: '#F87171' }}>{err}</div>}
            </div>
          </>
        )}
        <div style={{ marginTop: '16px' }}>
          <button className="btn btn-ghost" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
};

// ── LeagueTeamsModal ──────────────────────────────────────────────────────────
const LeagueTeamsModal = ({ league, onClose, onSaved }: {
  league: LeagueRow; onClose: () => void; onSaved: () => void;
}) => {
  const [assignedTeams, setAssignedTeams] = useState<any[]>([]);
  const [availableTeams, setAvailableTeams] = useState<any[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [assigned, available] = await Promise.all([
        api.get(`/leagues/${league.id}/teams`),
        api.get(`/leagues/${league.id}/available-teams`),
      ]);
      setAssignedTeams(assigned.data);
      setAvailableTeams(available.data);
    } catch {} finally { setLoading(false); }
  }, [league.id]);
  useEffect(() => { load(); }, [load]);

  const assignTeam = async () => {
    if (!selectedTeamId) return;
    setSaving(true);
    try { await api.post(`/leagues/${league.id}/assign-team`, { team_id: selectedTeamId }); await load(); setSelectedTeamId(''); onSaved(); }
    catch {} finally { setSaving(false); }
  };

  const removeTeam = async (teamId: string) => {
    if (!window.confirm('Remove this team from the league?')) return;
    await api.delete(`/leagues/${league.id}/teams/${teamId}`);
    await load(); onSaved();
  };

  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: '8px', fontSize: '14px', background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', color: 'var(--ink-1)', boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 800, margin: 0 }}>🏆 Teams — {league.name}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'var(--ink-3)', lineHeight: 1 }}>×</button>
        </div>

        {loading ? <div style={{ textAlign: 'center', padding: '24px', color: 'var(--ink-4)' }}>Loading…</div> : (
          <>
            {/* Assigned teams */}
            <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>
              In This League ({assignedTeams.length} / {league.max_teams} max)
            </div>
            {assignedTeams.length === 0 ? (
              <div style={{ fontSize: '13px', color: 'var(--ink-4)', fontStyle: 'italic', marginBottom: '12px' }}>No teams assigned yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
                {assignedTeams.map((t: any) => (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', background: 'var(--surface-sunken)', border: '1px solid var(--hairline)' }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: t.primary_color, flexShrink: 0 }} />
                    <div style={{ flex: 1, fontWeight: 700, fontSize: '13px', color: 'var(--ink-1)' }}>{t.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--ink-3)' }}>{t.sport}</div>
                    <button style={{ padding: '3px 8px', fontSize: '11px', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#F87171' }}
                      onClick={() => removeTeam(t.id)}>Remove</button>
                  </div>
                ))}
              </div>
            )}

            {/* Assign existing team */}
            {availableTeams.length > 0 && (
              <div style={{ borderTop: '1px solid var(--hairline)', paddingTop: '14px' }}>
                <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>Assign Existing Team</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select style={{ ...inp, flex: 1 }} value={selectedTeamId} onChange={e => setSelectedTeamId(e.target.value)}>
                    <option value="">— Select team —</option>
                    {availableTeams.map((t: any) => <option key={t.id} value={t.id}>{t.name} ({t.sport})</option>)}
                  </select>
                  <button className="btn btn-primary" onClick={assignTeam} disabled={!selectedTeamId || saving}>{saving ? '…' : 'Assign'}</button>
                </div>
              </div>
            )}
          </>
        )}
        <div style={{ marginTop: '16px' }}>
          <button className="btn btn-ghost" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
};

// ── LeaguesPanel ──────────────────────────────────────────────────────────────
const LeaguesPanel = ({ allUsers }: { allUsers: any[] }) => {
  const [leagues, setLeagues]               = useState<LeagueRow[]>([]);
  const [loading, setLoading]               = useState(true);
  const [leagueModal, setLeagueModal]       = useState<{ open: boolean; row?: LeagueRow }>({ open: false });
  const [managersModal, setManagersModal]   = useState<{ open: boolean; row?: LeagueRow }>({ open: false });
  const [teamsModal, setTeamsModal]         = useState<{ open: boolean; row?: LeagueRow }>({ open: false });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/leagues'); setLeagues(r.data); }
    catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const deleteLeague = async (l: LeagueRow) => {
    if (!window.confirm(`Delete "${l.name}"? Teams will be unlinked but not deleted.`)) return;
    await api.delete(`/leagues/${l.id}`);
    await load();
  };

  const SPORT_EMOJI: Record<string, string> = { soccer: '⚽', baseball: '⚾', softball: '🥎', basketball: '🏀' };
  const card: React.CSSProperties = { background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '12px', padding: '16px 20px' };

  if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: 'var(--ink-4)' }}>Loading…</div>;

  return (
    <div>
      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: 'Total Leagues', value: leagues.length,                                       color: '#F97316' },
          { label: 'Active',        value: leagues.filter(l => l.status === 'active').length,    color: '#22C55E' },
          { label: 'Teams Managed', value: leagues.reduce((s, l) => s + (l.team_count || 0), 0), color: '#3B82F6' },
        ].map(s => (
          <div key={s.label} style={{ ...card, textAlign: 'center' }}>
            <div style={{ fontSize: '30px', fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '4px' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Header actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <button className="btn btn-primary" onClick={() => setLeagueModal({ open: true })}>+ New League</button>
      </div>

      {/* League list */}
      {leagues.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '52px', marginBottom: '12px' }}>🏟️</div>
          <h3 style={{ fontWeight: 800, fontSize: '18px', marginBottom: '8px' }}>No leagues yet</h3>
          <p style={{ color: 'var(--ink-3)', fontSize: '14px', marginBottom: '20px' }}>Create your first league to group teams and unlock the League Manager portal.</p>
          <button className="btn btn-primary" onClick={() => setLeagueModal({ open: true })}>+ Create First League</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {leagues.map(l => {
            const sc = STATUS_COLORS[l.status] || STATUS_COLORS.active;
            return (
              <div key={l.id} style={card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                  {/* Logo / color swatch */}
                  <div style={{ width: 48, height: 48, borderRadius: '10px', flexShrink: 0, background: `${l.primary_color}20`, border: `2px solid ${l.primary_color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' }}>
                    {l.logo_url
                      ? <img src={mediaUrl(l.logo_url)} alt={l.name} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      : (SPORT_EMOJI[l.sport] || '🏟️')}
                  </div>
                  {/* Info */}
                  <div style={{ flex: 1, minWidth: '180px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '2px' }}>
                      <span style={{ fontWeight: 900, fontSize: '15px', color: 'var(--ink-1)' }}>{l.name}</span>
                      <span style={{ fontSize: '10px', fontWeight: 800, padding: '2px 8px', borderRadius: '12px', background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, textTransform: 'uppercase', letterSpacing: '0.7px' }}>
                        {l.status}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--ink-3)' }}>
                      {SPORT_EMOJI[l.sport]} {l.sport} · {l.season_year}
                      {l.location ? ` · ${l.location}` : ''}
                    </div>
                    <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
                      <span style={{ fontSize: '11px', color: 'var(--ink-4)' }}>🏆 {l.team_count || 0}/{l.max_teams} teams</span>
                      <span style={{ fontSize: '11px', color: 'var(--ink-4)' }}>👥 {l.manager_count || 0} managers</span>
                    </div>
                  </div>
                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', flexShrink: 0 }}>
                    <a href={`/leagues/${l.id}`} target="_blank" rel="noopener noreferrer"
                      style={{ padding: '5px 10px', fontSize: '12px', fontWeight: 700, borderRadius: '7px', border: '1px solid var(--hairline)', background: 'var(--surface-muted)', color: 'var(--ink-3)', textDecoration: 'none', display: 'inline-block' }}>
                      🔗 Public Page
                    </a>
                    <button className="btn btn-secondary btn-sm" onClick={() => setTeamsModal({ open: true, row: l })}>🏆 Teams</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setManagersModal({ open: true, row: l })}>👥 Managers</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setLeagueModal({ open: true, row: l })}>✏️ Edit</button>
                    <button style={{ padding: '5px 10px', fontSize: '12px', fontWeight: 700, borderRadius: '7px', cursor: 'pointer', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#F87171' }}
                      onClick={() => deleteLeague(l)}>Delete</button>
                  </div>
                </div>
                {l.notes && (
                  <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid var(--hairline)', fontSize: '12px', color: 'var(--ink-3)' }}>
                    📝 {l.notes}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {leagueModal.open && (
        <LeagueModal
          league={leagueModal.row}
          onClose={() => setLeagueModal({ open: false })}
          onSaved={() => { setLeagueModal({ open: false }); load(); }}
        />
      )}
      {managersModal.open && managersModal.row && (
        <LeagueManagersModal
          league={managersModal.row}
          allUsers={allUsers}
          onClose={() => setManagersModal({ open: false })}
        />
      )}
      {teamsModal.open && teamsModal.row && (
        <LeagueTeamsModal
          league={teamsModal.row}
          onClose={() => setTeamsModal({ open: false })}
          onSaved={load}
        />
      )}
    </div>
  );
};

// ─── Sponsors Panel ───────────────────────────────────────────────────────────

interface SponsorRow {
  id: string; name: string; tagline?: string; website_url?: string;
  logo_url?: string; contact_name?: string; contact_email?: string;
  notes?: string; created_at: string;
}
interface PlacementRow {
  id: string; sponsor_id: string; sponsor_name: string; sponsor_logo?: string;
  team_id?: string; team_name?: string; game_id?: string; game_title?: string;
  placement: string; season_year?: number; active: number;
}

const PLACEMENT_META: Record<string, { label: string; icon: string; desc: string; color: string }> = {
  field_bug:   { label: 'Field Bug',     icon: '📷', desc: 'Corner logo overlaid on the live video feed',    color: '#F97316' },
  lower_third: { label: 'Lower Third',   icon: '📺', desc: 'Full-width banner beneath the live stage',       color: '#3B82F6' },
  season_card: { label: 'Season Card',   icon: '🏆', desc: 'Sidebar card on team & game detail pages',       color: '#22C55E' },
  title:       { label: 'Title Sponsor', icon: '⭐', desc: 'Premium platform-wide title placement',          color: '#A78BFA' },
};

// ── SponsorModal ──────────────────────────────────────────────────────────────
const SponsorModal = ({ sponsor, onClose, onSaved }: {
  sponsor?: SponsorRow; onClose: () => void; onSaved: () => void;
}) => {
  const [form, setForm] = useState({
    name: sponsor?.name || '', tagline: sponsor?.tagline || '',
    website_url: sponsor?.website_url || '', logo_url: sponsor?.logo_url || '',
    contact_name: sponsor?.contact_name || '', contact_email: sponsor?.contact_email || '',
    notes: sponsor?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const logoRef = useRef<HTMLInputElement>(null);

  const up = (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [k]: e.target.value }));

  const uploadLogo = async (file: File) => {
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const { data } = await api.post('/videos/upload-photo', fd);
      setForm(f => ({ ...f, logo_url: data.photo_url }));
    } catch { setErr('Logo upload failed.'); }
    finally { setLogoUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { setErr('Business name is required.'); return; }
    setSaving(true); setErr(null);
    try {
      if (sponsor?.id) { await api.put(`/sponsors/${sponsor.id}`, form); }
      else              { await api.post('/sponsors', form); }
      onSaved();
    } catch (e: any) { setErr(e.response?.data?.error || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const inp: React.CSSProperties = {
    width: '100%', padding: '9px 12px', borderRadius: '8px', fontSize: '14px',
    background: 'var(--surface-sunken)', border: '1px solid var(--hairline)',
    color: 'var(--ink-1)', boxSizing: 'border-box',
  };
  const lbl: React.CSSProperties = { fontSize: '11px', fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '5px' };
  const fg: React.CSSProperties = { marginBottom: '14px' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 800, margin: 0 }}>{sponsor ? 'Edit Sponsor' : '+ New Sponsor'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'var(--ink-3)', lineHeight: 1 }}>×</button>
        </div>

        <div style={fg}><div style={lbl}>Business Name *</div>
          <input style={inp} value={form.name} onChange={up('name')} placeholder="e.g. Acme Sports Bar" /></div>

        <div style={fg}><div style={lbl}>Tagline</div>
          <input style={inp} value={form.tagline} onChange={up('tagline')} placeholder="Your #1 fan destination" /></div>

        <div style={fg}><div style={lbl}>Website URL</div>
          <input style={inp} value={form.website_url} onChange={up('website_url')} placeholder="https://example.com" /></div>

        <div style={fg}>
          <div style={lbl}>Sponsor Logo</div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {form.logo_url && (
              <div style={{ width: 70, height: 70, borderRadius: '10px', overflow: 'hidden', background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <img src={mediaUrl(form.logo_url)} alt="logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              </div>
            )}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '180px' }}>
              <input style={inp} value={form.logo_url} onChange={up('logo_url')} placeholder="Paste image URL or upload →" />
              <input ref={logoRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => { if (e.target.files?.[0]) uploadLogo(e.target.files[0]); }} />
              <button type="button" onClick={() => logoRef.current?.click()}
                style={{ padding: '6px 14px', fontSize: '12px', fontWeight: 700, borderRadius: '7px', border: '1px solid var(--hairline)', background: 'var(--surface-muted)', color: 'var(--ink-3)', cursor: logoUploading ? 'wait' : 'pointer' }}>
                {logoUploading ? '⏳ Uploading…' : '⬆ Upload Logo'}
              </button>
            </div>
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--hairline)', margin: '6px 0 14px', paddingTop: '14px' }}>
          <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '12px' }}>Contact Info</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div><div style={lbl}>Contact Name</div><input style={inp} value={form.contact_name} onChange={up('contact_name')} placeholder="Jane Smith" /></div>
            <div><div style={lbl}>Contact Email</div><input style={inp} value={form.contact_email} onChange={up('contact_email')} placeholder="jane@example.com" /></div>
          </div>
        </div>

        <div style={fg}><div style={lbl}>Notes / Package Details</div>
          <textarea style={{ ...inp, minHeight: '72px', resize: 'vertical' } as React.CSSProperties}
            value={form.notes} onChange={up('notes') as any}
            placeholder="Contract dates, rate, package tier, renewal date…" /></div>

        {err && <div style={{ marginBottom: '12px', padding: '10px 14px', borderRadius: '8px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)', fontSize: '13px', color: '#F87171' }}>{err}</div>}

        <div style={{ display: 'flex', gap: '10px', paddingTop: '4px' }}>
          <button className="btn btn-primary" onClick={save} disabled={saving || logoUploading}>{saving ? 'Saving…' : sponsor ? 'Save Changes' : 'Create Sponsor'}</button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ── PlacementModal ────────────────────────────────────────────────────────────
const PlacementModal = ({ placement, sponsors, teams, games, leagues, onClose, onSaved }: {
  placement?: PlacementRow; sponsors: SponsorRow[]; teams: Team[]; games: Game[]; leagues: LeagueRow[];
  onClose: () => void; onSaved: () => void;
}) => {
  const [form, setForm] = useState({
    sponsor_id:  placement?.sponsor_id || '',
    scope:       (placement?.team_id ? 'team' : placement?.game_id ? 'game' : (placement as any)?.league_id ? 'league' : 'team') as 'team' | 'game' | 'league',
    league_id:   (placement as any)?.league_id || '',
    team_id:     placement?.team_id || '',
    game_id:     placement?.game_id || '',
    placement:   placement?.placement || 'field_bug',
    season_year: String(placement?.season_year || new Date().getFullYear()),
    active:      String(placement !== undefined ? placement.active : 1),
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const upSel = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.sponsor_id) { setErr('Please select a sponsor.'); return; }
    if (form.scope === 'team' && !form.team_id) { setErr('Please select a team.'); return; }
    if (form.scope === 'game' && !form.game_id) { setErr('Please select a game.'); return; }
    setSaving(true); setErr(null);
    const body = {
      sponsor_id:  form.sponsor_id,
      team_id:     form.scope === 'team'   ? form.team_id   : null,
      game_id:     form.scope === 'game'   ? form.game_id   : null,
      league_id:   form.scope === 'league' ? (form as any).league_id : null,
      placement:   form.placement,
      season_year: parseInt(form.season_year),
      active:      parseInt(form.active),
    };
    try {
      if (placement?.id) { await api.put(`/sponsors/placements/${placement.id}`, body); }
      else               { await api.post('/sponsors/placements', body); }
      onSaved();
    } catch (e: any) { setErr(e.response?.data?.error || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const sel: React.CSSProperties = {
    width: '100%', padding: '9px 12px', borderRadius: '8px', fontSize: '14px',
    background: 'var(--surface-sunken)', border: '1px solid var(--hairline)',
    color: 'var(--ink-1)', boxSizing: 'border-box',
  };
  const lbl: React.CSSProperties = { fontSize: '11px', fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '5px' };
  const fg: React.CSSProperties = { marginBottom: '14px' };
  const meta = PLACEMENT_META[form.placement];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 800, margin: 0 }}>{placement ? 'Edit Placement' : '+ New Placement'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'var(--ink-3)', lineHeight: 1 }}>×</button>
        </div>

        <div style={fg}><div style={lbl}>Sponsor *</div>
          <select style={sel} value={form.sponsor_id} onChange={upSel('sponsor_id')}>
            <option value="">— Select a sponsor —</option>
            {sponsors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        <div style={fg}>
          <div style={lbl}>Placement Zone *</div>
          <select style={sel} value={form.placement} onChange={upSel('placement')}>
            {Object.entries(PLACEMENT_META).map(([k, v]) =>
              <option key={k} value={k}>{v.icon} {v.label} — {v.desc}</option>)}
          </select>
          {meta && (
            <div style={{ marginTop: '7px', padding: '9px 12px', borderRadius: '8px', background: `${meta.color}12`, border: `1px solid ${meta.color}30`, fontSize: '12px', color: meta.color, fontWeight: 600 }}>
              {meta.icon} {meta.label}: {meta.desc}
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
          <div><div style={lbl}>Scope</div>
            <select style={sel} value={form.scope} onChange={upSel('scope') as any}>
              <option value="team">Season-wide (Team)</option>
              <option value="game">Single Game</option>
              <option value="league">League-wide</option>
            </select>
          </div>
          <div><div style={lbl}>Season Year</div>
            <input style={sel} type="number" value={form.season_year} min="2020" max="2035"
              onChange={upSel('season_year') as any} />
          </div>
        </div>

        {form.scope === 'team' && (
          <div style={fg}><div style={lbl}>Team *</div>
            <select style={sel} value={form.team_id} onChange={upSel('team_id')}>
              <option value="">— Select a team —</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}

        {form.scope === 'game' && (
          <div style={fg}><div style={lbl}>Game *</div>
            <select style={sel} value={form.game_id} onChange={upSel('game_id')}>
              <option value="">— Select a game —</option>
              {games.map(g => <option key={g.id} value={g.id}>{g.title}</option>)}
            </select>
          </div>
        )}

        {form.scope === 'league' && (
          <div style={fg}><div style={lbl}>League *</div>
            <select style={sel} value={(form as any).league_id} onChange={upSel('league_id' as any)}>
              <option value="">— Select a league —</option>
              {leagues.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        )}

        <div style={fg}><div style={lbl}>Status</div>
          <select style={sel} value={form.active} onChange={upSel('active')}>
            <option value="1">✅ Active — currently showing</option>
            <option value="0">⏸ Paused — hidden from viewers</option>
          </select>
        </div>

        {err && <div style={{ marginBottom: '12px', padding: '10px 14px', borderRadius: '8px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)', fontSize: '13px', color: '#F87171' }}>{err}</div>}

        <div style={{ display: 'flex', gap: '10px', paddingTop: '4px' }}>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : placement ? 'Save Changes' : 'Add Placement'}</button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ── SponsorsPanel ─────────────────────────────────────────────────────────────
const SponsorsPanel = ({ teams, games }: { teams: Team[]; games: Game[] }) => {
  const [sponsors, setSponsors]         = useState<SponsorRow[]>([]);
  const [placements, setPlacements]     = useState<PlacementRow[]>([]);
  const [loading, setLoading]           = useState(true);
  const [view, setView]                 = useState<'sponsors' | 'placements'>('sponsors');
  const [sponsorModal, setSponsorModal] = useState<{ open: boolean; row?: SponsorRow }>({ open: false });
  const [placeModal, setPlaceModal]     = useState<{ open: boolean; row?: PlacementRow }>({ open: false });
  const [expandedId, setExpandedId]     = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, pRes] = await Promise.all([api.get('/sponsors'), api.get('/sponsors/placements')]);
      setSponsors(sRes.data);
      setPlacements(pRes.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteSponsor = async (s: SponsorRow) => {
    if (!window.confirm(`Delete "${s.name}"? All their placements will also be removed.`)) return;
    await api.delete(`/sponsors/${s.id}`);
    await load();
  };

  const deletePlacement = async (p: PlacementRow) => {
    if (!window.confirm('Remove this placement?')) return;
    await api.delete(`/sponsors/placements/${p.id}`);
    await load();
  };

  const toggleActive = async (p: PlacementRow) => {
    await api.put(`/sponsors/placements/${p.id}`, {
      sponsor_id: p.sponsor_id, team_id: p.team_id || null,
      game_id: p.game_id || null, placement: p.placement,
      season_year: p.season_year, active: p.active ? 0 : 1,
    });
    await load();
  };

  const activePlacements = placements.filter(p => p.active === 1).length;
  const card: React.CSSProperties = { background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '12px', padding: '16px 20px' };

  if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: 'var(--ink-4)' }}>Loading…</div>;

  return (
    <div>
      {/* ── Stats row ────────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: 'Sponsors',          value: sponsors.length,                                          color: '#F97316' },
          { label: 'Active Placements', value: activePlacements,                                         color: '#22C55E' },
          { label: 'Field Bugs',        value: placements.filter(p => p.placement === 'field_bug').length,   color: '#3B82F6' },
          { label: 'Season Cards',      value: placements.filter(p => p.placement === 'season_card').length, color: '#A78BFA' },
        ].map(s => (
          <div key={s.label} style={{ ...card, textAlign: 'center' }}>
            <div style={{ fontSize: '30px', fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '4px' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Placement zone legend ────────────────────────────────────────────── */}
      <div style={{ ...card, marginBottom: '20px' }}>
        <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '12px' }}>Ad Placement Zones</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '10px' }}>
          {Object.entries(PLACEMENT_META).map(([k, v]) => (
            <div key={k} style={{ padding: '12px 14px', borderRadius: '10px', background: `${v.color}10`, border: `1px solid ${v.color}28` }}>
              <div style={{ fontSize: '20px', marginBottom: '4px' }}>{v.icon}</div>
              <div style={{ fontWeight: 800, fontSize: '13px', color: v.color, marginBottom: '2px' }}>{v.label}</div>
              <div style={{ fontSize: '11px', color: 'var(--ink-3)', lineHeight: '1.4' }}>{v.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Sub-nav ──────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '16px', borderBottom: '1px solid var(--hairline)', paddingBottom: '4px' }}>
        {(['sponsors', 'placements'] as const).map(v => (
          <button key={v} onClick={() => setView(v)} style={{
            padding: '7px 16px', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
            background: view === v ? 'rgba(249,115,22,0.15)' : 'transparent',
            color: view === v ? '#F97316' : 'var(--ink-3)',
          }}>
            {v === 'sponsors' ? `💼 Sponsors (${sponsors.length})` : `📍 Placements (${placements.length})`}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        {view === 'sponsors' ? (
          <button className="btn btn-primary btn-sm" onClick={() => setSponsorModal({ open: true })}>+ New Sponsor</button>
        ) : (
          <button className="btn btn-primary btn-sm" onClick={() => setPlaceModal({ open: true })} disabled={sponsors.length === 0}>
            + New Placement
          </button>
        )}
      </div>

      {/* ── Sponsors list ────────────────────────────────────────────────────── */}
      {view === 'sponsors' && (
        sponsors.length === 0 ? (
          <div style={{ ...card, textAlign: 'center', padding: '56px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>💼</div>
            <h3 style={{ fontWeight: 800, fontSize: '18px', marginBottom: '6px' }}>No sponsors yet</h3>
            <p style={{ color: 'var(--ink-3)', fontSize: '14px', marginBottom: '20px' }}>
              Add your first sponsor to start placing ads on live streams and team pages.
            </p>
            <button className="btn btn-primary" onClick={() => setSponsorModal({ open: true })}>+ Add First Sponsor</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {sponsors.map(s => {
              const myPlacements = placements.filter(p => p.sponsor_id === s.id);
              const isExpanded   = expandedId === s.id;
              return (
                <div key={s.id} style={card}>
                  {/* ── Sponsor header row ── */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    {/* Logo / avatar */}
                    <div style={{ width: 54, height: 54, borderRadius: '10px', overflow: 'hidden', flexShrink: 0, background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: 900, color: '#F97316' }}>
                      {s.logo_url
                        ? <img src={mediaUrl(s.logo_url)} alt={s.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        : s.name.charAt(0).toUpperCase()}
                    </div>
                    {/* Name + meta */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 800, fontSize: '15px', color: 'var(--ink-1)', marginBottom: '2px' }}>{s.name}</div>
                      {s.tagline && <div style={{ fontSize: '12px', color: 'var(--ink-3)', marginBottom: '2px' }}>{s.tagline}</div>}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {s.website_url && (
                          <a href={s.website_url} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: '11px', color: '#3B82F6', fontWeight: 600 }}>
                            🌐 {s.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                          </a>
                        )}
                        {s.contact_email && (
                          <span style={{ fontSize: '11px', color: 'var(--ink-3)' }}>✉️ {s.contact_email}</span>
                        )}
                      </div>
                    </div>
                    {/* Placement count badge */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                      <div style={{
                        padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 800,
                        background: myPlacements.length > 0 ? 'rgba(34,197,94,0.12)' : 'var(--surface-muted)',
                        color: myPlacements.length > 0 ? '#22C55E' : 'var(--ink-4)',
                        border: myPlacements.length > 0 ? '1px solid rgba(34,197,94,0.3)' : '1px solid var(--hairline)',
                      }}>
                        {myPlacements.length} placement{myPlacements.length !== 1 ? 's' : ''}
                      </div>
                      <button className="btn btn-secondary btn-sm" onClick={() => setSponsorModal({ open: true, row: s })}>✏️ Edit</button>
                      <button style={{
                        padding: '5px 10px', fontSize: '12px', fontWeight: 700, borderRadius: '7px', cursor: 'pointer',
                        background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#F87171',
                      }} onClick={() => deleteSponsor(s)}>Delete</button>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', fontSize: '18px', lineHeight: 1, padding: '4px' }}
                        onClick={() => setExpandedId(isExpanded ? null : s.id)}>
                        {isExpanded ? '▲' : '▼'}
                      </button>
                    </div>
                  </div>

                  {/* ── Expanded: placement list + quick-add ── */}
                  {isExpanded && (
                    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--hairline)' }}>
                      {s.notes && (
                        <div style={{ marginBottom: '12px', padding: '10px 12px', borderRadius: '8px', background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', fontSize: '13px', color: 'var(--ink-3)' }}>
                          📝 {s.notes}
                        </div>
                      )}
                      <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>
                        Active Placements
                      </div>
                      {myPlacements.length === 0 ? (
                        <div style={{ fontSize: '13px', color: 'var(--ink-4)', fontStyle: 'italic', marginBottom: '10px' }}>
                          No placements yet — click "+ New Placement" to assign this sponsor to a team or game.
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                          {myPlacements.map(p => {
                            const meta = PLACEMENT_META[p.placement] || { label: p.placement, icon: '📍', color: 'var(--ink-3)' };
                            return (
                              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', background: 'var(--surface-sunken)', border: '1px solid var(--hairline)' }}>
                                <span style={{ fontSize: '14px' }}>{meta.icon}</span>
                                <span style={{ fontSize: '12px', fontWeight: 700, color: meta.color }}>{meta.label}</span>
                                <span style={{ fontSize: '12px', color: 'var(--ink-3)', flex: 1 }}>
                                  {p.team_name ? `📌 ${p.team_name}` : p.game_title ? `🎮 ${p.game_title}` : 'Global'}
                                  {p.season_year ? ` · ${p.season_year}` : ''}
                                </span>
                                <button onClick={() => toggleActive(p)} style={{
                                  padding: '3px 10px', fontSize: '11px', fontWeight: 700, borderRadius: '6px', cursor: 'pointer',
                                  background: p.active ? 'rgba(34,197,94,0.12)' : 'rgba(156,163,175,0.12)',
                                  border: p.active ? '1px solid rgba(34,197,94,0.3)' : '1px solid var(--hairline)',
                                  color: p.active ? '#22C55E' : 'var(--ink-4)',
                                }}>
                                  {p.active ? '✅ Active' : '⏸ Paused'}
                                </button>
                                <button className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '3px 8px' }}
                                  onClick={() => setPlaceModal({ open: true, row: p })}>Edit</button>
                                <button style={{ padding: '3px 8px', fontSize: '11px', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#F87171' }}
                                  onClick={() => deletePlacement(p)}>Remove</button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <button className="btn btn-secondary btn-sm" onClick={() => setPlaceModal({ open: true, row: { placement: 'field_bug', sponsor_id: s.id, active: 1 } as PlacementRow })}>
                        + Add Placement for {s.name}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ── All Placements list ──────────────────────────────────────────────── */}
      {view === 'placements' && (
        placements.length === 0 ? (
          <div style={{ ...card, textAlign: 'center', padding: '56px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📍</div>
            <h3 style={{ fontWeight: 800, fontSize: '18px', marginBottom: '6px' }}>No placements yet</h3>
            <p style={{ color: 'var(--ink-3)', fontSize: '14px', marginBottom: '20px' }}>
              {sponsors.length === 0
                ? 'Create a sponsor first, then assign them a placement zone.'
                : 'Link a sponsor to a team or game to start showing ads.'}
            </p>
            <button className="btn btn-primary" onClick={() => setPlaceModal({ open: true })} disabled={sponsors.length === 0}>
              + Add Placement
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Group by zone */}
            {Object.entries(PLACEMENT_META).map(([zone, meta]) => {
              const group = placements.filter(p => p.placement === zone);
              if (group.length === 0) return null;
              return (
                <div key={zone}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', marginTop: '8px' }}>
                    <span style={{ fontSize: '14px' }}>{meta.icon}</span>
                    <span style={{ fontSize: '11px', fontWeight: 800, color: meta.color, textTransform: 'uppercase', letterSpacing: '0.8px' }}>{meta.label}</span>
                    <span style={{ fontSize: '11px', color: 'var(--ink-4)' }}>({group.length})</span>
                  </div>
                  {group.map(p => (
                    <div key={p.id} style={{ ...card, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      {/* Sponsor logo */}
                      <div style={{ width: 36, height: 36, borderRadius: '7px', overflow: 'hidden', flexShrink: 0, background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 800, color: '#F97316' }}>
                        {p.sponsor_logo
                          ? <img src={mediaUrl(p.sponsor_logo)} alt={p.sponsor_name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                          : p.sponsor_name.charAt(0)}
                      </div>
                      <div style={{ flex: 1, minWidth: '160px' }}>
                        <div style={{ fontWeight: 800, fontSize: '14px', color: 'var(--ink-1)' }}>{p.sponsor_name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--ink-3)', marginTop: '2px' }}>
                          {p.team_name ? `📌 ${p.team_name}` : p.game_title ? `🎮 ${p.game_title}` : 'Global'}
                          {p.season_year ? ` · Season ${p.season_year}` : ''}
                        </div>
                      </div>
                      <button onClick={() => toggleActive(p)} style={{
                        padding: '5px 12px', fontSize: '12px', fontWeight: 700, borderRadius: '7px', cursor: 'pointer',
                        background: p.active ? 'rgba(34,197,94,0.12)' : 'rgba(156,163,175,0.12)',
                        border: p.active ? '1px solid rgba(34,197,94,0.3)' : '1px solid var(--hairline)',
                        color: p.active ? '#22C55E' : 'var(--ink-4)',
                      }}>
                        {p.active ? '✅ Active' : '⏸ Paused'}
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setPlaceModal({ open: true, row: p })}>✏️ Edit</button>
                      <button style={{ padding: '5px 10px', fontSize: '12px', fontWeight: 700, borderRadius: '7px', cursor: 'pointer', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#F87171' }}
                        onClick={() => deletePlacement(p)}>Remove</button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ── Modals ───────────────────────────────────────────────────────────── */}
      {sponsorModal.open && (
        <SponsorModal
          sponsor={sponsorModal.row}
          onClose={() => setSponsorModal({ open: false })}
          onSaved={() => { setSponsorModal({ open: false }); load(); }}
        />
      )}
      {placeModal.open && (
        <PlacementModal
          placement={placeModal.row}
          sponsors={sponsors} teams={teams} games={games} leagues={[]}
          onClose={() => setPlaceModal({ open: false })}
          onSaved={() => { setPlaceModal({ open: false }); load(); }}
        />
      )}
    </div>
  );
};

// ─── Main Admin ───────────────────────────────────────────────────────────────
const Admin = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [allPlayers, setAllPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<ModalState>({ type: null });
  const [activeTab, setActiveTab] = useState<'games' | 'teams' | 'videos' | 'users' | 'sponsors' | 'leagues'>('games');
  const [users, setUsers] = useState<any[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const [viewerCounts, setViewerCounts] = useState<Record<string, number>>({});
  const [summaryLoading, setSummaryLoading] = useState<string | null>(null);
  const [assignTeamsUser, setAssignTeamsUser] = useState<any | null>(null);
  const [manageTeamsUser, setManageTeamsUser] = useState<any | null>(null);
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }, []);

  const fetchGames   = useCallback(async () => { try { const r = await api.get('/games');       setGames(r.data);     } catch {} finally { setLoading(false); } }, []);
  const fetchTeams   = useCallback(async () => { try { const r = await api.get('/teams');       setTeams(r.data);     } catch {} }, []);
  const fetchPlayers = useCallback(async () => { try { const r = await api.get('/players');     setAllPlayers(r.data); } catch {} }, []);
  const fetchUsers   = useCallback(async () => { try { const r = await api.get('/auth/users');  setUsers(r.data);     } catch {} }, []);

  const changeUserRole = async (userId: string, newRole: 'admin' | 'viewer' | 'coach') => {
    try {
      await api.put(`/auth/users/${userId}/role`, { role: newRole });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      showToast(`Role updated to ${newRole}`);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update role');
    }
  };

  const deleteUser = async (u: any) => {
    if (!window.confirm(`Delete user "${u.username}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/auth/users/${u.id}`);
      setUsers(prev => prev.filter(x => x.id !== u.id));
      showToast('User deleted');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleApprove = async (userId: string, linkedPlayerId?: string) => {
    try {
      await api.post(`/auth/users/${userId}/approve`, { linked_player_id: linkedPlayerId });
      await fetchUsers();
      showToast('User approved');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to approve user');
    }
  };

  const handleReject = async (userId: string) => {
    if (!window.confirm('Reject this account request? The user will see a "request declined" message.')) return;
    try {
      await api.post(`/auth/users/${userId}/reject`);
      await fetchUsers();
      showToast('User rejected');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to reject user');
    }
  };

  const handleApproveManager = async (userId: string, teamIds: string[]) => {
    try {
      await api.post(`/auth/users/${userId}/approve-manager`, { team_ids: teamIds });
      await fetchUsers();
      showToast('Team Manager approved');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to approve Team Manager');
    }
  };

  const handleCreateAndApprove = async (userId: string, teamName: string, sport: string) => {
    try {
      // 1. Create the new team
      const teamRes = await api.post('/teams', { name: teamName, sport, season_year: new Date().getFullYear() });
      const newTeam = teamRes.data;
      // 2. Approve TM and assign them to the new team
      await api.post(`/auth/users/${userId}/approve-manager`, { team_ids: [newTeam.id] });
      await Promise.all([fetchUsers(), fetchTeams()]);
      showToast(`Team "${teamName}" created and Team Manager approved`);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create team and approve');
    }
  };

  const handleRejectManager = async (userId: string) => {
    if (!window.confirm('Reject this Team Manager request?')) return;
    try {
      await api.post(`/auth/users/${userId}/reject-manager`);
      await fetchUsers();
      showToast('Team Manager request rejected');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to reject Team Manager');
    }
  };

  const handleSaveUserTeams = async () => {
    await fetchUsers();
    showToast('Team access updated');
  };

  useEffect(() => { fetchGames(); fetchTeams(); fetchPlayers(); fetchUsers(); }, [fetchGames, fetchTeams, fetchPlayers, fetchUsers]);

  // Live viewer counts via socket admin room
  useEffect(() => {
    const SERVER_URL = process.env.REACT_APP_SERVER_URL ?? '';
    const socket = io(SERVER_URL, { transports: ['websocket', 'polling'] });
    socket.on('connect', () => socket.emit('join_admin'));
    socket.on('viewer_counts_update', (counts: Record<string, number>) => setViewerCounts(counts));
    return () => { socket.disconnect(); };
  }, []);

  const openModal  = (type: ModalType, data?: any) => setModal({ type, ...data });
  const closeModal = () => { setModal({ type: null }); fetchGames(); fetchTeams(); fetchPlayers(); setRefreshKey(k => k + 1); };

  const handleCreated = (g: Game) => { setGames(p => [g, ...p]); showToast('Game created'); };
  const handleUpdated = (u: Game) => { setGames(p => p.map(g => g.id === u.id ? u : g)); showToast('Saved'); };
  const deleteGame = async (game: Game) => {
    if (!window.confirm(`Delete "${game.title}"? This cannot be undone.`)) return;
    await api.delete(`/games/${game.id}`);
    setGames(p => p.filter(g => g.id !== game.id));
  };

  const generateSummary = async (game: Game) => {
    setSummaryLoading(game.id);
    try {
      const r = await api.post(`/games/${game.id}/generate-summary`);
      setGames(p => p.map(g => g.id === game.id ? { ...g, ai_summary: r.data.summary } : g));
      showToast('✨ Summary generated!');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to generate summary. Check that GEMINI_API_KEY is set on the server.');
    } finally {
      setSummaryLoading(null);
    }
  };

  const statCards = [
    { label: 'Total', value: games.length, color: 'var(--ink-1)' },
    { label: 'Live', value: games.filter(g => g.status === 'live').length, color: '#F44336' },
    { label: 'Upcoming', value: games.filter(g => g.status === 'scheduled').length, color: '#F97316' },
    { label: 'Completed', value: games.filter(g => g.status === 'completed').length, color: '#22C55E' },
  ];

  const TabBtn = ({ id, label }: { id: 'games' | 'teams' | 'videos' | 'users' | 'sponsors' | 'leagues'; label: string }) => (
    <button onClick={() => setActiveTab(id)} style={{
      padding: '9px 18px', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '14px',
      cursor: 'pointer', transition: 'all 0.15s',
      background: activeTab === id ? 'rgba(249,115,22,0.15)' : 'transparent',
      color: activeTab === id ? '#F97316' : 'var(--ink-3)',
    }}>{label}</button>
  );

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '30px', fontWeight: 900, letterSpacing: '-0.5px', marginBottom: '4px' }}>Admin</h1>
          <p style={{ color: 'var(--ink-3)', fontSize: '14px' }}>Manage games, teams, players, videos, and sponsor placements.</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '28px' }}>
        {statCards.map(s => (
          <div key={s.label} style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: '11px', color: 'var(--ink-4)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '4px' }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid var(--hairline)', paddingBottom: '4px' }}>
        <TabBtn id="games"    label="🏟️ Games" />
        <TabBtn id="teams"    label="🏆 Teams & Players" />
        <TabBtn id="videos"   label="🎬 Videos" />
        <TabBtn id="users"    label="👥 Users" />
        <TabBtn id="sponsors" label="💰 Sponsors & Ads" />
        <TabBtn id="leagues"  label="🏟️ Leagues" />
      </div>

      {/* ── Games Tab ── */}
      {activeTab === 'games' && (
        <>
          {/* Live viewer counts — only shown when at least one game has viewers */}
          {Object.keys(viewerCounts).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
              {Object.entries(viewerCounts).map(([gameId, count]) => {
                const game = games.find(g => g.id === gameId);
                return (
                  <div key={gameId} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                    borderRadius: 10, padding: '8px 14px',
                  }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#EF4444', display: 'inline-block', flexShrink: 0, animation: 'pulse 1.5s infinite' }} />
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-1)' }}>
                      {game?.title ?? gameId}
                    </span>
                    <span style={{ fontSize: 20, fontWeight: 900, color: '#EF4444', lineHeight: 1 }}>{count}</span>
                    <span style={{ fontSize: 11, color: 'var(--ink-4)', fontWeight: 600 }}>watching</span>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '16px' }}>
            <button className="btn btn-secondary" onClick={() => openModal('import_ical')}>📅 Import from iCal</button>
            <button className="btn btn-primary" onClick={() => openModal('create')}>+ New Game</button>
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--ink-4)' }}>Loading...</div>
          ) : games.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 20px', background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '16px' }}>
              <div style={{ fontSize: '56px', marginBottom: '16px' }}>🏟️</div>
              <h3 style={{ fontSize: '20px', fontWeight: 800, marginBottom: '8px' }}>No games yet</h3>
              <button className="btn btn-primary" onClick={() => openModal('create')}>+ Create Game</button>
            </div>
          ) : (() => {
            const now = Date.now();
            // A game is "upcoming" if it's live, or scheduled within the last 12 hours or in the future.
            // Old games still sitting at 'scheduled' belong in the past section.
            const upcoming = [...games]
              .filter(g => g.status === 'live' || (g.status === 'scheduled' && new Date(g.scheduled_at).getTime() > now - 12 * 3600 * 1000))
              .sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime());
            const past = [...games]
              .filter(g => g.status === 'completed' || (g.status === 'scheduled' && new Date(g.scheduled_at).getTime() <= now - 12 * 3600 * 1000))
              .sort((a, b) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime());
            const renderGame = (game: Game) => (
                <div key={game.id} style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '12px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: '26px', flexShrink: 0 }}>
                    {game.sport === 'baseball' ? '⚾' : game.sport === 'softball' ? '🥎' : game.sport === 'basketball' ? '🏀' : '⚽'}
                  </div>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ fontWeight: 800, fontSize: '15px', marginBottom: '3px' }}>{game.title}</div>
                    <div style={{ fontSize: '13px', color: 'var(--ink-3)', marginBottom: '3px' }}>
                      {game.away_team} <span style={{ color: 'var(--ink-4)' }}>@</span> {game.home_team}
                      {game.home_final_score != null && game.away_final_score != null && (
                        <span style={{ fontWeight: 900, color: 'var(--ink-1)', marginLeft: '8px' }}>
                          {game.away_final_score}–{game.home_final_score}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--ink-4)' }}>
                      {formatDate(game.scheduled_at)}{game.location ? ` · ${game.location}` : ''}
                    </div>
                  </div>
                  <StatusChip status={game.status} />
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => openModal('edit_status', { game })}>✏️ Edit</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => openModal('stream_info', { game })}>📡 Camera</button>
                    {(game.sport === 'baseball' || game.sport === 'softball') && (
                      <button className="btn btn-sm" style={{ background: 'rgba(249,115,22,0.12)', color: '#F97316', border: '1px solid rgba(249,115,22,0.3)' }}
                        onClick={() => openModal('score_baseball', { game })}>Scoreboard</button>
                    )}
                    {game.sport === 'soccer' && (
                      <button className="btn btn-sm" style={{ background: 'rgba(249,115,22,0.12)', color: '#F97316', border: '1px solid rgba(249,115,22,0.3)' }}
                        onClick={() => openModal('score_soccer', { game })}>Scoreboard</button>
                    )}
                    {game.sport === 'basketball' && (
                      <button className="btn btn-sm" style={{ background: 'rgba(249,115,22,0.12)', color: '#F97316', border: '1px solid rgba(249,115,22,0.3)' }}
                        onClick={() => window.open(`/watch/${game.id}`, '_blank')}>🏀 Score</button>
                    )}
                    {game.status === 'completed' && (
                      <button
                        className="btn btn-sm"
                        style={{ background: 'rgba(34,197,94,0.12)', color: '#22C55E', border: '1px solid rgba(34,197,94,0.3)' }}
                        onClick={() => openModal('game_film', { game })}
                      >
                        🎬 View Game Film
                      </button>
                    )}
                    {game.status === 'completed' && (
                      <button
                        className="btn btn-sm"
                        style={{ background: 'rgba(139,92,246,0.12)', color: '#A78BFA', border: '1px solid rgba(139,92,246,0.3)' }}
                        disabled={summaryLoading === game.id}
                        onClick={() => generateSummary(game)}
                      >
                        {summaryLoading === game.id ? '⏳ Generating…' : game.ai_summary ? '✨ Re-summarize' : '✨ AI Summary'}
                      </button>
                    )}
                    <button className="btn btn-danger btn-sm" onClick={() => deleteGame(game)}>Delete</button>
                  </div>
                </div>
            );
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {upcoming.length > 0 && (
                  <>
                    <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '1.2px', color: 'var(--ink-4)', textTransform: 'uppercase', marginTop: '4px' }}>
                      Upcoming &amp; Live ({upcoming.length})
                    </div>
                    {upcoming.map(renderGame)}
                  </>
                )}
                {past.length > 0 && (
                  <>
                    <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '1.2px', color: 'var(--ink-4)', textTransform: 'uppercase', marginTop: upcoming.length > 0 ? '12px' : '4px' }}>
                      Past Games ({past.length})
                    </div>
                    {past.map(renderGame)}
                  </>
                )}
              </div>
            );
          })()}
        </>
      )}

      {activeTab === 'teams'    && <TeamsPanel key={refreshKey} games={games} onModalOpen={openModal} />}
      {activeTab === 'videos'   && <VideosPanel games={games} players={allPlayers} onModalOpen={openModal} />}
      {activeTab === 'sponsors' && <SponsorsPanel teams={teams} games={games} />}
      {activeTab === 'leagues'  && <LeaguesPanel allUsers={users} />}

      {/* ── Users Tab ── */}
      {activeTab === 'users' && (() => {
        const pendingUsers   = users.filter((u: any) => u.account_status === 'pending');
        const pendingManagers = users.filter((u: any) => u.team_manager_status === 'pending');
        const activeUsers    = users.filter((u: any) => u.account_status !== 'pending');
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ fontSize: '13px', color: 'var(--ink-3)' }}>{users.length} registered user{users.length !== 1 ? 's' : ''}</div>
              <button className="btn btn-ghost btn-sm" onClick={fetchUsers}>↻ Refresh</button>
            </div>

            {/* Pending approvals section */}
            {pendingUsers.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                  <div style={{ fontWeight: 800, fontSize: '15px', color: '#F97316' }}>⏳ Pending Approvals</div>
                  <span style={{
                    background: 'rgba(249,115,22,0.15)', color: '#F97316',
                    border: '1px solid rgba(249,115,22,0.3)',
                    padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 700,
                  }}>
                    {pendingUsers.length}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {pendingUsers.map((u: any) => (
                    <PendingUserCard
                      key={u.id}
                      user={u}
                      allPlayers={allPlayers}
                      onApprove={handleApprove}
                      onReject={handleReject}
                    />
                  ))}
                </div>
                <div style={{ borderTop: '1px solid var(--hairline)', margin: '20px 0' }} />
              </div>
            )}

            {/* Pending Team Manager approvals */}
            {pendingManagers.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                  <div style={{ fontWeight: 800, fontSize: '15px', color: '#22C55E' }}>🗂️ Pending Team Managers</div>
                  <span style={{
                    background: 'rgba(34,197,94,0.15)', color: '#22C55E',
                    border: '1px solid rgba(34,197,94,0.3)',
                    padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 700,
                  }}>
                    {pendingManagers.length}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {pendingManagers.map((u: any) => (
                    <PendingManagerCard
                      key={u.id}
                      user={u}
                      allTeams={teams}
                      onApprove={handleApproveManager}
                      onReject={handleRejectManager}
                      onCreateAndApprove={handleCreateAndApprove}
                    />
                  ))}
                </div>
                <div style={{ borderTop: '1px solid var(--hairline)', margin: '20px 0' }} />
              </div>
            )}

            {/* Active users list */}
            {activeUsers.length === 0 && pendingUsers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px', color: 'var(--ink-4)' }}>No users yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {activeUsers.map((u: any) => {
                  const joinedDate = u.created_at
                    ? new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : '—';
                  const lastLogin = u.last_login_at
                    ? new Date(u.last_login_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
                    : 'Never';

                  const roleChip = (() => {
                    if (u.account_status === 'rejected') return { bg: 'rgba(239,68,68,0.1)', color: '#F87171', border: '1px solid rgba(239,68,68,0.25)', label: 'Rejected' };
                    if (u.role === 'admin')         return { bg: 'rgba(249,115,22,0.15)', color: '#F97316', border: '1px solid rgba(249,115,22,0.3)', label: 'Admin' };
                    if (u.role === 'coach')         return { bg: 'rgba(6,182,212,0.12)',  color: '#22D3EE', border: '1px solid rgba(6,182,212,0.3)', label: 'Coach' };
                    if (u.role === 'player_parent') return { bg: 'rgba(139,92,246,0.12)', color: '#A78BFA', border: '1px solid rgba(139,92,246,0.3)', label: 'Player/Parent' };
                    return { bg: 'var(--surface-muted)', color: 'var(--ink-3)', border: '1px solid var(--hairline)', label: 'Fan' };
                  })();

                  return (
                    <div key={u.id} style={{
                      background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '12px',
                      padding: '16px 20px',
                    }}>
                      {/* Row 1: Avatar + Info */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        <div style={{
                          width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                          background: 'linear-gradient(135deg, #F97316, #EA580C)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '16px', fontWeight: 800, color: '#fff',
                        }}>
                          {u.username.charAt(0).toUpperCase()}
                        </div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 800, fontSize: '15px' }}>{u.username}</span>
                            <span style={{
                              fontSize: '10px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase',
                              padding: '2px 8px', borderRadius: '20px',
                              background: roleChip.bg, color: roleChip.color, border: roleChip.border,
                            }}>
                              {roleChip.label}
                            </span>
                          </div>
                          <div style={{ fontSize: '13px', color: 'var(--ink-3)', marginTop: '2px' }}>{u.email}</div>

                          {/* Coach team chips */}
                          {u.role === 'coach' && (
                            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginTop: '6px' }}>
                              {(u.coach_teams || []).length === 0 ? (
                                <span style={{ fontSize: '11px', color: 'var(--ink-4)', fontStyle: 'italic' }}>No teams assigned</span>
                              ) : (u.coach_teams || []).map((t: any) => (
                                <span key={t.id} style={{
                                  fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '12px',
                                  background: `${t.primary_color}18`, color: t.primary_color,
                                  border: `1px solid ${t.primary_color}40`,
                                }}>{t.name}</span>
                              ))}
                            </div>
                          )}

                          {/* Player/Parent linked player */}
                          {u.role === 'player_parent' && (
                            <div style={{ fontSize: '12px', color: '#A78BFA', marginTop: '4px' }}>
                              {u.linked_player_name ? `⚽ Linked to: ${u.linked_player_name}` : '⚠️ No player linked'}
                            </div>
                          )}

                          {/* Team Manager chips */}
                          {u.team_manager_status === 'active' && (
                            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginTop: '6px', alignItems: 'center' }}>
                              <span style={{ fontSize: '10px', color: '#22C55E', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>🗂️ Manages:</span>
                              {(u.managed_team_names || []).length === 0 ? (
                                <span style={{ fontSize: '10px', color: 'var(--ink-4)', fontStyle: 'italic' }}>No teams assigned</span>
                              ) : (u.managed_team_names || []).map((name: string, i: number) => (
                                <span key={i} style={{
                                  fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '12px',
                                  background: 'rgba(34,197,94,0.1)', color: '#22C55E',
                                  border: '1px solid rgba(34,197,94,0.3)',
                                }}>{name}</span>
                              ))}
                            </div>
                          )}
                          {u.team_manager_status === 'pending' && (
                            <div style={{ fontSize: '11px', color: '#22C55E', marginTop: '4px' }}>🗂️ Team Manager request pending</div>
                          )}
                          {u.team_manager_status === 'rejected' && (
                            <div style={{ fontSize: '11px', color: 'var(--ink-4)', marginTop: '4px' }}>🗂️ Team Manager request rejected</div>
                          )}

                          {/* Team access chips */}
                          {u.role !== 'admin' && (
                            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginTop: '6px', alignItems: 'center' }}>
                              <span style={{ fontSize: '10px', color: 'var(--ink-4)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Access:</span>
                              {(u.team_names || []).length === 0 ? (
                                <span style={{ fontSize: '10px', color: 'var(--ink-4)', padding: '2px 8px', borderRadius: '12px', background: 'var(--surface-sunken)', border: '1px solid var(--hairline)' }}>All teams</span>
                              ) : (u.team_names || []).map((name: string, i: number) => (
                                <span key={i} style={{
                                  fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '12px',
                                  background: 'rgba(249,115,22,0.1)', color: '#F97316',
                                  border: '1px solid rgba(249,115,22,0.3)',
                                }}>{name}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Row 2: Stats + Actions */}
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        flexWrap: 'wrap', gap: '10px',
                        marginTop: '12px', paddingTop: '12px',
                        borderTop: '1px solid var(--hairline)',
                      }}>
                        {/* Stats */}
                        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '18px', fontWeight: 900, color: 'var(--ink-1)', lineHeight: 1 }}>{u.games_created}</div>
                            <div style={{ fontSize: '10px', color: 'var(--ink-4)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Games</div>
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', color: 'var(--ink-4)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Joined</div>
                            <div style={{ fontSize: '13px', color: 'var(--ink-3)' }}>{joinedDate}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', color: 'var(--ink-4)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Last Login</div>
                            <div style={{ fontSize: '13px', color: lastLogin === 'Never' ? 'var(--ink-4)' : 'var(--ink-3)' }}>{lastLogin}</div>
                          </div>
                        </div>

                        {/* Actions */}
                        {u.id === currentUser.id ? (
                          <span style={{ fontSize: '11px', color: 'var(--ink-4)', fontWeight: 700 }}>YOU</span>
                        ) : (
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {u.role !== 'admin' && u.account_status !== 'rejected' && (
                              <button className="btn btn-secondary btn-sm" onClick={() => changeUserRole(u.id, 'admin')}>↑ Admin</button>
                            )}
                            {u.role !== 'coach' && u.account_status !== 'rejected' && (
                              <button className="btn btn-sm" style={{ background: 'rgba(6,182,212,0.1)', color: '#22D3EE', border: '1px solid rgba(6,182,212,0.25)' }}
                                onClick={() => changeUserRole(u.id, 'coach')}>
                                {u.role === 'admin' ? '↓ Coach' : '↑ Coach'}
                              </button>
                            )}
                            {u.role === 'coach' && (
                              <button
                                className="btn btn-sm"
                                style={{ background: 'rgba(34,197,94,0.1)', color: '#22C55E', border: '1px solid rgba(34,197,94,0.3)' }}
                                onClick={() => setAssignTeamsUser(u)}
                              >
                                🏆 Assign Teams
                              </button>
                            )}
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => setManageTeamsUser(u)}
                            >
                              🔑 Team Access
                            </button>
                            {u.role !== 'viewer' && u.account_status !== 'rejected' && (
                              <button className="btn btn-secondary btn-sm" onClick={() => changeUserRole(u.id, 'viewer')}>↓ Fan</button>
                            )}
                            <button className="btn btn-danger btn-sm" onClick={() => deleteUser(u)}>Delete</button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Modals ── */}
      {modal.type === 'create'           && <CreateGameModal teams={teams} onClose={closeModal} onCreated={handleCreated} />}
      {modal.type === 'edit_status'      && modal.game && <EditStatusModal game={modal.game} teams={teams} onClose={closeModal} onUpdated={handleUpdated} />}
      {modal.type === 'score_baseball'   && modal.game && <BaseballScoreModal game={modal.game} onClose={closeModal} />}
      {modal.type === 'score_soccer'     && modal.game && <SoccerScoreModal game={modal.game} onClose={closeModal} />}
      {modal.type === 'stream_info'      && modal.game && <StreamInfoModal game={modal.game} onClose={closeModal} />}
      {modal.type === 'game_film'        && modal.game && <GameFilmModal game={modal.game} onClose={closeModal} />}
      {modal.type === 'create_team'      && <TeamModal onClose={closeModal} onSaved={() => { fetchTeams(); showToast('Team created'); }} />}
      {modal.type === 'edit_team'        && modal.team && <TeamModal team={modal.team} onClose={closeModal} onSaved={() => { fetchTeams(); showToast('Team saved'); }} />}
      {modal.type === 'roster_scan'      && modal.team && <RosterScanModal teamId={modal.team.id} onClose={closeModal} onImported={() => { fetchPlayers(); showToast('Roster imported!'); }} />}
      {modal.type === 'create_player'    && modal.team && <PlayerModal teamId={modal.team.id} onClose={closeModal} onSaved={() => { fetchPlayers(); showToast('Player added'); closeModal(); }} />}
      {modal.type === 'edit_player'      && modal.player && modal.team && <PlayerModal player={modal.player} teamId={modal.team.id} onClose={closeModal} onSaved={() => { fetchPlayers(); showToast('Player saved'); closeModal(); }} />}
      {modal.type === 'player_stats'     && modal.player && <PlayerStatsModal player={modal.player} games={games} sport={modal.sport} onClose={closeModal} />}
      {modal.type === 'upload_recording' && <UploadVideoModal videoType="recording" games={games} players={allPlayers} teams={teams} onClose={closeModal} onUploaded={() => { showToast('Recording uploaded'); closeModal(); }} />}
      {modal.type === 'upload_highlight' && <UploadVideoModal videoType="highlight" games={games} players={allPlayers} teams={teams} onClose={closeModal} onUploaded={() => { showToast('Highlight uploaded'); closeModal(); }} />}
      {modal.type === 'import_ical'      && <ImportIcalModal teams={teams} onClose={closeModal} onImported={(n) => { fetchGames(); if (n > 0) showToast(`${n} game${n !== 1 ? 's' : ''} imported`); }} />}
      {assignTeamsUser && (
        <AssignTeamsModal
          user={assignTeamsUser}
          allTeams={teams}
          onClose={() => setAssignTeamsUser(null)}
          onChanged={() => { fetchUsers(); showToast('Team assignments saved'); setAssignTeamsUser(null); }}
        />
      )}

      {manageTeamsUser && (
        <ManageUserTeamsModal
          user={manageTeamsUser}
          allTeams={teams}
          onClose={() => setManageTeamsUser(null)}
          onChanged={() => { handleSaveUserTeams(); setManageTeamsUser(null); }}
        />
      )}

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '90px', left: '50%', transform: 'translateX(-50%)',
          background: '#22C55E', color: '#fff', padding: '10px 20px', borderRadius: '8px',
          fontWeight: 700, fontSize: '14px', zIndex: 9999,
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)', pointerEvents: 'none', whiteSpace: 'nowrap',
        }}>
          ✓ {toast}
        </div>
      )}
    </div>
  );
};

export default Admin;
